import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translation data
const translations = {
  en: {
    // Navigation
    overview: 'Overview',
    sites: 'Sites',
    compliance: 'Compliance',
    navGroupMain: 'Main',
    navGroupAnalytics: 'Analytics',
    navGroupSystem: 'System',
    
    // Dashboard
    totalUsage: 'Total Usage',
    alerts: 'Alerts',
    powerUsageTrend: 'Power Usage Trend',
    active: 'Active',
    
    // Common
    search: 'Search',
    backToSites: 'Back to Sites',
    expandAll: 'Expand All',
    collapseAll: 'Collapse All',
    online: 'Online',
    offline: 'Offline',
    refreshLiveData: 'Refresh Live Data',
    enterFullscreen: 'Enter Fullscreen',
    exitFullscreen: 'Exit Fullscreen',
    live: 'Live',
    graphic: 'Graphic',
    historic: 'Historic',
    fuseboardDevice: 'Fuseboard Device',
    generatingAiInsights: 'Generating AI Insights',
    analysingReadings: 'Analyzing latest readings from this device...',
    mouldRiskZoneMap: 'Mould Risk Zone Map',
    property: 'property',
    withMouldRisk: 'with mould risk',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    changeLanguage: 'Change Language',
    searchPlaceholder: 'Search…',
    // TODO: localise the strings below for ar/es/zh
    switchOrganisation: 'Switch organisation',
    logout: 'Logout',
    
    // Loading Messages
    loadingFuseboardHierarchy: 'Loading fuseboard hierarchy...',
    loadingEnvironmentalDashboard: 'Loading environmental dashboard...',
    loadingHistoricalData: 'Loading historical data...',
    loadingDashboardOverview: 'Loading dashboard overview...',
    loadingSites: 'Loading sites...',
    loadingReports: 'Loading reports...',
    loadingConfigurations: 'Loading configurations...',
    loadingOfflineDevices: 'Loading offline devices...',
    noOfflineDevices: 'All Clear',
    allDevicesReporting: 'All devices are reporting normally',

    // Buttons & Actions
    exportCsv: 'Export CSV',
    exportPdf: 'Export PDF',
    exportLeagueTable: 'Export League Table',
    switchTo2D: 'Switch to 2D View',
    switchTo3D: 'Switch to 3D View (Shows 3D Buildings)',
    changeMapStyle: 'Change Map Style',
    collapseAllTreeNodes: 'Collapse all tree nodes',
    expandAllTreeNodes: 'Expand all tree nodes',
    
    // Search Placeholders
    searchAlerts: 'Search alerts...',
    searchConfigurations: 'Search configurations...',
    searchByTime: 'Search by time (e.g., 14:30, 2024-01-15, Jan 15)...',
    
    // Export & Reports
    fuseboardChartsExport: 'Fuseboard Charts Export',
    exportedFromSmartInstallers: 'Exported from {companyName} Insights',
    generatedOn: 'Generated on:',
    exportLiveDataToCsv: 'Export Live Data to CSV',
    exportChartsToPdf: 'Export Charts to PDF',
    exportHistoricDataToCsv: 'Export Historic Data to CSV',
    
    // Form Labels
    to: 'to',
    from: 'from',
    
    // Time
    vsLastMonth: 'vs last month',
    vsLastWeek: 'vs last week',
    
    // Admin Page
    admin: 'Admin',
    help: 'Help',
    settings: 'Settings',
    addDevice: 'Add Device',
    editDevice: 'Edit Device',
    createDevice: 'Create Device',
    updateDevice: 'Update Device',
    deleteDevice: 'Delete Device',
    searchDevices: 'Search devices...',
    allDeviceTypes: 'All Device Types',
    noDevicesFound: 'No devices found',
    tryAdjustingSearch: 'Try adjusting your search or filters',
    identifier: 'Identifier',
    title: 'Title',
    actions: 'Actions',
    units: 'Units',
    unitsOptional: 'Units (Optional)',
    noUnitsAvailable: 'No units available for this site',
    unassignedDevices: 'Unassigned Devices',
    siteLevel: 'Site Level',
    notAssignedToUnit: 'Not assigned to any unit',
    unitsSelected: 'unit(s) selected',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    areYouSure: 'Are you sure?',
    cannotBeUndone: 'This action cannot be undone.',
    
    // Fuse Configuration
    fuseConfigurationRequired: 'Fuse Configuration (Required for FUSEBOARD_UDC)',
    configureAtLeast2Fuses: 'Configure at least 2 fuses with voltage, power factor, device name, and category. Fuses 3 and 4 are optional.',
    fuse: 'Fuse',
    deviceName: 'Device Name',
    category: 'Category',
    voltage: 'Voltage (V)',
    powerFactor: 'Power Factor',
    supply: 'Supply',
    circuit: 'Circuit',
    load: 'Load',
    other: 'Other',
    atLeast2FusesRequired: 'At least 2 fuses must be configured for FUSEBOARD_UDC devices',
    
    // Organization & Site Settings
    organizationKpiConfiguration: 'Organisation KPI configuration',
    siteKpiConfiguration: 'Site KPI configuration',
    unitRate: 'Unit Rate (£ per kWh)',
    intervalMinutes: 'Interval Minutes',
    carbonFactor: 'Carbon Factor (kg CO₂e per kWh)',
    howOftenReadingsArrive: 'How often device readings arrive (in minutes)',
    carbonEmissionFactor: 'Carbon emission factor for carbon savings calculation',
    appliesToAllSites: 'These settings apply to all sites unless overridden at the site level.',
    overrideOrganizationSettings: 'Override organization settings for a specific site. Leave empty to use organization defaults.',
    selectASite: '-- Select a site --',
    unitRateOverride: 'Unit Rate Override (£ per kWh)',
    intervalMinutesOverride: 'Interval Minutes Override',
    carbonFactorOverride: 'Carbon Factor Override (kg CO₂e per kWh)',
    leaveEmptyForDefault: 'Leave empty to use org default',
    current: 'Current',
    usingOrgDefault: 'Using org default',
    saveOrganizationSettings: 'Save Organization Settings',
    saveSiteSettings: 'Save Site Settings',
    
    // Alerts
    recentAlerts: 'Recent Alerts',
    recentAlertsHub: 'Recent Alerts Hub',
    noAlertsFound: 'No alerts found',
    activeAlerts: 'Alerts',
    configurations: 'Configurations',
    offlineMonitoring: 'Offline Monitoring',
    acknowledged: 'Acknowledged',
    resolved: 'Resolved',
    critical: 'Critical',
    warning: 'Warning',
    info: 'Info',
    all: 'All',
    severity: 'Severity',
    status: 'Status',
    acknowledge: 'Acknowledge',
    resolve: 'Resolve',
    view: 'View',
    createAlertRule: 'Create Alert Rule',
    newMonitor: 'New Monitor',
    
    // Common UI
    refresh: 'Refresh',
    close: 'Close',
    open: 'Open',
    yes: 'Yes',
    no: 'No',
    confirm: 'Confirm',
    required: 'Required',
    optional: 'Optional',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    failed: 'Failed',
    saved: 'Saved',
    updated: 'Updated',
    deleted: 'Deleted',
    created: 'Created',
    
    // Navigation
    aiHub: 'AI Hub',
    
    // Pagination
    itemsPerPage: 'Items per page',
    previous: 'Previous',
    next: 'Next',
    page: 'Page',
    showing: 'Showing',
    item: 'item',
    items: 'items',
    nextStep: 'Next Step',
    previousAlert: 'Previous alert',
    nextAlert: 'Next alert',
    
    // Map
    gridVsCircuitVoltage: 'Grid vs Circuit Voltage',
    
    // Reports
    reports: 'Reports',
    timeRange: 'Time Range',
    last24Hours: 'Last 24 Hours',
    last7Days: 'Last 7 Days',
    last30Days: 'Last 30 Days',
    last90Days: 'Last 90 Days',
    customRange: 'Custom Range',
    
    // Chat Assistant
    helpAssistant: 'Help Assistant',
    askMeAnything: 'Ask me anything about {companyName} Insights',
    askAQuestion: 'Ask a question...',
    send: 'Send',
    closeChat: 'Close chat',
    
    // Additional Alert & UI Strings
    viewAll: 'View All',
    viewAllAlerts: 'View All Alerts',
    refreshAlerts: 'Refresh alerts',
    alertOverview: 'Alert Overview',
    unread: 'Unread',
    allSeverity: 'All Severity',
    allStatus: 'All Status',
    markAsUnseen: 'Mark as Unseen',
    allClear: 'All Clear!',
    noAlertsMatchFilters: 'No alerts match your current filters',
    loadingAlerts: 'Loading alerts...',
    exportAlertsToCsv: 'Export Alerts to CSV',
    manage: 'Manage',
    paused: 'Paused',
    drafts: 'Drafts',
    editMonitor: 'Edit Monitor',
    deleteMonitor: 'Delete Monitor',
    noAlertsToExport: 'No alerts to export',
    total: 'Total',
    activeFuses: 'Active Fuses',
    overloadAlert: 'Overload alert',
    noAlerts: 'No alerts',
    smartInstallers: '{companyName}',
    edit: 'Edit',
    energySaved: 'Energy Saved',
    totalEnergy: 'Total Energy',
    totalEnergySaved: 'Total Energy Saved',
    voltageTrend: 'Voltage Trend',
    environmentalTrend: 'Environmental Trend',
    cumulativeTotal: 'Cumulative total',
    
    // Reports
    leagueTable: 'League Table',
    aiInsights: 'AI Insights',
    savingsCarbon: 'Savings & Carbon',
    customisedReporting: 'Customised Reporting',
    recommendedAction: 'Recommended Action',
    noInsightsAvailable: 'No Insights Available',
    noInsightsDescription: 'Select a device to view AI-generated insights',
    sensorHealth: 'Sensor Health',

    // Site Manager
    siteNotes: 'Site Map',
    dashboard: 'Dashboard',
    
    // Help Page - UI
    searchHelpTopics: 'Search help topics...',
    searchHelp: 'Search help...',
    tableOfContents: 'Table of Contents',
    noResultsFound: 'No results found',
    tryAdjustingSearchTerms: 'Try adjusting your search terms or browse the table of contents.',
    favouriteSites: 'Favourite Sites',
    notifications: 'Notifications',
    settingsPage: 'Settings Page',
    helpCenter: 'Help Center',
    helpHeroSubtitle: 'Everything you need to get the most out of {companyName} Monitoring Platform',

    // Quick Start Guide
    quickStartGuide: 'Quick Start Guide',
    quickStartSubtitle: 'New to {companyName} Monitoring Platform? Start here to get up and running quickly.',
    quickStartLogin: 'Logging In',
    quickStartLoginDesc: 'Access the platform with Auth0 authentication',
    quickStartDashboard: 'Dashboard Overview',
    quickStartDashboardDesc: 'Understand KPIs, alerts, and site monitoring',
    quickStartFeatures: 'Key Features',
    quickStartFeaturesDesc: 'Explore Sites, Alerts, and Reports',
    stillNeedHelp: 'Still need help?',
    stillNeedHelpDesc: 'Our team is here to assist you. Use the chat assistant for instant support.',
    openChatAssistant: 'Open Chat Assistant',

    // Help Page - Section Titles
    gettingStarted: 'Getting Started',
    overviewDashboard: 'Overview Dashboard',
    sitesManagement: 'Sites Management',
    alertsManagement: 'Alerts Management',
    reportsAnalytics: 'Reports & Analytics',
    adminSettings: 'Admin & Settings',
    chatAssistant: 'Chat Assistant',
    tipsTroubleshooting: 'Tips & Troubleshooting',
    
    // Help Page - Subsection Titles
    loggingIn: 'Logging In',
    navigationOverview: 'Navigation Overview',
    settingsPreferences: 'Settings & Preferences',
    understandingKpiCards: 'Understanding KPI Cards',
    recentAlertsSection: 'Recent Alerts Section',
    siteMap: 'Site Map',
    sitesPageOverview: 'Sites Page Overview',
    siteDashboards: 'Site Dashboards',
    siteNotesAnnotations: 'Site Manager & Annotations',
    alertsPageOverview: 'Alerts Page Overview',
    viewingFilteringAlerts: 'Viewing and Filtering Alerts',
    alertActions: 'Alert Actions',
    alertConfigurations: 'Alert Configurations',
    offlineDeviceMonitoring: 'Offline Device Monitoring',
    reportsPageOverview: 'Reports Page Overview',
    selectingTimeRanges: 'Selecting Time Ranges',
    understandingChartsGraphs: 'Understanding Charts and Graphs',
    exportingReports: 'Exporting Reports',
    customReportingServices: 'Custom Reporting Services',
    adminPageOverview: 'Admin Page Overview',
    deviceManagement: 'Device Management',
    fuseConfiguration: 'Fuse Configuration (FUSEBOARD_UDC)',
    organizationSettings: 'Organization Settings',
    siteSettings: 'Site Settings',
    usingChatAssistant: 'Using the Chat Assistant',
    bestPractices: 'Best Practices',
    commonIssuesSolutions: 'Common Issues & Solutions',
    keyboardShortcuts: 'Keyboard Shortcuts',
    gettingAdditionalHelp: 'Getting Additional Help',
    
    // AI Hub - Section Titles
    aiHubOverview: 'AI Hub Overview',
    aiInsightsWizard: 'AI Insights Wizard',
    aiAssistant: 'AI Assistant',
    smartRecommendations: 'Smart Recommendations',
    savedInsights: 'Saved Insights',
    insightHistory: 'Insight History',
    realTimeMonitoring: 'Real-time Monitoring',
    predictiveAnalytics: 'Predictive Analytics',
    dynamicTelemetry: 'Dynamic Telemetry',
    aiReports: 'AI Reports',
    siteComparison: 'Site Comparison',
    anomalyDetection: 'Anomaly Detection',
    carbonAnalytics: 'Carbon Analytics',
    predictiveMaintenance: 'Predictive Maintenance',
    energyOptimisation: 'Energy Optimisation',
    costAnalysis: 'Cost Analysis',
    forecasting: 'Forecasting',
    carbonImpact: 'Carbon Impact',
    
    // AI Hub Feature Descriptions (short)
    aiInsightsWizardDesc: 'Generate custom AI insights with step-by-step guidance',
    aiAssistantDesc: 'Chat with AI to get instant answers about your data',
    smartRecommendationsDesc: 'AI-powered suggestions to optimise your operations',
    savedInsightsDesc: 'Access your bookmarked and favourite insights',
    insightHistoryDesc: 'View past insights and analysis reports',
    realTimeMonitoringDesc: 'Live AI-powered monitoring and alerts',
    predictiveAnalyticsDesc: 'Forecast trends and predict future outcomes',
    dynamicTelemetryDesc: 'Dynamically controls how often sensors report based on real-time environmental conditions',
    aiReportsDesc: 'Generate comprehensive AI analysis reports',
    siteComparisonDesc: 'Compare performance across multiple sites with AI',
    anomalyDetectionDesc: 'Detect unusual patterns and potential issues',
    carbonAnalyticsDesc: 'Track and optimise your carbon footprint',
    backToHub: 'Back to Hub',
    startConversation: 'Start a conversation',
    askMeAnythingAbout: 'Ask me anything about your sites, units, devices, or insights into the environmental conditions',
    loadingReportData: 'Loading report data...',
    generatingComprehensiveReport: 'Generating comprehensive analysis report',
    comprehensiveAnalysisReport: 'Comprehensive Analysis Report',
    downloadReport: 'Download Report',
    analysisOfEnvironmentalConditions: 'Analysis of environmental conditions and mould growth risk across all monitored sites',
    siteCostSavings: 'Site Cost Savings',
    topSitesByCostSavings: 'Top {count} Sites by Cost Savings',
    costSavingsBySite: 'Cost Savings by Site',
    costSavingsBreakdown: 'Cost savings breakdown for this location',
    leadingLocationsContributing: 'Leading locations contributing to total savings',
    siteBySiteBreakdown: 'Site-by-Site Breakdown',
    rank: 'Rank',
    siteName: 'Site Name',
    costSavings: 'Cost Savings',
    carbonSaved: 'Carbon Saved',
    impactScore: 'Impact Score',
    selectSiteAndDevices: 'Select Site & Devices',
    chooseSiteAndSelectDevices: 'Choose a site and select the devices you want to compare',
    siteSelection: 'Site Selection',
    siteBySiteRiskAnalysis: 'Site-by-Site Risk Analysis',
    totalDevices: 'Total Devices',
    avgDaysToMould: 'Avg Days to Mould',
    avgTemp: 'Avg Temp',
    avgHumidity: 'Avg Humidity',
    riskPercent: 'Risk %',
    riskDistributionAcrossAllSites: 'Risk Distribution Across All Sites',
    riskComparisonBySite: 'Risk Comparison by Site',
    daysToMouldByDevice: 'Days to Mould by Device (Top 20)',
    atRisk: 'At Risk',
    temperature: 'Temperature',
    humidity: 'Humidity',
    riskLevel: 'Risk Level',
    lastReading: 'Last Reading',
    noReportsAvailable: 'No Reports Available',
    noReportsEnabled: 'You haven\'t enabled any reports yet. Go to Settings to enable the reports you want to see.',
    selectSitesToCompare: 'Select Sites to Compare',
    site: 'Site',
    labelFormatterSite: 'Site: {label}',
    savingsCarbonImpactReport: 'Savings & Carbon Impact Report',
    analysisOfCostSavings: 'Analysis of cost savings, energy efficiency, and environmental impact',
    totalCostSavings: 'Total Cost Savings',
    environmentalImpact: 'Environmental Impact',
    energyCarbonImpact: 'Energy & Carbon Impact',
    energyCarbonCorrelation: 'Energy & Carbon Correlation',
    energyCarbonData: 'Energy & Carbon Data',
    energySavedCarbonReductionLocation: 'Energy saved and carbon reduction for this location',
    relationshipEnergyCarbonReduction: 'Relationship between energy saved and carbon reduction across sites',
    carbonKg: 'Carbon (kg)',
    energyKwh: 'Energy (kWh)',
    yourEnergyOptimizationEfforts: 'Your energy optimisation efforts have saved',
    ofCO2: 'of CO₂',
    equivalentToPlanting: 'equivalent to planting',
    trees: 'trees',
    orRemoving: 'or removing',
    carsFromRoadForYear: 'cars from the road for a year',
    financialImpact: 'Financial Impact',
    totalCostSavingsOf: 'Total cost savings of',
    demonstrateSignificantFinancialBenefits: 'demonstrate the significant financial benefits of proactive energy management and optimisation',
    energyEfficiency: 'Energy Efficiency',
    bySaving: 'By saving',
    youveReducedEnergyConsumption: 'you\'ve reduced energy consumption equivalent to powering',
    averageHomesAnnually: 'average homes annually',
    bespokeReportingServices: 'Bespoke Reporting Services',
    weCraftCustomDashboards: 'We craft custom analytical dashboards tailored to your specific monitoring and reporting needs',
    customDashboards: 'Custom Dashboards',
    tailoredVisualizationInterfaces: 'Tailored visualization interfaces designed around your unique data requirements',
    advancedAnalytics: 'Advanced Analytics',
    deepInsightsPredictive: 'Deep insights with predictive modeling and trend analysis capabilities',
    realTimeInsights: 'Real-time Insights',
    liveDataProcessing: 'Live data processing with instant alerts and monitoring',
    systemIntegration: 'System Integration',
    seamlessConnection: 'Seamless connection with your existing infrastructure and tools',
    readyToTransformData: 'Ready to transform your data into actionable insights?',
    getStarted: 'Get Started',
    contactDevelopmentTeam: 'Contact our development team to discuss your requirements',
    device: 'Device',
    selectDeviceType: 'Select Device Type',
    chooseDeviceTypeToAnalyse: 'Choose the type of device you want to analyse',
    deviceType: 'Device Type',
    devicesSelected: 'Devices Selected',
    
    // Sites Page
    allTypes: 'All Types',
    temporaryPlantRoom: 'Temporary Plant Room',
    heatNetwork: 'Heat Network',
    fuseboardManagement: 'Fuseboard Management',
    environmentalMonitoring: 'Environmental Monitoring',
    mouldRisk: 'Mould Risk',
    mouldRiskScore: 'Mould Risk Score',
    mouldRiskCalculation: 'Mould Risk Calculation',
    mouldRiskActive: 'Mould Risk Active',
    mouldRiskAssessment: 'Mould Risk Assessment',
    environmentalSensor: 'Environmental Sensor',
    environmentalSensors: 'Environmental Sensors',
    averageDaysToMould: 'Average Days to Mould',
    averageDaysToCritical: 'Average Days to Critical',
    averageTemperature: 'Average Temperature',
    averageHumidity: 'Average Humidity',
    insufficientData: '—',
    devicesAtRisk: 'Devices at Risk',
    mouldRiskStatus: 'Mould Risk Status',
    propertiesAtRisk: 'Devices at Risk',
    siteAverageTrend: 'Site Average Trend',
    siteAverage: 'Site Average',
    risk: 'Risk',
    criticalRisk: 'Critical Risk',
    highRisk: 'High Risk',
    mediumRisk: 'Medium Risk',
    lowRisk: 'Low Risk',
    noRisk: 'No Risk',
    mouldRiskReport: 'Mould Risk Report',
    mouldRiskAssessmentReport: 'Mould Risk Assessment Report',
    recommendedActions: 'Recommended Actions',
    recommendedActionsReport: 'Recommended Actions Report',
    recommendedActionsDesc: 'Prioritised recommendations for property improvement, maintenance, compliance, and environmental actions based on real-time monitoring data',
    environmentalActions: 'Environmental Actions',
    maintenanceActions: 'Maintenance Actions',
    complianceActions: 'Compliance Actions',
    propertyImprovementActions: 'Property Improvement Actions',
    actionPriority: 'Priority',
    estimatedImpact: 'Estimated Impact',
    currentMetrics: 'Current Metrics',
    targetMetrics: 'Target Metrics',
    recommendedSteps: 'Recommended Steps',
    regulatoryReferences: 'Regulatory References',
    implementationDifficulty: 'Implementation Difficulty',
    timeToImplement: 'Time to Implement',
    markAsComplete: 'Mark as Complete',
    viewDetails: 'View Details',
    relatedActions: 'Related Actions',
    exportActionPlan: 'Export Action Plan',
    deviceMouldRiskAssessment: 'Device Mould Risk Assessment',
    days: 'days',
    daysToMould: 'Days to Mould',
    noEnvironmentalSensorsFound: 'No environmental sensors found',
    attentionRequired: 'Attention Required',
    allSystemsNormal: 'All Systems Normal',
    noSiteDataAvailable: 'No site data available',
    noSavingsDataAvailable: 'No savings data available',
    noComparisonDataAvailable: 'No comparison data available',
    noSiteSavingsDataAvailable: 'No site savings data available',
    systemStatus: 'System Status',
    errorLoadingInsights: 'Error Loading Insights',
    highPriority: 'High Priority',
    mediumPriority: 'Medium Priority',
    lowPriority: 'Low Priority',
    systemHealth: 'System Health',
    popular: 'Popular',
    new: 'New',
    beta: 'Early access',
    selectSite: 'Select Site',
    selectUnit: 'Select Unit',
    selectDevice: 'Select Device',
    aiCapabilities: 'AI Capabilities',
    generate: 'Generate',
    searchSitesByNameOrType: 'Search sites by name or type...',
    searchSites: 'Search sites…',
    clearFilters: 'Clear filters',
    addSite: 'Add Site',
    moreActions: 'More Actions',
    manageInAdmin: 'Manage in Admin',
    healthy: 'Healthy',
    withAlerts: 'With Alerts',
    type: 'Type',
    address: 'Address',
    activity: 'Activity',
    lastSeen: 'Last Seen',
    siteNameRequired: 'Site name is required',
    dashboardType: 'Dashboard Type',
    dashboardTypeRequired: 'Dashboard type is required',
    createSite: 'Create Site',
    saving: 'Saving…',
    propertyType: 'Property Type',
    properties: 'Properties',
    devices: 'Devices',
    enterDashboard: 'Enter Dashboard',
    of: 'of',
    matching: 'matching',
    noSitesFound: 'No Sites Found',
    noSitesMatchCriteria: 'No sites match your current search and filter criteria.',
    tryAdjustingSearchOrFilters: 'Try adjusting your search terms or filters.',
    noSitesAvailable: 'No Sites Available',
    noSitesConfigured: 'No sites have been configured for your organisation yet.',
    unknownDashboardType: 'Unknown Dashboard Type',
    dashboardTypeNotSupported: 'Dashboard type "{type}" is not supported.',
    unknown: 'Unknown',
    
    // Help Page Content
    'helpContent.login': `<p>To access {companyName} Insights, you'll need to log in with your credentials.</p>

            <h4>Steps to Log In:</h4>
            <ol>
              <li>Enter your username in the username field</li>
              <li>Enter your password in the password field</li>
              <li>Click the "Log In" button</li>
            </ol>
            
            <div class="help-tip">
              <strong>Tip:</strong> If you forget your password, contact your system administrator for assistance.
            </div>`,
    'helpContent.navigation': `<p>The main navigation is located in the left sidebar. You can access different sections of the application:</p>

            <h4>Main Sections:</h4>
            <ul>
              <li><strong>Overview</strong> - Dashboard with KPIs, alerts, and site map</li>
              <li><strong>Sites</strong> - View and manage all your sites</li>
              <li><strong>Alerts</strong> - Monitor and manage system alerts</li>
              <li><strong>Reports</strong> - Generate and view reports</li>
              <li><strong>AI Hub</strong> - AI-powered features including insights wizard, assistant, recommendations, and analytics</li>
              <li><strong>Admin</strong> - Device management and system settings (admin access required)</li>
              <li><strong>Help</strong> - Comprehensive help documentation and chat assistant</li>
            </ul>
            
            `,
    'helpContent.settings': `<p>You can customise your experience with various settings:</p>
            
            <h4>Theme Settings:</h4>
            <ul>
              <li>Toggle between light and dark mode using the theme switcher in the top right</li>
              <li>Your preference is saved automatically</li>
            </ul>
            
            <h4>Language Settings:</h4>
            <ul>
              <li>Change the interface language using the language selector</li>
              <li>Available languages are shown in the dropdown menu</li>
            </ul>
            
            `,
    'helpContent.kpiCards': `<p>The Overview dashboard displays key performance indicators (KPIs) that give you quick insights into your system's performance.</p>

            <h4>Available KPIs:</h4>
            <p>The home dashboard displays four KPI cards that can be customised by administrators. Available KPIs include:</p>
            
            <h5>Fuseboard Metrics:</h5>
            <ul>
              <li><strong>Carbon Saved</strong> - Total carbon emissions saved, with trend indicators</li>
              <li><strong>Cost Savings</strong> - Financial savings achieved, compared to previous periods</li>
              <li><strong>Total Usage</strong> - Overall energy consumption metrics</li>
              <li><strong>Grid vs Circuit Voltage</strong> - Voltage monitoring and status</li>
            </ul>
            
            <h5>Environmental Metrics:</h5>
            <ul>
              <li><strong>Devices at Risk</strong> - Number of devices with mould risk conditions</li>
              <li><strong>Average Days to Mould</strong> - Average time until mould growth conditions are met</li>
              <li><strong>Average Temperature</strong> - Mean temperature across all environmental sensors</li>
              <li><strong>Average Humidity</strong> - Mean humidity across all environmental sensors</li>
            </ul>
            
            <h4>Customizing Home Dashboard KPIs:</h4>
            <p>Administrators can customise which KPIs appear on the home dashboard through Settings. You can mix fuseboard metrics with environmental metrics to create a tailored dashboard view.</p>
            <ul>
              <li>Go to Settings → Organization Settings</li>
              <li>Scroll to "Home Dashboard KPI Configuration"</li>
              <li>Select exactly 4 KPIs from the available options (one for each slot)</li>
              <li>Save your configuration</li>
            </ul>

            <h4>Hover Effects:</h4>
            <p>Hover over any KPI card to see enhanced visual feedback. The cards will highlight with coloured borders and shadows.</p>

            <div class="help-tip">
              <strong>Tip:</strong> Click on alert-related KPIs to navigate directly to the Alerts page. Hover over cards to see enhanced visual feedback.
            </div>`,
    'helpContent.recentAlerts': `<p>The Recent Alerts section shows you the latest alerts from your system, helping you stay informed about any issues or important events.</p>

            <h4>Alert Information:</h4>
            <ul>
              <li><strong>Severity Levels:</strong>
                <ul>
                  <li><span class="badge-critical">CRITICAL</span> - Requires immediate attention</li>
                  <li><span class="badge-warning">WARNING</span> - Important but not urgent</li>
                  <li><span class="badge-info">INFO</span> - Informational messages</li>
                </ul>
              </li>
              <li><strong>Alert Message</strong> - Description of what triggered the alert</li>
              <li><strong>Timestamp</strong> - When the alert was generated</li>
            </ul>
            
            <h4>Understanding the Alert List:</h4>
            <p>Each alert displays with a coloured severity indicator on the left side, a severity badge, the alert message, and timestamp. The styling matches the Alerts page for consistency.</p>
            
            <div class="help-tip">
              <strong>Tip:</strong> Click on an alert in the Recent Alerts section to view more details or navigate to the full Alerts page.
            </div>`,
    'helpContent.siteMap': `<p>The Site Map provides a visual representation of all your sites, gateways, and configured buildings on an interactive 3D map.</p>

            <h4>Map Features:</h4>
            <ul>
              <li><strong>Gateway Markers</strong> - Green circular markers indicate online gateways, red markers indicate offline gateways</li>
              <li><strong>3D Buildings</strong> - Buildings are displayed in three dimensions with realistic height and detail</li>
              <li><strong>Building Configuration Icons</strong> - Coloured circular icons show configured buildings assigned to sites, units, or devices:
                <ul>
                  <li><strong>Building Icon</strong> (🏢) - Indicates a Site configuration</li>
                  <li><strong>Home Icon</strong> (🏠) - Indicates a Unit configuration</li>
                  <li><strong>Activity Icon</strong> (⚡) - Indicates a Device configuration</li>
                </ul>
              </li>
              <li><strong>Coloured Buildings</strong> - Buildings change colour based on alert status:
                <ul>
                  <li><strong>Red</strong> - Critical alerts present</li>
                  <li><strong>Orange</strong> - Warning alerts present</li>
                  <li><strong>Blue</strong> - Info alerts present</li>
                  <li><strong>Green</strong> - No alerts (healthy status)</li>
                </ul>
              </li>
            </ul>
            
            <h4>Interacting with Map Elements:</h4>
            <ul>
              <li><strong>Gateway Markers</strong> - Click on gateway markers to see detailed information about the gateway and its status</li>
              <li><strong>Building Configuration Icons</strong> - Hover over configuration icons to see a tooltip with:
                <ul>
                  <li>Configuration name</li>
                  <li>Asset type (Site, Unit, or Device) and name</li>
                  <li>Number of buildings in the configuration</li>
                  <li>Alert status and count</li>
                  <li>"Enter Dashboard" button to navigate to the asset dashboard</li>
                </ul>
              </li>
              <li><strong>Icon Click</strong> - Click on a configuration icon to zoom the map to show all buildings in that configuration</li>
              <li><strong>Building Colours</strong> - Building colours update automatically based on the highest priority alert for the assigned asset</li>
            </ul>

            <h4>Map Navigation:</h4>
            <ul>
              <li><strong>Zoom</strong> - Use mouse wheel or pinch gestures to zoom in and out</li>
              <li><strong>Pan</strong> - Click and drag to move around the map</li>
              <li><strong>Rotate</strong> - Hold right-click (or Ctrl+click) and drag to rotate the 3D view</li>
              <li><strong>Tilt</strong> - Hold right-click (or Ctrl+click) and drag vertically to adjust the viewing angle</li>
            </ul>
            
            <h4>Configuring Buildings:</h4>
            <p>To assign buildings to sites, units, or devices, go to Settings → Map Buildings. See the Map Building Configuration section for detailed instructions.</p>
            
            <div class="help-tip">
              <strong>Tip:</strong> The map automatically fits to show all gateways and configured buildings when it loads. Building colours update in real-time as alerts change.
            </div>`,
    'helpContent.sitesOverview': `<p>The Sites page allows you to view and manage all your sites. You can search, filter, and access detailed dashboards for each site.</p>

            <h4>Viewing Sites:</h4>
            <ul>
              <li><strong>List View</strong> - See sites in a list format with key information</li>
              <li><strong>Grid View</strong> - View sites as cards in a grid layout</li>
              <li><strong>Search</strong> - Use the search bar to find specific sites by name</li>
              <li><strong>Filter</strong> - Filter sites by dashboard type (Temp Plant, Heat Network, Fuseboard, Environmental Monitoring)</li>
              <li><strong>Favourite Sites</strong> - Click the star icon on any site card to mark it as a favourite. Favourite sites appear at the top of the list and are sorted first</li>
            </ul>

            <div class="help-tip">
              <strong>Tip:</strong> Use favourites to quickly access your most important sites. Favourite sites are automatically sorted to the top of the list.
            </div>`,
    'helpContent.siteDashboards': `<p>Each site has a dedicated dashboard that provides detailed information and monitoring capabilities. The dashboard type depends on the site configuration.</p>
            
            <h4>Dashboard Types:</h4>
            
            <h5>1. Fuseboard Dashboard</h5>
            <p>The Fuseboard dashboard provides a comprehensive view of fuseboard management with a hierarchical tree structure.</p>

            <h6>Left Panel - Hierarchy Tree:</h6>
            <ul>
              <li>Shows the site structure with units, properties, and fuseboards</li>
              <li>Search functionality to quickly find specific fuseboards or units</li>
              <li>Expand/collapse nodes to navigate the hierarchy</li>
              <li>Status indicators show online/offline status for each device</li>
              <li>Alert indicators show if there are any active alerts</li>
            </ul>

            <h6>Right Panel - KPIs and Charts:</h6>
            <ul>
              <li><strong>KPI Cards</strong> - Carbon saved, cost savings, total usage, and alerts</li>
              <li><strong>Power Usage Trend Chart</strong> - Interactive chart showing power consumption over the last 24 hours</li>
              <li>Hover over KPI cards to see enhanced visual feedback</li>
              <li>Click on alert KPI to view detailed alerts for the site</li>
            </ul>

            <h6>Interacting with Fuseboards:</h6>
            <ul>
              <li>Click on any fuseboard in the tree to open a detailed modal</li>
              <li>The modal shows comprehensive device information, readings, and historical data</li>
              <li>View voltage, current, and power readings with charts</li>
              <li>Export data or view in fullscreen mode</li>
            </ul>
            
            <h6>Device Detail Modals:</h6>
            <p>When you click on energy meter devices in the hierarchy tree, detailed modals open showing device-specific information. The exact content depends on the device type, but typically includes:</p>
            <ul>
              <li><strong>Live View</strong> - Current readings and measurements displayed in tables or charts</li>
              <li><strong>Historical View</strong> - Past data with date range selection to view trends over time</li>
              <li><strong>Charts and Visualisations</strong> - Graphical representation of device readings</li>
              <li><strong>Export Functionality</strong> - Export live data as CSV or historical charts as PDF</li>
              <li><strong>Fullscreen Mode</strong> - Expand the modal for detailed analysis</li>
            </ul>
            
            <h6>Using Device Detail Modals:</h6>
            <ul>
              <li><strong>View Modes</strong> - Switch between "Live" and "Historic" views using the tabs</li>
              <li><strong>Refresh Data</strong> - Click the refresh button to update live readings</li>
              <li><strong>Date Range</strong> - In historic view, select start and end dates to view historical data</li>
              <li><strong>Show/Hide Parameters</strong> - Toggle visibility of specific parameters in charts (if available)</li>
              <li><strong>Export</strong> - Export live data as CSV or historical charts as PDF</li>
              <li><strong>Fullscreen</strong> - Click the fullscreen button to expand the modal for detailed analysis</li>
            </ul>

            <h5>2. Temporary Plant Dashboard</h5>
            <p>The Temporary Plant dashboard is designed for monitoring temporary plant room installations.</p>

            <h5>3. Heat Network Dashboard</h5>
            <p>The Heat Network dashboard provides monitoring for heat network systems.</p>

            <h5>4. Environmental Monitoring Dashboard</h5>
            <p>The Environmental Monitoring dashboard provides comprehensive environmental monitoring with mould risk assessment capabilities.</p>

            <h6>Left Panel - Hierarchy Tree:</h6>
            <ul>
              <li>Shows the site structure with units, properties, and environmental sensors</li>
              <li>Search functionality to quickly find specific sensors or units</li>
              <li>Expand/collapse nodes to navigate the hierarchy</li>
              <li>Status indicators show online/offline status for each device</li>
              <li>Mould risk indicators show risk levels (High, Medium, Low, No Risk)</li>
            </ul>
            
            <h6>Right Panel - Environmental KPIs:</h6>
            <ul>
              <li><strong>Devices at Risk</strong> - Number of devices with mould risk conditions</li>
              <li><strong>Average Days to Mould</strong> - Average time until mould growth conditions are met</li>
              <li><strong>Average Temperature</strong> - Mean temperature across all sensors</li>
              <li><strong>Average Humidity</strong> - Mean humidity across all sensors</li>
              <li><strong>Last Reading</strong> - Timestamp of the most recent sensor reading</li>
            </ul>
            
            <h6>Interacting with Environmental Devices:</h6>
            <ul>
              <li>Click on any environmental sensor in the tree to open a detailed modal</li>
              <li>The modal shows temperature, humidity, dew point, and mould risk metrics</li>
              <li>View historical trends and risk assessments</li>
              <li>Access AI-powered environmental insights</li>
              <li>View compliance information (Awaab's Law, HHSRS, ISO 16000)</li>
            </ul>

            <div class="help-tip">
              <strong>Tip:</strong> The layout is fixed with equal-sized panels, so both the hierarchy tree and KPIs/charts are always visible without scrolling.
            </div>`,
    'helpContent.siteNotes': `<p>Site Manager allows you to add annotations, markers, and notes to site images for documentation and collaboration.</p>

            <h4>Features:</h4>
            <ul>
              <li><strong>Image Viewing</strong> - View site images in high resolution</li>
              <li><strong>Markers</strong> - Add markers to specific locations on the image</li>
              <li><strong>Rich Text Editor</strong> - Add formatted notes and documentation</li>
              <li><strong>Notepad</strong> - Quick notes section for site-specific information</li>
              <li><strong>Device Information</strong> - View device details by clicking markers</li>
            </ul>
            
            <h4>Adding Markers:</h4>
            <ol>
              <li>Click the "Add Marker" button or click directly on the image</li>
              <li>Position the marker where needed</li>
              <li>Add a description or link to a device</li>
              <li>Save the marker</li>
            </ol>

            <h4>Using the Notepad:</h4>
            <ul>
              <li>Click the notepad icon to open the notes editor</li>
              <li>Add quick notes, reminders, or site-specific information</li>
              <li>Notes are automatically saved</li>
              <li>Access notes anytime from the site notes page</li>
            </ul>
            
            <div class="help-tip">
              <strong>Tip:</strong> Markers can be linked to devices to quickly access device information and readings.
            </div>`,
    'helpContent.alertsOverview': `<p>The Alerts page provides comprehensive alert management with three main tabs: Active Alerts, Configurations, and Offline Monitoring.</p>

            <h4>Alert Statistics:</h4>
            <p>At the top of the Active Alerts tab, you'll see a summary showing:</p>
            <ul>
              <li>Number of Critical alerts</li>
              <li>Number of Warning alerts</li>
              <li>Number of Info alerts</li>
              <li>Total alert count</li>
            </ul>`,
    'helpContent.viewingAlerts': `<p>The alerts list shows all alerts with detailed information and action options.</p>

            <h4>Alert Information Displayed:</h4>
            <ul>
              <li><strong>Severity Indicator</strong> - Coloured bar on the left (red for critical, yellow for warning, blue for info)</li>
              <li><strong>Severity Badge</strong> - Visual badge showing alert level</li>
              <li><strong>Alert Message</strong> - Description of what triggered the alert</li>
              <li><strong>Device Identifier</strong> - Which device generated the alert</li>
              <li><strong>Timestamp</strong> - When the alert was raised</li>
              <li><strong>Status</strong> - Current status (Active, Acknowledged, Resolved)</li>
            </ul>
            
            <h4>Search and Filter:</h4>
            <ul>
              <li><strong>Search Bar</strong> - Search alerts by message or device identifier</li>
              <li><strong>Severity Filter</strong> - Filter by severity level (All, Critical, Warning, Info)</li>
              <li><strong>Status Filter</strong> - Filter by status (All, Active, Acknowledged, Resolved)</li>
            </ul>
            
            `,
    'helpContent.alertActions': `<p>You can perform various actions on alerts depending on their status.</p>

            <h4>Available Actions:</h4>
            
            <h5>Acknowledge Alert:</h5>
            <ul>
              <li>Available for Active alerts</li>
              <li>Marks the alert as acknowledged</li>
              <li>Shows that you're aware of the issue</li>
              <li>Does not resolve the alert</li>
            </ul>
            
            <h5>Resolve Alert:</h5>
            <ul>
              <li>Available for Active or Acknowledged alerts</li>
              <li>Marks the alert as resolved</li>
              <li>Indicates the issue has been addressed</li>
              <li>Resolved alerts can still be viewed but are filtered out by default</li>
            </ul>
            
            <h5>View Details:</h5>
            <ul>
              <li>Available for Resolved alerts</li>
              <li>View detailed information about the alert</li>
              <li>Access historical data related to the alert</li>
            </ul>

            <div class="help-tip">
              <strong>Tip:</strong> Use the acknowledge action to track which alerts you've reviewed, even if you haven't resolved them yet.
            </div>`,
    'helpContent.alertConfigurations': `<p>Alert Configurations allow you to create and manage custom alert rules that monitor your system for specific conditions.</p>

            <h4>Configuration Management:</h4>
            <p>Configurations are organised in a Kanban board with three columns:</p>
            <ul>
              <li><strong>Active</strong> - Configurations that are currently monitoring</li>
              <li><strong>Paused</strong> - Configurations that are temporarily disabled</li>
              <li><strong>Drafts</strong> - Configuration templates (future feature)</li>
            </ul>
            
            <h4>Creating a New Configuration:</h4>
            <ol>
              <li>Click the "Create Alert Rule" button</li>
              <li>Select a template from the dropdown</li>
              <li>Choose the scope (Site, Unit, or Device level)</li>
              <li>Select the specific site/unit/device if applicable</li>
              <li>Set threshold values (minimum and maximum)</li>
              <li>Choose the alert level (Info, Warning, or Critical)</li>
              <li>Enable or disable the configuration</li>
              <li>Click "Create" to save</li>
            </ol>

            <h4>Managing Configurations:</h4>
            <ul>
              <li><strong>Drag and Drop</strong> - Move configurations between Active and Paused columns</li>
              <li><strong>Edit</strong> - Click the edit button to modify an existing configuration</li>
              <li><strong>Delete</strong> - Remove a configuration permanently</li>
            </ul>

            <div class="help-tip">
              <strong>Tip:</strong> Use paused configurations to temporarily disable monitoring without deleting the rule entirely.
            </div>

            <div class="help-placeholder">
              <p><strong>Screenshot: Alert Configurations Kanban Board</strong></p>
              <p>Image showing the Kanban board with Active, Paused, and Drafts columns</p>
            </div>`,
    'helpContent.offlineMonitoring': `<p>The Offline Monitoring tab allows you to set up alerts for when devices go offline.</p>

            <h4>Creating an Offline Monitor:</h4>
            <ol>
              <li>Click the "New Monitor" button</li>
              <li>Select the device to monitor</li>
              <li>Set the offline threshold (in minutes) - how long a device must be offline before triggering an alert</li>
              <li>Choose the alert level (Warning or Critical)</li>
              <li>Add an optional description</li>
              <li>Enable or disable the monitor</li>
              <li>Click "Create" to save</li>
            </ol>

            <h4>Monitor Information:</h4>
            <p>Each monitor shows:</p>
            <ul>
              <li>Device name</li>
              <li>Description (if provided)</li>
              <li>Last seen timestamp</li>
              <li>Offline threshold</li>
              <li>Alert level</li>
              <li>Enabled/Disabled status</li>
            </ul>`,
    'helpContent.reportsOverview': `<p>The Reports page provides comprehensive analytics and reporting capabilities for your system data.</p>

            <h4>Available Reports:</h4>
            <ul>
              <li><strong>Savings & Carbon Report</strong> - Analysis of cost savings, energy efficiency, and carbon impact. Shows total savings, carbon reduction, and equivalent metrics (trees, cars, homes)</li>
              <li><strong>Mould Risk Report</strong> - Comprehensive environmental monitoring and mould risk assessment. Includes risk trends, property-level analysis, and compliance information (Awaab's Law, HHSRS, ISO 16000)</li>
              <li><strong>League Table</strong> - Device performance comparison across sites with rankings and metrics</li>
              <li><strong>Customised Reporting</strong> - Custom reports and analytics with device selection, parameter filtering, and time range selection</li>
              <li><strong>Recommended Actions Report</strong> - Prioritised recommendations for property improvements, maintenance, compliance, and environmental actions</li>
            </ul>
            
            <h4>Report Visibility:</h4>
            <p>Report visibility can be controlled through Settings. Administrators can enable or disable specific reports for users. If a report is not visible, contact your administrator to enable it.</p>
            
            <div class="help-tip">
              <strong>Tip:</strong> Use the time range selector at the top of each report to analyze different periods. Most reports support custom date ranges for detailed analysis.
            </div>`,
    'helpContent.timeRanges': `<p>Most reports allow you to filter data by time range to analyse specific periods.</p>

            <h4>Available Time Ranges:</h4>
            <ul>
              <li><strong>Last 24 Hours</strong> - Data from the past day</li>
              <li><strong>Last 7 Days</strong> - Weekly view</li>
              <li><strong>Last 30 Days</strong> - Monthly view</li>
              <li><strong>Last 90 Days</strong> - Quarterly view</li>
              <li><strong>Custom Range</strong> - Select specific start and end dates</li>
            </ul>

            <div class="help-tip">
              <strong>Tip:</strong> Use custom date ranges to analyse specific events or periods of interest.
            </div>

            <div class="help-placeholder">
              <p><strong>Screenshot: Time Range Selector</strong></p>
              <p>Image showing time range dropdown with preset options and custom date picker</p>
            </div>`,
    'helpContent.chartsGraphs': `<p>Reports include various types of charts and graphs to visualise your data effectively.</p>

            <h4>Chart Types:</h4>
            <ul>
              <li><strong>Line Charts</strong> - Show trends over time</li>
              <li><strong>Bar Charts</strong> - Compare values across categories</li>
              <li><strong>Area Charts</strong> - Show cumulative values with gradients</li>
              <li><strong>Pie Charts</strong> - Display proportions and percentages</li>
            </ul>
            
            <h4>Interacting with Charts:</h4>
            <ul>
              <li>Hover over data points to see exact values</li>
              <li>Click on legend items to show/hide data series</li>
              <li>Use the zoom controls if available</li>
              <li>Export charts as images or PDF</li>
            </ul>

            <div class="help-placeholder">
              <p><strong>Screenshot: Charts and Graphs</strong></p>
              <p>Image showing various chart types with data visualization</p>
            </div>`,
    'helpContent.exportingReports': `<p>You can export reports in various formats for sharing, analysis, or documentation purposes.</p>

            <h4>Export Formats:</h4>
            <ul>
              <li><strong>CSV</strong> - Export data as comma-separated values for spreadsheet analysis</li>
              <li><strong>PDF</strong> - Export charts and reports as PDF documents</li>
              <li><strong>League Table</strong> - Export comparison tables</li>
            </ul>
            
            <h4>What Gets Exported:</h4>
            <ul>
              <li>Current data based on selected filters</li>
              <li>Charts and visualisations</li>
              <li>Summary statistics</li>
              <li>Timestamps and metadata</li>
            </ul>

            <div class="help-tip">
              <strong>Tip:</strong> Export reports regularly for record-keeping and compliance purposes.
            </div>

            <div class="help-placeholder">
              <p><strong>Screenshot: Export Options</strong></p>
              <p>Image showing export buttons for CSV, PDF, and League Table formats</p>
            </div>`,
    'helpContent.scheduledReports': `<p>Scheduled Reports automate report generation and delivery via email. Reports can be sent daily, weekly, or monthly to keep stakeholders informed without manual effort.</p>

            <h4>Accessing Scheduled Reports:</h4>
            <ul>
              <li>Navigate to the Reports page</li>
              <li>Click the "Automation" tab at the top</li>
              <li>View all scheduled reports in the list</li>
            </ul>

            <h4>Available Report Types for Scheduling:</h4>
            <ul>
              <li><strong>Savings & Carbon</strong> - Cost savings, energy efficiency, and carbon impact analysis</li>
              <li><strong>Mould Risk</strong> - Environmental monitoring and mould risk assessment</li>
              <li><strong>Recommended Actions</strong> - Prioritised recommendations for improvements</li>
              <li><strong>Sensor Health</strong> - Device health, battery levels, and uptime statistics</li>
              <li><strong>Customised Reporting</strong> - Custom reports with configurable parameters and devices</li>
            </ul>

            <h4>Creating a Scheduled Report:</h4>
            <ol>
              <li>Click "New Scheduled Report" button</li>
              <li>Enter a descriptive name for the report</li>
              <li>Select the report type from the dropdown</li>
              <li>Choose the time range (7 days, 30 days, 90 days, etc.)</li>
              <li>For Customised Reporting, configure report parameters (devices, sites, parameters)</li>
              <li>Select the schedule type:
                <ul>
                  <li><strong>Daily</strong> - Report sent every day at a specified time</li>
                  <li><strong>Weekly</strong> - Report sent on a specific day of the week at a specified time</li>
                  <li><strong>Monthly</strong> - Report sent on a specific day of the month at a specified time</li>
                </ul>
              </li>
              <li>Set the time (HH:MM format, e.g., 08:00)</li>
              <li>For weekly schedules, select the day of the week</li>
              <li>For monthly schedules, select the day of the month (1-31)</li>
              <li>Select the timezone (e.g., Europe/London, America/New_York)</li>
              <li>Choose the output format (PDF, Excel, or CSV)</li>
              <li>Add recipient email addresses (one per line or comma-separated)</li>
              <li>Enable or disable the schedule</li>
              <li>Click "Create" to save</li>
            </ol>

            <h4>Managing Scheduled Reports:</h4>
            <ul>
              <li><strong>Edit</strong> - Click the edit icon to modify an existing scheduled report</li>
              <li><strong>Test</strong> - Click the test button to immediately generate and send a test report</li>
              <li><strong>Enable/Disable</strong> - Toggle the schedule on or off without deleting it</li>
              <li><strong>Delete</strong> - Remove a scheduled report permanently</li>
              <li><strong>View Status</strong> - See when the report last ran and its status (success or failed)</li>
            </ul>
            
            <h4>Scheduled Report Information:</h4>
            <p>Each scheduled report shows:</p>
            <ul>
              <li>Report name and type</li>
              <li>Schedule description (e.g., "Daily at 08:00")</li>
              <li>Time range</li>
              <li>Output format</li>
              <li>Number of recipients</li>
              <li>Enabled/Disabled status</li>
              <li>Last run timestamp and status</li>
              <li>Last error message (if the last run failed)</li>
            </ul>
            
            <div class="help-tip">
              <strong>Tip:</strong> Use scheduled reports to automatically deliver regular updates to stakeholders. Test your scheduled report before enabling it to ensure it generates correctly and recipients receive it properly.
            </div>`,
    'helpContent.customReports': `<p>For advanced reporting needs, custom analytical dashboards can be created tailored to your specific requirements.</p>

            <h4>Custom Dashboard Features:</h4>
            <ul>
              <li>Tailored visualisation interfaces</li>
              <li>Advanced analytics with predictive modelling</li>
              <li>Real-time insights with instant alerts</li>
              <li>Custom metrics and KPIs</li>
            </ul>
            
            <p>Contact your system administrator or support team to discuss custom reporting requirements.</p>

            <div class="help-placeholder">
              <p><strong>Screenshot: Custom Reporting Services</strong></p>
              <p>Image showing the custom reporting services showcase</p>
            </div>`,
    'helpContent.mouldRiskReport': `<p>The Mould Risk Report provides comprehensive environmental monitoring and mould risk assessment across all your sites.</p>

            <h4>Report Features:</h4>
            <ul>
              <li><strong>Risk Trends</strong> - Historical trends showing temperature, humidity, and days to mould over time</li>
              <li><strong>Property-Level Analysis</strong> - Detailed risk assessment for each property with specific metrics</li>
              <li><strong>Risk Levels</strong> - Visual indicators for High, Medium, Low, and No Risk conditions</li>
              <li><strong>Compliance Information</strong> - References to Awaab's Law, HHSRS, ISO 16000, and other UK building regulations</li>
              <li><strong>Time Range Selection</strong> - Filter data by time period (7 days, 30 days, 90 days, or custom range)</li>
            </ul>
            
            <h4>Understanding Risk Metrics:</h4>
            <ul>
              <li><strong>Risk Score (0-100)</strong> - Composite assessment combining humidity, dew point, temperature, and days to mould</li>
              <li><strong>Days to Mould</strong> - Estimated time until mould growth conditions are met based on current environmental conditions</li>
              <li><strong>Risk Level</strong> - HIGH (70-100), MEDIUM (40-69), LOW (10-39), or NONE (0-9)</li>
              <li><strong>Mould Risk Active</strong> - Boolean indicating if conditions are suitable for mould growth (humidity ≥ 65% and temperature 5-45°C)</li>
            </ul>
            
            <h4>Compliance Standards:</h4>
            <ul>
              <li><strong>Awaab's Law</strong> - Emergency hazards (HIGH risk) must be addressed within 24 hours</li>
              <li><strong>HHSRS</strong> - Housing Health and Safety Rating System for hazard assessment</li>
              <li><strong>ISO 16000</strong> - Air quality standards and mould assessment methodologies</li>
              <li><strong>Building Regulations Part F</strong> - Ventilation requirements</li>
              <li><strong>WHO Guidelines</strong> - Health impact assessments for dampness and mould</li>
            </ul>

            <div class="help-tip">
              <strong>Tip:</strong> High-risk conditions require immediate action per Awaab's Law. Use the time range selector to identify trends and patterns in environmental conditions.
            </div>`,
    'helpContent.recommendedActionsReport': `<p>The Recommended Actions Report provides prioritised recommendations for property improvements, maintenance, compliance, and environmental actions.</p>

            <h4>Report Features:</h4>
            <ul>
              <li><strong>Action Summary</strong> - Overview of total actions by category and priority</li>
              <li><strong>Prioritised Actions</strong> - Actions sorted by priority (Critical, High, Medium, Low)</li>
              <li><strong>Category Filtering</strong> - Filter by Environmental, Maintenance, Compliance, or Property categories</li>
              <li><strong>Priority Filtering</strong> - Filter by Critical, High, Medium, or Low priority</li>
              <li><strong>Time Range Selection</strong> - Select time period (30 days, 90 days, or custom range)</li>
              <li><strong>Action Details</strong> - Click on any action to view detailed information, estimated impact, and implementation guidance</li>
            </ul>
            
            <h4>Action Categories:</h4>
            <ul>
              <li><strong>Environmental</strong> - Actions related to mould risk, humidity control, ventilation, and environmental optimisation</li>
              <li><strong>Maintenance</strong> - Equipment maintenance, system checks, and preventive maintenance recommendations</li>
              <li><strong>Compliance</strong> - Actions to ensure compliance with Awaab's Law, HHSRS, Building Regulations, and other standards</li>
              <li><strong>Property</strong> - Property-wide improvements, structural recommendations, and holistic property management actions</li>
            </ul>
            
            <h4>Understanding Action Impact:</h4>
            <ul>
              <li><strong>Cost Savings (Annual)</strong> - Estimated annual cost savings from implementing the action</li>
              <li><strong>Risk Reduction</strong> - Percentage reduction in mould risk or other hazards</li>
              <li><strong>Compliance Score Improvement</strong> - Improvement in compliance score from implementing the action</li>
            </ul>

            <div class="help-tip">
              <strong>Tip:</strong> Start with Critical and High priority actions for maximum impact. Many actions can be implemented quickly with significant results.
            </div>`,
    'helpContent.adminOverview': `<p>The Admin page provides administrative tools for managing devices and configuring system settings. This page requires admin access.</p>

            <h4>Admin Features:</h4>
            <ul>
              <li><strong>Device Management</strong> - Create, edit, and delete devices</li>
              <li><strong>Device Search & Filtering</strong> - Find devices by identifier, title, or device type</li>
              <li><strong>Fuse Configuration</strong> - Configure fuses for FUSEBOARD_UDC devices</li>
              <li><strong>Organization Settings</strong> - Configure KPI calculation parameters for the entire organization</li>
              <li><strong>Site Settings</strong> - Override organization settings for specific sites</li>
            </ul>
            
            <div class="help-tip">
              <strong>Note:</strong> Admin access is required to view and use this page. Contact your system administrator if you need admin privileges.
            </div>`,
    'helpContent.deviceManagement': `<p>Manage all devices in your system from the Admin page.</p>

            <h4>Viewing Devices:</h4>
            <ul>
              <li><strong>Device Table</strong> - See all devices with identifier, title, device type, and site</li>
              <li><strong>Search</strong> - Use the search bar to find devices by identifier or title</li>
              <li><strong>Filter by Type</strong> - Filter devices by device type using the dropdown</li>
            </ul>
            
            <h4>Creating a Device:</h4>
            <ol>
              <li>Click the "Add Device" button</li>
              <li>Enter the device identifier (required, cannot be changed after creation)</li>
              <li>Enter an optional title</li>
              <li>Select the device type from the dropdown</li>
              <li>Select the site where the device is located</li>
              <li>Optionally select units associated with the device</li>
              <li>For FUSEBOARD_UDC and CS01_NB devices, configure at least 2 fuses (see Fuse Configuration)</li>
              <li>For EWATTCH_SQUID devices, configure clamp assignments (see Squid Clamp Configuration)</li>
              <li>Click "Create Device" to save</li>
            </ol>

            <h4>Editing a Device:</h4>
            <ul>
              <li>Click the edit icon next to any device in the table</li>
              <li>Modify the title, device type, site, or units</li>
              <li>Note: The identifier cannot be changed after creation</li>
              <li>For FUSEBOARD_UDC and CS01_NB devices, update fuse configurations as needed</li>
              <li>For EWATTCH_SQUID devices, update clamp assignments as needed</li>
              <li>Click "Update Device" to save changes</li>
            </ul>
            
            <h4>Deleting a Device:</h4>
            <ul>
              <li>Click the delete icon next to the device</li>
              <li>Confirm the deletion in the confirmation dialog</li>
              <li><strong>Warning:</strong> This action cannot be undone</li>
            </ul>
            
            <h4>Device Types:</h4>
            <p>The system supports several device types, each with specific configuration requirements:</p>
            
            <h5>FUSEBOARD_UDC (Fuseboard UDC)</h5>
            <ul>
              <li><strong>Purpose:</strong> Monitors fuseboard circuits with up to 4 current sensors</li>
              <li><strong>Configuration:</strong> Requires fuse configuration for at least 2 fuses (see Fuse Configuration section)</li>
              <li><strong>Data:</strong> Measures current (A) for each fuse, calculates power (W) using configured voltage and power factor</li>
              <li><strong>Use Case:</strong> Main fuseboard monitoring, circuit-level power tracking</li>
            </ul>
            
            <h5>CS01_NB (CS-01 NB-IoT Current Sensor)</h5>
            <ul>
              <li><strong>Purpose:</strong> NB-IoT current sensor supporting up to 4 current measurement channels</li>
              <li><strong>Configuration:</strong> Requires fuse configuration for at least 2 channels (see Fuse Configuration section)</li>
              <li><strong>Data:</strong> Measures current (A) for each channel, calculates power (W) using configured voltage and power factor</li>
              <li><strong>Use Case:</strong> Remote current monitoring, circuit-level power tracking in hard-to-reach locations</li>
              <li><strong>Features:</strong> Battery-powered, NB-IoT connectivity, low power consumption</li>
            </ul>
            
            <h5>EWATTCH_SQUID (Squid Clamp Device)</h5>
            <ul>
              <li><strong>Purpose:</strong> Multi-clamp current monitoring device supporting up to 12 clamps</li>
              <li><strong>Configuration:</strong> Requires clamp assignment to units (see Squid Clamp Configuration)</li>
              <li><strong>Data:</strong> Measures current (A), power (W), energy (kWh), voltage (V), and other electrical parameters</li>
              <li><strong>Use Case:</strong> Comprehensive electrical monitoring, multiple circuit tracking, energy management</li>
              <li><strong>Features:</strong> Supports various measurement types including active energy, reactive energy, apparent power, frequency</li>
            </ul>
            
            <h5>ELSYS_ELT (Elsys Environmental Sensor)</h5>
            <ul>
              <li><strong>Purpose:</strong> Environmental monitoring sensor</li>
              <li><strong>Configuration:</strong> Basic device setup (identifier, title, site)</li>
              <li><strong>Data:</strong> Temperature, humidity, and other environmental parameters</li>
              <li><strong>Use Case:</strong> Building environmental monitoring, HVAC system monitoring</li>
            </ul>
            
            <h5>ELVACO_4130 (Elvaco Heat Meter)</h5>
            <ul>
              <li><strong>Purpose:</strong> Heat meter for district heating systems</li>
              <li><strong>Configuration:</strong> Basic device setup (identifier, title, site)</li>
              <li><strong>Data:</strong> Energy consumption (kWh), flow rate (m³/h), forward/return temperatures (°C), power (W)</li>
              <li><strong>Use Case:</strong> District heating monitoring, heat network management, energy billing</li>
              <li><strong>Features:</strong> Supports M-Bus protocol, multiple tariff periods, error flag detection</li>
            </ul>
            
            <div class="help-tip">
              <strong>Tip:</strong> Use the search and filter features to quickly find specific devices when managing large numbers of devices. Each device type has specific configuration requirements - ensure these are completed for accurate data monitoring.
            </div>`,
    'helpContent.fuseConfiguration': `<p>FUSEBOARD_UDC and CS01_NB devices require fuse configuration to properly monitor and calculate power usage.</p>

            <h4>Fuse Configuration Requirements:</h4>
            <ul>
              <li><strong>Minimum 2 Fuses/Channels</strong> - At least fuses/channels 1 and 2 must be configured</li>
              <li><strong>Fuses/Channels 3 and 4</strong> - Optional, can be left empty</li>
              <li><strong>Required Fields</strong> - Device name, category, voltage, and power factor</li>
              <li><strong>Applies To:</strong> FUSEBOARD_UDC (fuses) and CS01_NB (channels) devices</li>
            </ul>
            
            <h4>Configuring a Fuse/Channel:</h4>
            <ol>
              <li>Enter the <strong>Device Name</strong> (e.g., "Main Supply", "Circuit A", "Kitchen Appliances")</li>
              <li>Select the <strong>Category</strong>:
                <ul>
                  <li><strong>Supply</strong> - Main power supply entry point</li>
                  <li><strong>Circuit</strong> - Individual circuit or branch</li>
                  <li><strong>Load</strong> - Specific load or appliance</li>
                  <li><strong>Other</strong> - Other category not listed above</li>
                </ul>
              </li>
              <li>Enter the <strong>Voltage (V)</strong> - Typically 240V for UK systems, 230V for EU systems</li>
              <li>Enter the <strong>Power Factor</strong> - A value between 0 and 1 (typically 0.9 for most loads, 1.0 for resistive loads)</li>
            </ol>

            <h4>Device-Specific Notes:</h4>
            <ul>
              <li><strong>FUSEBOARD_UDC:</strong> Each fuse represents a physical fuse position on the fuseboard. Configure fuses 1-4 based on your installation.</li>
              <li><strong>CS01_NB:</strong> Each channel represents a current sensor input. Configure channels 1-4 based on which sensors are connected.</li>
            </ul>
            
            <h4>Important Notes:</h4>
            <ul>
              <li>Fuse/channel configuration values are read during installation and remain constant</li>
              <li>These values are used for accurate power calculations: Power (W) = Voltage (V) × Current (A) × Power Factor</li>
              <li>Accurate configuration ensures correct KPI metrics, cost savings calculations, and carbon footprint tracking</li>
              <li>You can update fuse/channel configurations after device creation</li>
              <li>Optional fuses/channels (3 and 4) can be deleted by clearing all fields</li>
            </ul>
            
            <div class="help-tip">
              <strong>Tip:</strong> Ensure voltage and power factor values are accurate for correct power usage calculations and cost savings metrics. For UK installations, standard voltage is 240V and typical power factor is 0.9.
            </div>`,
    'helpContent.organizationSettings': `<p>Organization settings control KPI calculations for all sites unless overridden at the site level.</p>

            <h4>Organization KPI Configuration:</h4>
            
            <h5>Unit Rate (£ per kWh):</h5>
            <ul>
              <li>The cost per kilowatt-hour of electricity</li>
              <li>Used to calculate cost savings</li>
              <li>Example: 0.30 means £0.30 per kWh</li>
              <li>Required field</li>
            </ul>
            
            <h5>Interval Minutes:</h5>
            <ul>
              <li>How often device readings arrive (in minutes)</li>
              <li>Used for accurate time-based calculations</li>
              <li>Example: 5 means readings arrive every 5 minutes</li>
              <li>Required field, must be at least 1</li>
            </ul>
            
            <h5>Carbon Factor (kg CO₂e per kWh):</h5>
            <ul>
              <li>Carbon emission factor for carbon savings calculation</li>
              <li>Used to calculate carbon saved metrics</li>
              <li>Example: 0.233 means 0.233 kg CO₂e per kWh</li>
              <li>Required field</li>
            </ul>
            
            <h4>Saving Organization Settings:</h4>
            <ol>
              <li>Click the "Settings" button in the Admin page header</li>
              <li>Navigate to the "Organisation KPI configuration" section</li>
              <li>Enter or update the values for unit rate, interval minutes, and carbon factor</li>
              <li>Click "Save Organization Settings"</li>
              <li>Settings apply to all sites unless overridden</li>
            </ol>

            <div class="help-tip">
              <strong>Tip:</strong> Update these settings when electricity rates or carbon factors change to ensure accurate KPI calculations.
            </div>`,
    'helpContent.siteSettings': `<p>Site settings allow you to override organization settings for specific sites.</p>

            <h4>Site KPI Configuration:</h4>
            <p>You can override organization settings for individual sites:</p>
            <ul>
              <li><strong>Unit Rate Override</strong> - Site-specific electricity rate</li>
              <li><strong>Interval Minutes Override</strong> - Site-specific reading interval</li>
              <li><strong>Carbon Factor Override</strong> - Site-specific carbon emission factor</li>
            </ul>
            
            <h4>Configuring Site Settings:</h4>
            <ol>
              <li>Click the "Settings" button in the Admin page header</li>
              <li>Navigate to the "Site KPI configuration" section</li>
              <li>Select a site from the dropdown</li>
              <li>Enter override values (leave empty to use organization defaults)</li>
              <li>View current effective values in the small text below each field</li>
              <li>Click "Save Site Settings"</li>
            </ol>
            
            <h4>Understanding Overrides:</h4>
            <ul>
              <li><strong>Empty Override</strong> - Site uses organization default value</li>
              <li><strong>Set Override</strong> - Site uses the override value instead of organization default</li>
              <li><strong>Current Value Display</strong> - Shows the effective value being used (override or default)</li>
            </ul>

            <div class="help-tip">
              <strong>Tip:</strong> Use site overrides when a site has different electricity rates or requires different calculation parameters than the organization default.
            </div>`,
    'helpContent.savingsCarbonReport': `<p>The Savings & Carbon Report provides comprehensive analysis of cost savings, energy efficiency, and carbon impact across your sites.</p>

            <h4>Report Features:</h4>
            <ul>
              <li><strong>Total Savings Metrics</strong> - Overview of total cost savings, carbon savings, and energy saved</li>
              <li><strong>Carbon Reduction</strong> - Total carbon dioxide equivalent (CO₂e) saved in kilograms</li>
              <li><strong>Equivalent Metrics</strong> - Visual comparisons showing equivalent trees planted, cars removed, and homes powered</li>
              <li><strong>Site Savings Chart</strong> - Visual breakdown of savings by site</li>
              <li><strong>Savings Table</strong> - Detailed site-by-site breakdown with cost savings, carbon savings, and energy saved</li>
              <li><strong>Time Range Selection</strong> - Filter data by time period (30 days, 90 days, or custom range)</li>
            </ul>
            
            <h4>Understanding Savings Metrics:</h4>
            <ul>
              <li><strong>Cost Savings</strong> - Calculated based on energy saved and unit rate (£ per kWh)</li>
              <li><strong>Carbon Savings</strong> - Calculated based on energy saved and carbon factor (kg CO₂e per kWh)</li>
              <li><strong>Energy Saved (kWh)</strong> - Total energy saved through efficiency measures</li>
            </ul>
            
            <h4>Equivalent Metrics:</h4>
            <ul>
              <li><strong>Trees Planted</strong> - Equivalent number of trees that would absorb the same amount of CO₂</li>
              <li><strong>Cars Removed</strong> - Equivalent number of cars removed from the road</li>
              <li><strong>Homes Powered</strong> - Equivalent number of homes that could be powered with the energy saved</li>
            </ul>

            <div class="help-tip">
              <strong>Tip:</strong> Use the time range selector to analyze savings trends over different periods. Compare site performance to identify best practices.
            </div>`,
    'helpContent.leagueTableReport': `<p>The League Table Report provides device and site performance rankings, allowing you to compare metrics across your portfolio and identify top and bottom performers.</p>

            <h4>What the League Table Shows:</h4>
            <ul>
              <li><strong>Performance Rankings</strong> - Sites or devices ranked from highest to lowest based on selected metrics</li>
              <li><strong>Key Metrics</strong> - Energy consumption, cost, carbon emissions, efficiency scores, and alert counts</li>
              <li><strong>Trend Indicators</strong> - Visual indicators showing performance change compared to previous period</li>
              <li><strong>Comparison View</strong> - Side-by-side comparison of performance across all monitored locations</li>
            </ul>

            <h4>How Rankings Are Calculated:</h4>
            <ul>
              <li><strong>Energy Efficiency</strong> - Based on kWh consumption relative to baseline or similar sites</li>
              <li><strong>Cost Performance</strong> - Calculated using energy consumption and configured unit rates</li>
              <li><strong>Carbon Impact</strong> - Derived from energy usage multiplied by carbon factor (kg CO₂e per kWh)</li>
              <li><strong>Alert Performance</strong> - Number and severity of alerts generated during the period</li>
              <li><strong>Uptime Score</strong> - Percentage of time devices were online and reporting data</li>
            </ul>

            <h4>Interpreting the Metrics:</h4>
            <ul>
              <li><strong>Green indicators (↑)</strong> - Performance improved compared to previous period</li>
              <li><strong>Red indicators (↓)</strong> - Performance declined compared to previous period</li>
              <li><strong>Rank Position</strong> - Higher rank indicates better performance for efficiency metrics, lower rank for consumption metrics</li>
              <li><strong>Comparison to Average</strong> - Shows how each site/device compares to the portfolio average</li>
            </ul>

            <h4>Using the League Table:</h4>
            <ul>
              <li>Click column headers to sort by different metrics</li>
              <li>Use the search bar to find specific sites or devices</li>
              <li>Select time range to compare performance over different periods</li>
              <li>Filter by site type or device category for focused comparisons</li>
            </ul>

            <h4>Export Options:</h4>
            <ul>
              <li><strong>CSV</strong> - Export raw data for spreadsheet analysis</li>
              <li><strong>PDF</strong> - Generate a formatted report for sharing and printing</li>
              <li><strong>Excel</strong> - Export with formatting preserved for further analysis</li>
            </ul>

            <div class="help-tip">
              <strong>Tip:</strong> Use the league table to identify best practices from top-performing sites. Investigate why certain locations rank highly and apply those learnings to improve underperforming sites.
            </div>

            <div class="help-placeholder">
              <p><strong>Screenshot: League Table Report</strong></p>
              <p>Image showing device/site performance rankings with comparison metrics</p>
            </div>`,
    'helpContent.sensorHealthReport': `<p>The Sensor Health Report provides comprehensive monitoring of device health, battery levels, uptime, and alert performance across your sites, helping you proactively maintain your sensor network.</p>

            <h4>What the Report Shows:</h4>
            <ul>
              <li><strong>Health Summary</strong> - Overview cards showing total devices, healthy devices, devices with warnings, and devices with critical issues</li>
              <li><strong>Device Health Table</strong> - Detailed list of all devices with individual health metrics</li>
              <li><strong>Battery Monitoring</strong> - Battery levels for all battery-powered sensors</li>
              <li><strong>Connectivity Status</strong> - Last seen timestamps and connection reliability</li>
              <li><strong>Alert Performance</strong> - Alert counts and response statistics per device</li>
            </ul>

            <h4>Key Metrics Displayed:</h4>
            <ul>
              <li><strong>Battery Level (%)</strong> - Current battery percentage for battery-powered devices</li>
              <li><strong>Last Seen</strong> - Time since the device last reported data</li>
              <li><strong>Uptime (%)</strong> - Percentage of time the device has been online during the selected period</li>
              <li><strong>Data Points</strong> - Number of readings received from the device</li>
              <li><strong>Alert Count</strong> - Number of alerts generated by the device</li>
              <li><strong>Response Time</strong> - Average time from data receipt to alert generation</li>
            </ul>

            <h4>Understanding Health Status:</h4>
            <ul>
              <li><strong>Healthy (Green)</strong> - Device operating normally with good battery (>30%), online, and reporting regularly</li>
              <li><strong>Warning (Yellow)</strong> - Device has minor issues: low battery (10-30%), intermittent connectivity, or reduced data frequency</li>
              <li><strong>Critical (Red)</strong> - Device needs immediate attention: very low battery (<10%), offline for extended period, or no recent data</li>
            </ul>

            <h4>Identifying Devices Needing Attention:</h4>
            <ol>
              <li><strong>Sort by Status</strong> - Click the status column to group critical and warning devices at the top</li>
              <li><strong>Filter by Health</strong> - Use health status filters to show only problematic devices</li>
              <li><strong>Check Battery Alerts</strong> - Look for devices with battery levels below 20%</li>
              <li><strong>Review Offline Devices</strong> - Identify devices that haven't reported in over 24 hours</li>
              <li><strong>Monitor Uptime Drops</strong> - Watch for devices with declining uptime percentages</li>
            </ol>

            <h4>Alert Performance Metrics:</h4>
            <ul>
              <li><strong>Alerts Generated</strong> - Total alerts triggered by each device during the period</li>
              <li><strong>Alert Types</strong> - Breakdown by severity (critical, warning, informational)</li>
              <li><strong>Alert Frequency</strong> - Average alerts per day or week</li>
              <li><strong>Resolution Rate</strong> - Percentage of alerts that were acknowledged and resolved</li>
            </ul>

            <h4>Time Range Selection:</h4>
            <ul>
              <li><strong>7 Days</strong> - Short-term health overview for recent issues</li>
              <li><strong>30 Days</strong> - Standard monitoring period for trend analysis</li>
              <li><strong>Custom Range</strong> - Select specific dates for detailed investigation</li>
            </ul>

            <div class="help-tip">
              <strong>Tip:</strong> Schedule regular reviews of the Sensor Health Report to catch issues before they become critical. Devices showing declining battery or uptime trends should be prioritised for maintenance visits.
            </div>`,
    'helpContent.settingsPage': `<p>The Settings page allows you to customise your experience, configure report visibility, and manage home dashboard KPIs.</p>

            <h4>Settings Sections:</h4>
            <ul>
              <li><strong>Organisation Settings</strong> - Configure organization-wide KPI parameters (unit rate, interval minutes, carbon factor)</li>
              <li><strong>Site Settings</strong> - Override organization settings for specific sites</li>
              <li><strong>Home KPIs</strong> - Configure which KPIs appear on the home dashboard</li>
              <li><strong>Report Visibility</strong> - Control which reports are visible to you</li>
              <li><strong>Default Report Visibility</strong> (Admin only) - Set default report visibility for all users</li>
              <li><strong>Map Building Configuration</strong> - Assign buildings on the network map to sites, units, or devices (see Map Building Configuration section for details)</li>
            </ul>
            
            <h4>Home KPI Configuration:</h4>
            <ul>
              <li>Select up to 4 KPIs to display on the home dashboard</li>
              <li>Choose from available KPI options (Carbon Saved, Cost Savings, Total Usage, Grid vs Circuit Voltage, etc.)</li>
              <li>KPIs are displayed in the order you select them</li>
              <li>Changes take effect immediately after saving</li>
            </ul>
            
            <h4>Report Visibility:</h4>
            <ul>
              <li>Toggle individual reports on or off</li>
              <li>Hidden reports won't appear in the Reports page</li>
              <li>Administrators can set default visibility for all users</li>
              <li>Your personal settings override default visibility</li>
            </ul>

            <div class="help-tip">
              <strong>Tip:</strong> Customize your home dashboard KPIs to show the metrics most important to you. Hide reports you don't use to simplify the Reports page.
            </div>`,
    'helpContent.notifications': `<p>The Notifications panel provides quick access to unread alerts and important system updates.</p>

            <h4>Accessing Notifications:</h4>
            <ul>
              <li>Click the notification bell icon in the header</li>
              <li>The bell shows a badge with the number of unread alerts</li>
              <li>The notifications panel slides in from the right</li>
            </ul>
            
            <h4>Notification Features:</h4>
            <ul>
              <li><strong>Unread Alerts</strong> - Shows all unread alerts that haven't been resolved</li>
              <li><strong>Alert Details</strong> - Each notification shows the alert message and timestamp</li>
              <li><strong>View All Alerts</strong> - Click "View All Alerts" to go to the Alerts page</li>
              <li><strong>Auto-Refresh</strong> - Notifications update automatically every 30 seconds</li>
            </ul>
            
            <h4>Understanding Alert Status:</h4>
            <ul>
              <li><strong>Unread</strong> - Alert has not been read yet</li>
              <li><strong>Read</strong> - Alert has been viewed</li>
              <li><strong>Resolved</strong> - Alert has been resolved and won't appear in notifications</li>
            </ul>
            
            <div class="help-tip">
              <strong>Tip:</strong> Keep the notifications panel open to stay updated on new alerts. Click on an alert to navigate directly to the Alerts page.
            </div>`,
    'helpContent.favouriteSites': `<p>Favourite sites allow you to quickly access your most important sites by marking them as favourites.</p>

            <h4>Marking Sites as Favourites:</h4>
            <ul>
              <li>Click the star icon on any site card to mark it as a favourite</li>
              <li>Favourite sites are indicated by a filled/coloured star icon</li>
              <li>Click the star again to remove the favourite status</li>
            </ul>
            
            <h4>Favourite Site Benefits:</h4>
            <ul>
              <li><strong>Top of List</strong> - Favourite sites appear at the top of the sites list</li>
              <li><strong>Quick Access</strong> - Easily find your most important sites</li>
              <li><strong>Sorted First</strong> - Favourites are sorted before non-favourite sites</li>
              <li><strong>Persistent</strong> - Favourite status is saved and persists across sessions</li>
            </ul>

            <div class="help-tip">
              <strong>Tip:</strong> Mark your most frequently accessed sites as favourites to save time navigating. Favourite sites are always at the top of the list, making them easy to find.
            </div>`,
    'helpContent.mapBuildingConfiguration': `<p>Map Building Configuration allows you to assign buildings on the network map to specific sites, units, or devices. This creates visual markers on the map and enables building colours to reflect alert status.</p>

            <h4>What is Map Building Configuration?</h4>
            <p>This feature lets you visually organize your assets on the network map by assigning one or more buildings to a site, unit, or device. Once configured:</p>
            <ul>
              <li>An icon appears on the map at the location of the assigned buildings</li>
              <li>The icon type indicates the asset type (Building icon for Sites, Home icon for Units, Activity icon for Devices)</li>
              <li>Buildings change colour based on the highest priority alert for the assigned asset:
                <ul>
                  <li><strong>Red</strong> - Critical alerts</li>
                  <li><strong>Orange</strong> - Warning alerts</li>
                  <li><strong>Blue</strong> - Info alerts</li>
                  <li><strong>Green</strong> - No alerts</li>
                </ul>
              </li>
              <li>Hovering over the icon shows a tooltip with configuration details and an "Enter Dashboard" button</li>
            </ul>
            
            <h4>Creating a Configuration:</h4>
            <ol>
              <li>Go to Settings → Map Buildings</li>
              <li>Click "Add Configuration"</li>
              <li>Enter a name for the configuration (e.g., "Mould Risk Site")</li>
              <li>Select the asset type (Site, Unit, or Device)</li>
              <li>Choose the specific asset from the dropdown</li>
              <li>On the map, click on buildings to select them (selected buildings will be highlighted in blue)</li>
              <li>You can select multiple buildings by clicking on each one</li>
              <li>Click "Save Configuration" to save</li>
            </ol>

            <h4>Editing a Configuration:</h4>
            <ul>
              <li>Click the edit icon (pencil) next to any configuration</li>
              <li>Modify the name, asset type, asset selection, or building selection</li>
              <li>Click "Save Configuration" to update</li>
            </ul>
            
            <h4>Deleting a Configuration:</h4>
            <ul>
              <li>Click the delete icon (trash) next to any configuration</li>
              <li>Confirm the deletion</li>
              <li>The configuration and its map markers will be removed</li>
            </ul>
            
            <h4>Icon Placement:</h4>
            <ul>
              <li>For a single building, the icon is placed on that building</li>
              <li>For multiple buildings, the icon is placed at the central point of all selected buildings</li>
              <li>The icon position is calculated automatically</li>
            </ul>
            
            <h4>Interacting with Configuration Icons on the Map:</h4>
            <ul>
              <li><strong>Hover</strong> - Hover over an icon to see a tooltip with:
                <ul>
                  <li>Configuration name</li>
                  <li>Asset type and name</li>
                  <li>Number of buildings</li>
                  <li>Alert status and count</li>
                  <li>"Enter Dashboard" button</li>
                </ul>
              </li>
              <li><strong>Click Icon</strong> - Click on an icon to zoom the map to show all buildings in that configuration</li>
              <li><strong>Enter Dashboard</strong> - Click the "Enter Dashboard" button in the tooltip to navigate to the asset's dashboard page</li>
            </ul>

            <h4>Building Colours:</h4>
            <p>All buildings in a configuration are coloured based on the highest priority alert for the assigned asset. The colour updates automatically as alerts change:</p>
            <ul>
              <li>If any device in the asset has a Critical alert, buildings are red</li>
              <li>If any device has a Warning alert (but no Critical), buildings are orange</li>
              <li>If any device has an Info alert (but no Critical or Warning), buildings are blue</li>
              <li>If there are no alerts, buildings are green</li>
            </ul>
            
            <div class="help-tip">
              <strong>Tip:</strong> Use Map Building Configuration to create a visual overview of your assets on the network map. This makes it easy to see at a glance which areas have alerts and quickly navigate to asset dashboards.
            </div>`,
    'helpContent.chatbotOverview': `<p>The Chat Assistant is an AI-powered help tool available on the Help page. It can answer questions about the application and guide you to relevant documentation.</p>

            <h4>Accessing the Chat Assistant:</h4>
            <ul>
              <li>Look for the chat button in the bottom-right corner of the Help page</li>
              <li>Click the chat icon to open the chat window</li>
              <li>The assistant greets you with a welcome message</li>
            </ul>
            
            <h4>Asking Questions:</h4>
            <ul>
              <li>Type your question in the input field at the bottom</li>
              <li>Press Enter or click the send button</li>
              <li>The assistant will provide an answer based on your question</li>
              <li>You can ask follow-up questions in the same conversation</li>
            </ul>
            
            <h4>What You Can Ask:</h4>
            <ul>
              <li><strong>Navigation</strong> - "How do I navigate to alerts?"</li>
              <li><strong>Features</strong> - "What are KPIs?" or "How do I export reports?"</li>
              <li><strong>Alerts</strong> - "How do I acknowledge an alert?"</li>
              <li><strong>Settings</strong> - "How do I change the theme?"</li>
              <li><strong>General Help</strong> - "How do I filter sites?"</li>
            </ul>

            <h4>Chat Features:</h4>
            <ul>
              <li><strong>Message History</strong> - See your conversation history in the chat window</li>
              <li><strong>Timestamps</strong> - Each message shows when it was sent</li>
              <li><strong>Typing Indicator</strong> - See when the assistant is typing a response</li>
              <li><strong>Close/Reopen</strong> - Close the chat and reopen it anytime</li>
            </ul>
            
            <div class="help-tip">
              <strong>Tip:</strong> The chat assistant can help you quickly find information without browsing through all the help sections. Try asking specific questions for best results.
            </div>`,
    'helpContent.bestPractices': `<h4>Dashboard Usage:</h4>
            <ul>
              <li>Check the Overview dashboard regularly for quick insights</li>
              <li>Set up alert configurations proactively to catch issues early</li>
              <li>Use the site map to get a visual overview of all locations</li>
              <li>Review reports periodically to identify trends</li>
            </ul>
            
            <h4>Alert Management:</h4>
            <ul>
              <li>Acknowledge alerts promptly to track your review status</li>
              <li>Resolve alerts after addressing the underlying issue</li>
              <li>Use severity filters to focus on critical issues first</li>
              <li>Set up offline monitoring for critical devices</li>
            </ul>
            
            <h4>Data Analysis:</h4>
            <ul>
              <li>Compare data across different time ranges to identify patterns</li>
              <li>Export reports for offline analysis and record-keeping</li>
              <li>Use site notes to document important findings</li>
              <li>Review historical trends to predict future needs</li>
            </ul>`,
    'helpContent.commonIssues': `<h4>Map Not Loading:</h4>
            <ul>
              <li>Check your internet connection</li>
              <li>Try refreshing the page</li>
              <li>Switch map styles - sometimes a different style loads faster</li>
              <li>Clear your browser cache if issues persist</li>
            </ul>
            
            <h4>Charts Not Displaying:</h4>
            <ul>
              <li>Ensure you have data for the selected time range</li>
              <li>Try selecting a different time range</li>
              <li>Check if filters are too restrictive</li>
              <li>Refresh the page to reload data</li>
            </ul>
            
            <h4>Alerts Not Showing:</h4>
            <ul>
              <li>Check the filter settings - you might have filters applied</li>
              <li>Verify that alert configurations are enabled</li>
              <li>Check if you're looking at the correct status (Active vs Resolved)</li>
              <li>Ensure devices are online and reporting data</li>
            </ul>
            
            <h4>Performance Issues:</h4>
            <ul>
              <li>Close unnecessary browser tabs</li>
              <li>Try a different browser if current one is slow</li>
              <li>Reduce the time range for reports with large datasets</li>
              <li>Contact support if issues persist</li>
            </ul>`,
    'helpContent.keyboardShortcuts': `<p>While the application is primarily mouse-driven, here are some useful keyboard shortcuts:</p>
            
            <ul>
              <li><strong>Ctrl/Cmd + F</strong> - Focus on search fields (when available)</li>
              <li><strong>Esc</strong> - Close modals and popups</li>
              <li><strong>Tab</strong> - Navigate between form fields</li>
              <li><strong>Enter</strong> - Submit forms or activate buttons</li>
            </ul>
            
            <div class="help-tip">
              <strong>Tip:</strong> Most actions can be performed with mouse clicks. Keyboard shortcuts are provided as a convenience.
            </div>`,
    'helpContent.gettingHelp': `<h4>In-App Resources:</h4>
            <ul>
              <li>This help page - comprehensive guide to all features</li>
              <li>Tooltips - hover over buttons and icons for quick information</li>
              <li>Contextual help - some pages have inline help text</li>
            </ul>
            
            <h4>Support Channels:</h4>
            <ul>
              <li>Contact your system administrator for account and access issues</li>
              <li>Reach out to technical support for bugs or technical problems</li>
              <li>Check system notifications for important updates and announcements</li>
            </ul>
            
            <div class="help-tip">
              <strong>Tip:</strong> When reporting issues, include screenshots and details about what you were trying to do when the issue occurred.
            </div>`,
    'helpContent.aiHubOverview': `<p>The AI Hub is your central location for all AI-powered features and insights. It provides a suite of tools to analyse your building data, generate insights, and optimise operations.</p>

            <h4>Available Features:</h4>
            <ul>
              <li><strong>AI Insights Wizard</strong> <em>(Popular)</em> - Step-by-step wizard to generate custom AI insights tailored to your specific needs</li>
              <li><strong>AI Assistant</strong> <em>(Beta)</em> - Chat interface to ask questions about your data and get instant answers</li>
              <li><strong>Smart Recommendations</strong> <em>(Beta)</em> - AI-powered suggestions to optimise your operations based on building data</li>
              <li><strong>Predictive Analytics</strong> <em>(Beta)</em> - Forecast trends and predict future outcomes based on historical data</li>
              <li><strong>Auto Optimisation</strong> <em>(Coming Soon)</em> - Automated recommendations for energy and cost savings</li>
              <li><strong>Anomaly Detection</strong> <em>(Beta)</em> - Detect unusual patterns and potential issues in your system data</li>
            </ul>

            <h4>Filtering Features:</h4>
            <p>Use the circular icon filter buttons at the top to filter features by category:</p>
            <ul>
              <li><strong>All Features</strong> - View all available features</li>
              <li><strong>Generation</strong> - Features that generate insights and reports (AI Insights Wizard)</li>
              <li><strong>Interactive</strong> - Features for direct interaction with AI (AI Assistant)</li>
              <li><strong>Intelligence</strong> - AI-powered recommendation features (Smart Recommendations)</li>
              <li><strong>Analytics</strong> - Data analysis and forecasting features (Predictive Analytics)</li>
              <li><strong>Monitoring</strong> - Pattern detection and monitoring features (Anomaly Detection)</li>
              <li><strong>Optimisation</strong> - Features focused on improving efficiency (Auto Optimisation)</li>
            </ul>

            <h4>Feature Status Badges:</h4>
            <ul>
              <li><strong>Popular</strong> - Most frequently used features</li>
              <li><strong>Beta</strong> - Features in testing phase with full functionality</li>
              <li><strong>Coming Soon</strong> - Features currently in development</li>
            </ul>

            <div class="help-tip">
              <strong>Tip:</strong> Start with the AI Insights Wizard to generate your first custom insights. Click on any feature card to access that specific AI tool.
            </div>`,
    'helpContent.aiInsightsWizard': `<p>The AI Insights Wizard guides you through a step-by-step process to generate custom AI insights tailored to your specific needs.</p>

            <h4>Wizard Steps:</h4>
            <ol>
              <li><strong>Select Site</strong> - Choose the site you want to analyse with AI insights</li>
              <li><strong>Select Unit (Optional)</strong> - Choose one or more units, or select all units to analyse. You can skip this step if you want to analyse all units.</li>
              <li><strong>Select Device (Optional)</strong> - Choose a specific device or analyse all devices. You can skip this step to analyse all devices.</li>
              <li><strong>AI Capabilities</strong> - Choose one or more AI capabilities to generate tailored insights:
                <ul>
                  <li>Predictive Maintenance - Anticipate equipment needs before failures</li>
                  <li>Energy Optimisation - Reduce consumption and operational costs</li>
                  <li>Anomaly Detection - Real-time pattern recognition and alerts</li>
                  <li>Cost Analysis - Calculate savings and ROI automatically</li>
                  <li>Forecasting - Predict future performance and trends</li>
                  <li>Carbon Impact - Track and optimise sustainability</li>
                </ul>
              </li>
              <li><strong>Generate Insights</strong> - Review your selections and generate insights</li>
            </ol>
            
            <h4>Using the Wizard:</h4>
            <ul>
              <li>Click "Next Step" to proceed through the wizard</li>
              <li>Click "Back" to return to previous steps</li>
              <li>Click "Skip" to skip optional steps (Units and Devices)</li>
              <li>Select multiple items where applicable (units, devices, capabilities)</li>
              <li>Review your selections on the final step before generating</li>
            </ul>

            <div class="help-tip">
              <strong>Tip:</strong> You can save generated insights to access them later from the Saved Insights feature.
            </div>`,
    'helpContent.aiAssistant': `<p>The AI Assistant allows you to chat with AI to get instant answers about your data, sites, devices, energy consumption, or insights.</p>

            <h4>Using the AI Assistant:</h4>
            <ul>
              <li>Type your question in the input field at the bottom</li>
              <li>Press Enter or click the send button to submit your question</li>
              <li>The AI will analyse your data and provide a detailed response</li>
              <li>You can ask follow-up questions in the same conversation</li>
            </ul>
            
            <h4>What You Can Ask:</h4>
            <ul>
              <li>Questions about your sites and devices</li>
              <li>Energy consumption analysis</li>
              <li>Optimisation opportunities</li>
              <li>Request detailed reports</li>
              <li>General questions about your data</li>
            </ul>
            
            <div class="help-tip">
              <strong>Tip:</strong> Be specific in your questions for the best results. The AI has access to your building data and can provide detailed, contextual answers.
            </div>`,
    'helpContent.smartRecommendations': `<p>Smart Recommendations provides AI-powered suggestions to optimise your operations based on your building data.</p>

            <h4>Recommendation Features:</h4>
            <ul>
              <li><strong>Priority Levels</strong> - Recommendations are categorised by priority (High, Medium, Low)</li>
              <li><strong>Actionable Insights</strong> - Each recommendation includes specific actions you can take</li>
              <li><strong>Data-Driven</strong> - Recommendations are based on actual analysis of your building data</li>
              <li><strong>Savings Calculations</strong> - Many recommendations include potential savings or ROI calculations</li>
            </ul>
            
            <h4>Generating Recommendations:</h4>
            <ul>
              <li>Click "Generate Recommendations" to create personalised recommendations</li>
              <li>The system will analyse your data and generate suggestions</li>
              <li>Recommendations are updated based on the latest data</li>
            </ul>
            
            <div class="help-tip">
              <strong>Tip:</strong> Review recommendations regularly as your building data changes. New opportunities may appear as patterns emerge.
            </div>`,
    'helpContent.predictiveAnalytics': `<p>Predictive Analytics forecasts trends and predicts future outcomes based on your historical data.</p>

            <h4>Analytics Features:</h4>
            <ul>
              <li><strong>Trend Forecasting</strong> - Predict future performance based on historical patterns</li>
              <li><strong>Metric Predictions</strong> - Forecast key metrics like energy consumption, costs, and usage</li>
              <li><strong>Visual Charts</strong> - Graphical representation of predictions and trends</li>
              <li><strong>Data-Driven Insights</strong> - Predictions based on actual historical data analysis</li>
            </ul>
            
            <h4>Using Predictive Analytics:</h4>
            <ul>
              <li>View forecasted trends for your key metrics</li>
              <li>Identify potential future issues before they occur</li>
              <li>Plan for future energy needs and costs</li>
              <li>Make data-driven decisions based on predictions</li>
            </ul>
            
            <div class="help-tip">
              <strong>Tip:</strong> Use predictive analytics to plan maintenance, budget for energy costs, and anticipate system needs.
            </div>`,
    'helpContent.dynamicTelemetry': `<p>Dynamic Telemetry intelligently controls sensor reporting rates based on real-time environmental conditions.</p>

            <h4>Key Features:</h4>
            <ul>
              <li><strong>Adaptive Sampling</strong> - Automatically increases data capture rates when conditions worsen</li>
              <li><strong>Condition-Based Triggers</strong> - Adjusts reporting intervals based on environmental thresholds</li>
              <li><strong>Battery Optimisation</strong> - Reduces reporting frequency during stable conditions to conserve sensor battery life</li>
              <li><strong>Downlink Commands</strong> - Sends configuration updates to sensors via downlink to adjust behaviour in real-time</li>
            </ul>

            <h4>How It Works:</h4>
            <ul>
              <li>AI monitors environmental trends across your sensor network</li>
              <li>When conditions approach critical thresholds, sensors are instructed to report more frequently</li>
              <li>During stable periods, reporting rates are reduced to save power and bandwidth</li>
              <li>All adjustments are logged and can be reviewed in the telemetry history</li>
            </ul>

            <div class="help-tip">
              <strong>Tip:</strong> Dynamic Telemetry ensures you capture the most detailed data exactly when it matters most, without wasting battery life during stable conditions.
            </div>`,
    'helpContent.anomalyDetection': `<p>Anomaly Detection identifies unusual patterns and potential issues in your system data.</p>

            <h4>Detection Features:</h4>
            <ul>
              <li><strong>Pattern Recognition</strong> - AI identifies unusual patterns in your data</li>
              <li><strong>Severity Levels</strong> - Anomalies categorised by severity (High, Medium, Low)</li>
              <li><strong>Device Identification</strong> - Specific devices or circuits where anomalies occur</li>
              <li><strong>Descriptive Analysis</strong> - Detailed explanation of detected anomalies</li>
            </ul>
            
            <h4>Using Anomaly Detection:</h4>
            <ul>
              <li>Review detected anomalies regularly</li>
              <li>Investigate high-severity anomalies first</li>
              <li>Use anomaly information to diagnose issues</li>
              <li>Take action based on anomaly recommendations</li>
            </ul>
            
            <div class="help-tip">
              <strong>Tip:</strong> Anomaly detection can help you catch issues early before they become major problems.
            </div>`,
  },
  ar: {
    // Navigation
    overview: 'نظرة عامة',
    sites: 'المواقع',
    compliance: 'الامتثال',
    navGroupMain: 'الرئيسية',
    navGroupAnalytics: 'التحليلات',
    navGroupSystem: 'النظام',

    // Dashboard
    totalUsage: 'الاستخدام الإجمالي',
    alerts: 'التنبيهات',
    powerUsageTrend: 'اتجاه استخدام الطاقة',
    active: 'نشط',
    
    // Common
    search: 'بحث',
    backToSites: 'العودة إلى المواقع',
    expandAll: 'توسيع الكل',
    collapseAll: 'طي الكل',
    online: 'متصل',
    offline: 'غير متصل',
    refreshLiveData: 'تحديث البيانات المباشرة',
    enterFullscreen: 'دخول وضع ملء الشاشة',
    exitFullscreen: 'الخروج من وضع ملء الشاشة',
    live: 'مباشر',
    graphic: 'رسم بياني',
    historic: 'تاريخي',
    fuseboardDevice: 'جهاز لوحة التوزيع',
    generatingAiInsights: 'إنشاء رؤى الذكاء الاصطناعي',
    analysingReadings: 'تحليل آخر القراءات من هذا الجهاز...',
    mouldRiskZoneMap: 'خريطة منطقة خطر العفن',
    property: 'خاصية',
    withMouldRisk: 'مع خطر العفن',
    high: 'عالي',
    medium: 'متوسط',
    low: 'منخفض',
    changeLanguage: 'تغيير اللغة',
    searchPlaceholder: 'بحث…',
    logout: 'تسجيل الخروج',
    
    // Loading Messages
    loadingFuseboardHierarchy: 'جاري تحميل هرمية لوحة التوزيع...',
    loadingEnvironmentalDashboard: 'جاري تحميل لوحة المعلومات البيئية...',
    loadingHistoricalData: 'جاري تحميل البيانات التاريخية...',
    loadingDashboardOverview: 'جاري تحميل نظرة عامة على لوحة التحكم...',
    loadingSites: 'جاري تحميل المواقع...',
    loadingReports: 'جاري تحميل التقارير...',
    loadingConfigurations: 'جاري تحميل التكوينات...',
    loadingOfflineDevices: 'جاري تحميل الأجهزة غير المتصلة...',
    noOfflineDevices: 'كل شيء على ما يرام',
    allDevicesReporting: 'جميع الأجهزة تعمل بشكل طبيعي',

    // Buttons & Actions
    exportCsv: 'تصدير CSV',
    exportPdf: 'تصدير PDF',
    exportLeagueTable: 'تصدير جدول الدوري',
    switchTo2D: 'التبديل إلى العرض ثنائي الأبعاد',
    switchTo3D: 'التبديل إلى العرض ثلاثي الأبعاد (يظهر المباني ثلاثية الأبعاد)',
    changeMapStyle: 'تغيير نمط الخريطة',
    collapseAllTreeNodes: 'طي جميع عقد الشجرة',
    expandAllTreeNodes: 'توسيع جميع عقد الشجرة',
    
    // Search Placeholders
    searchAlerts: 'البحث في التنبيهات...',
    searchConfigurations: 'البحث في التكوينات...',
    searchByTime: 'البحث بالوقت (مثل: 14:30، 2024-01-15، 15 يناير)...',
    
    // Export & Reports
    fuseboardChartsExport: 'تصدير مخططات لوحة التوزيع',
    exportedFromSmartInstallers: 'مُصدر من رؤى المثبتين الأذكياء',
    generatedOn: 'تم إنشاؤه في:',
    exportLiveDataToCsv: 'تصدير البيانات المباشرة إلى CSV',
    exportChartsToPdf: 'تصدير المخططات إلى PDF',
    exportHistoricDataToCsv: 'تصدير البيانات التاريخية إلى CSV',
    
    // Form Labels
    to: 'إلى',
    from: 'من',
    
    // Time
    vsLastMonth: 'مقارنة بالشهر الماضي',
    vsLastWeek: 'مقارنة بالأسبوع الماضي',
    
    // Admin Page
    admin: 'المسؤول',
    help: 'المساعدة',
    settings: 'الإعدادات',
    addDevice: 'إضافة جهاز',
    editDevice: 'تعديل جهاز',
    createDevice: 'إنشاء جهاز',
    updateDevice: 'تحديث جهاز',
    deleteDevice: 'حذف جهاز',
    searchDevices: 'البحث عن الأجهزة...',
    allDeviceTypes: 'جميع أنواع الأجهزة',
    noDevicesFound: 'لم يتم العثور على أجهزة',
    tryAdjustingSearch: 'حاول تعديل البحث أو المرشحات',
    identifier: 'المعرف',
    title: 'العنوان',
    actions: 'الإجراءات',
    units: 'الوحدات',
    unitsOptional: 'الوحدات (اختياري)',
    noUnitsAvailable: 'لا توجد وحدات متاحة لهذا الموقع',
    unassignedDevices: 'أجهزة غير مخصصة',
    siteLevel: 'مستوى الموقع',
    notAssignedToUnit: 'غير مخصص لأي وحدة',
    unitsSelected: 'وحدة محددة',
    cancel: 'إلغاء',
    save: 'حفظ',
    delete: 'حذف',
    areYouSure: 'هل أنت متأكد؟',
    cannotBeUndone: 'لا يمكن التراجع عن هذا الإجراء.',
    
    // Fuse Configuration
    fuseConfigurationRequired: 'تكوين الصمامات (مطلوب لـ FUSEBOARD_UDC)',
    configureAtLeast2Fuses: 'قم بتكوين ما لا يقل عن صمامين مع الجهد، ومعامل القدرة، واسم الجهاز، والفئة. الصمامات 3 و 4 اختيارية.',
    fuse: 'صمام',
    deviceName: 'اسم الجهاز',
    category: 'الفئة',
    voltage: 'الجهد (V)',
    powerFactor: 'معامل القدرة',
    supply: 'الإمداد',
    circuit: 'الدائرة',
    load: 'الحمل',
    other: 'أخرى',
    atLeast2FusesRequired: 'يجب تكوين ما لا يقل عن صمامين لأجهزة FUSEBOARD_UDC',
    
    // Organization & Site Settings
    organizationKpiConfiguration: 'تكوين مؤشرات الأداء الرئيسية للمؤسسة',
    siteKpiConfiguration: 'تكوين مؤشرات الأداء الرئيسية للموقع',
    unitRate: 'معدل الوحدة (£ لكل كيلوواط ساعة)',
    intervalMinutes: 'دقائق الفترة',
    carbonFactor: 'عامل الكربون (كجم CO₂e لكل كيلوواط ساعة)',
    howOftenReadingsArrive: 'عدد مرات وصول قراءات الجهاز (بالدقائق)',
    carbonEmissionFactor: 'عامل انبعاث الكربون لحساب توفير الكربون',
    appliesToAllSites: 'تنطبق هذه الإعدادات على جميع المواقع ما لم يتم تجاوزها على مستوى الموقع.',
    overrideOrganizationSettings: 'تجاوز إعدادات المؤسسة لموقع محدد. اتركه فارغًا لاستخدام إعدادات المؤسسة الافتراضية.',
    selectASite: '-- اختر موقعًا --',
    unitRateOverride: 'تجاوز معدل الوحدة (£ لكل كيلوواط ساعة)',
    intervalMinutesOverride: 'تجاوز دقائق الفترة',
    carbonFactorOverride: 'تجاوز عامل الكربون (كجم CO₂e لكل كيلوواط ساعة)',
    leaveEmptyForDefault: 'اتركه فارغًا لاستخدام الإعداد الافتراضي للمؤسسة',
    current: 'الحالي',
    usingOrgDefault: 'استخدام الإعداد الافتراضي للمؤسسة',
    saveOrganizationSettings: 'حفظ إعدادات المؤسسة',
    saveSiteSettings: 'حفظ إعدادات الموقع',
    
    // Alerts
    recentAlerts: 'التنبيهات الأخيرة',
    recentAlertsHub: 'مركز التنبيهات الأخيرة',
    noAlertsFound: 'لم يتم العثور على تنبيهات',
    activeAlerts: 'التنبيهات',
    configurations: 'التكوينات',
    offlineMonitoring: 'مراقبة غير متصل',
    acknowledged: 'معترف به',
    resolved: 'تم الحل',
    critical: 'حرج',
    warning: 'تحذير',
    info: 'معلومات',
    all: 'الكل',
    severity: 'الشدة',
    status: 'الحالة',
    acknowledge: 'الاعتراف',
    resolve: 'حل',
    view: 'عرض',
    viewDetails: 'عرض التفاصيل',
    createAlertRule: 'إنشاء قاعدة تنبيه',
    newMonitor: 'مراقب جديد',
    
    // Common UI
    refresh: 'تحديث',
    close: 'إغلاق',
    open: 'فتح',
    yes: 'نعم',
    no: 'لا',
    confirm: 'تأكيد',
    required: 'مطلوب',
    optional: 'اختياري',
    loading: 'جاري التحميل...',
    error: 'خطأ',
    success: 'نجح',
    failed: 'فشل',
    saved: 'تم الحفظ',
    updated: 'تم التحديث',
    deleted: 'تم الحذف',
    created: 'تم الإنشاء',
    
    // Navigation
    aiHub: 'مركز الذكاء الاصطناعي',
    
    // Pagination
    itemsPerPage: 'عناصر لكل صفحة',
    previous: 'السابق',
    next: 'التالي',
    page: 'صفحة',
    showing: 'عرض',
    item: 'عنصر',
    items: 'عناصر',
    nextStep: 'الخطوة التالية',
    previousAlert: 'التنبيه السابق',
    nextAlert: 'التنبيه التالي',
    
    // Map
    gridVsCircuitVoltage: 'جهد الشبكة مقابل الدائرة',
    
    // Reports
    reports: 'التقارير',
    timeRange: 'نطاق الوقت',
    last24Hours: 'آخر 24 ساعة',
    last7Days: 'آخر 7 أيام',
    last30Days: 'آخر 30 يومًا',
    last90Days: 'آخر 90 يومًا',
    customRange: 'نطاق مخصص',
    
    // Chat Assistant
    helpAssistant: 'مساعد المساعدة',
    askMeAnything: 'اسألني أي شيء عن {companyName} Insights',
    askAQuestion: 'اطرح سؤالاً...',
    send: 'إرسال',
    closeChat: 'إغلاق الدردشة',
    
    // Additional Alert & UI Strings
    viewAll: 'عرض الكل',
    viewAllAlerts: 'عرض جميع التنبيهات',
    refreshAlerts: 'تحديث التنبيهات',
    alertOverview: 'نظرة عامة على التنبيهات',
    unread: 'غير مقروء',
    allSeverity: 'جميع المستويات',
    allStatus: 'جميع الحالات',
    markAsUnseen: 'وضع علامة كغير مرئي',
    allClear: 'كل شيء واضح!',
    noAlertsMatchFilters: 'لا توجد تنبيهات تطابق المرشحات الحالية',
    loadingAlerts: 'جاري تحميل التنبيهات...',
    exportAlertsToCsv: 'تصدير التنبيهات إلى CSV',
    manage: 'إدارة',
    paused: 'متوقف مؤقتاً',
    drafts: 'مسودات',
    editMonitor: 'تعديل المراقب',
    deleteMonitor: 'حذف المراقب',
    noAlertsToExport: 'لا توجد تنبيهات للتصدير',
    total: 'الإجمالي',
    activeFuses: 'الصمامات النشطة',
    overloadAlert: 'تنبيه التحميل الزائد',
    noAlerts: 'لا توجد تنبيهات',
    smartInstallers: 'المثبتون الأذكياء',
    edit: 'تعديل',
    energySaved: 'الطاقة المحفوظة',
    totalEnergy: 'إجمالي الطاقة',
    totalEnergySaved: 'إجمالي الطاقة المحفوظة',
    voltageTrend: 'اتجاه الجهد',
    environmentalTrend: 'الاتجاه البيئي',
    cumulativeTotal: 'المجموع التراكمي',
    
    // Reports
    leagueTable: 'جدول الدوري',
    aiInsights: 'رؤى الذكاء الاصطناعي',
    savingsCarbon: 'التوفير والكربون',
    customisedReporting: 'التقارير المخصصة',
    recommendedActions: 'الإجراءات الموصى بها',
    recommendedAction: 'الإجراء الموصى به',
    noInsightsAvailable: 'لا توجد رؤى متاحة',
    noInsightsDescription: 'حدد جهازًا لعرض الرؤى المُنشأة بالذكاء الاصطناعي',
    sensorHealth: 'صحة المستشعر',

    // Site Manager
    siteNotes: 'مدير الموقع',
    dashboard: 'لوحة التحكم',
    
    // Help Page - UI
    searchHelpTopics: 'البحث في مواضيع المساعدة...',
    searchHelp: 'البحث في المساعدة...',
    tableOfContents: 'جدول المحتويات',
    noResultsFound: 'لم يتم العثور على نتائج',
    tryAdjustingSearchTerms: 'حاول تعديل مصطلحات البحث أو تصفح جدول المحتويات.',
    helpCenter: 'مركز المساعدة',
    helpHeroSubtitle: 'كل ما تحتاجه للاستفادة القصوى من {companyName} Monitoring Platform',

    // Quick Start Guide
    quickStartGuide: 'دليل البدء السريع',
    quickStartSubtitle: 'جديد على {companyName} Monitoring Platform؟ ابدأ هنا للعمل بسرعة.',
    quickStartLogin: 'تسجيل الدخول',
    quickStartLoginDesc: 'الوصول إلى المنصة باستخدام مصادقة Auth0',
    quickStartDashboard: 'نظرة عامة على لوحة التحكم',
    quickStartDashboardDesc: 'فهم مؤشرات الأداء والتنبيهات ومراقبة المواقع',
    quickStartFeatures: 'الميزات الرئيسية',
    quickStartFeaturesDesc: 'استكشف المواقع والتنبيهات والتقارير',
    stillNeedHelp: 'لا تزال بحاجة إلى مساعدة؟',
    stillNeedHelpDesc: 'فريقنا هنا لمساعدتك. استخدم مساعد الدردشة للحصول على الدعم الفوري.',
    openChatAssistant: 'افتح مساعد الدردشة',

    // Help Page - Section Titles
    gettingStarted: 'البدء',
    overviewDashboard: 'لوحة التحكم العامة',
    sitesManagement: 'إدارة المواقع',
    alertsManagement: 'إدارة التنبيهات',
    reportsAnalytics: 'التقارير والتحليلات',
    adminSettings: 'الإدارة والإعدادات',
    chatAssistant: 'مساعد الدردشة',
    tipsTroubleshooting: 'نصائح وحل المشاكل',
    
    // Help Page - Subsection Titles
    loggingIn: 'تسجيل الدخول',
    navigationOverview: 'نظرة عامة على التنقل',
    settingsPreferences: 'الإعدادات والتفضيلات',
    understandingKpiCards: 'فهم بطاقات مؤشرات الأداء الرئيسية',
    recentAlertsSection: 'قسم التنبيهات الأخيرة',
    siteMap: 'خريطة الموقع',
    sitesPageOverview: 'نظرة عامة على صفحة المواقع',
    siteDashboards: 'لوحات تحكم الموقع',
    siteNotesAnnotations: 'مدير الموقع والتعليقات',
    alertsPageOverview: 'نظرة عامة على صفحة التنبيهات',
    viewingFilteringAlerts: 'عرض وتصفية التنبيهات',
    alertActions: 'إجراءات التنبيه',
    alertConfigurations: 'تكوينات التنبيه',
    offlineDeviceMonitoring: 'مراقبة الأجهزة غير المتصلة',
    reportsPageOverview: 'نظرة عامة على صفحة التقارير',
    selectingTimeRanges: 'اختيار نطاقات الوقت',
    understandingChartsGraphs: 'فهم المخططات والرسوم البيانية',
    exportingReports: 'تصدير التقارير',
    customReportingServices: 'خدمات التقارير المخصصة',
    adminPageOverview: 'نظرة عامة على صفحة الإدارة',
    deviceManagement: 'إدارة الأجهزة',
    fuseConfiguration: 'تكوين الصمامات (FUSEBOARD_UDC)',
    organizationSettings: 'إعدادات المؤسسة',
    siteSettings: 'إعدادات الموقع',
    usingChatAssistant: 'استخدام مساعد الدردشة',
    bestPractices: 'أفضل الممارسات',
    commonIssuesSolutions: 'المشاكل الشائعة والحلول',
    keyboardShortcuts: 'اختصارات لوحة المفاتيح',
    gettingAdditionalHelp: 'الحصول على مساعدة إضافية',
    
    // AI Hub - Section Titles
    aiHubOverview: 'نظرة عامة على مركز الذكاء الاصطناعي',
    aiInsightsWizard: 'معالج رؤى الذكاء الاصطناعي',
    aiAssistant: 'مساعد الذكاء الاصطناعي',
    smartRecommendations: 'التوصيات الذكية',
    savedInsights: 'الرؤى المحفوظة',
    insightHistory: 'تاريخ الرؤى',
    realTimeMonitoring: 'المراقبة في الوقت الفعلي',
    predictiveAnalytics: 'التحليلات التنبؤية',
    dynamicTelemetry: 'القياس الديناميكي عن بُعد',
    aiReports: 'تقارير الذكاء الاصطناعي',
    siteComparison: 'مقارنة المواقع',
    anomalyDetection: 'اكتشاف الشذوذ',
    carbonAnalytics: 'تحليلات الكربون',
    
    // Sites Page
    allTypes: 'جميع الأنواع',
    temporaryPlantRoom: 'غرفة النبات المؤقتة',
    heatNetwork: 'شبكة التدفئة',
    fuseboardManagement: 'إدارة لوحة التوزيع',
    environmentalMonitoring: 'مراقبة البيئة',
    mouldRisk: 'خطر العفن',
    mouldRiskScore: 'نقاط خطر العفن',
    mouldRiskCalculation: 'حساب خطر العفن',
    mouldRiskActive: 'خطر العفن نشط',
    mouldRiskAssessment: 'تقييم خطر العفن',
    environmentalSensor: 'مستشعر بيئي',
    environmentalSensors: 'المستشعرات البيئية',
    averageDaysToMould: 'متوسط الأيام حتى العفن',
    averageDaysToCritical: 'متوسط الأيام إلى الحالة الحرجة',
    averageTemperature: 'متوسط درجة الحرارة',
    averageHumidity: 'متوسط الرطوبة',
    insufficientData: '—',
    devicesAtRisk: 'الأجهزة المعرضة للخطر',
    mouldRiskStatus: 'حالة خطر العفن',
    propertiesAtRisk: 'الأجهزة المعرضة للخطر',
    siteAverageTrend: 'متوسط اتجاه الموقع',
    siteAverage: 'متوسط الموقع',
    risk: 'خطر',
    criticalRisk: 'خطر حرج',
    highRisk: 'خطر عالٍ',
    mediumRisk: 'خطر متوسط',
    lowRisk: 'خطر منخفض',
    noRisk: 'لا يوجد خطر',
    mouldRiskReport: 'تقرير خطر العفن',
    mouldRiskAssessmentReport: 'تقرير تقييم خطر العفن',
    deviceMouldRiskAssessment: 'تقييم خطر العفن للأجهزة',
    days: 'أيام',
    noEnvironmentalSensorsFound: 'لم يتم العثور على أجهزة استشعار بيئية',
    attentionRequired: 'يتطلب الاهتمام',
    allSystemsNormal: 'جميع الأنظمة طبيعية',
    noSiteDataAvailable: 'لا توجد بيانات موقع متاحة',
    noSavingsDataAvailable: 'لا توجد بيانات توفير متاحة',
    noComparisonDataAvailable: 'لا توجد بيانات مقارنة متاحة',
    noSiteSavingsDataAvailable: 'لا توجد بيانات توفير موقع متاحة',
    systemStatus: 'حالة النظام',
    errorLoadingInsights: 'خطأ في تحميل الرؤى',
    highPriority: 'أولوية عالية',
    mediumPriority: 'أولوية متوسطة',
    lowPriority: 'أولوية منخفضة',
    systemHealth: 'صحة النظام',
    popular: 'شائع',
    new: 'جديد',
    beta: 'الوصول المبكر',
    selectSite: 'اختر الموقع',
    selectUnit: 'اختر الوحدة',
    selectDevice: 'اختر الجهاز',
    aiCapabilities: 'قدرات الذكاء الاصطناعي',
    generate: 'إنشاء',
    predictiveMaintenance: 'الصيانة التنبؤية',
    energyOptimisation: 'تحسين الطاقة',
    costAnalysis: 'تحليل التكلفة',
    forecasting: 'التنبؤ',
    carbonImpact: 'تأثير الكربون',
    aiInsightsWizardDesc: 'إنشاء رؤى مخصصة للذكاء الاصطناعي مع إرشادات خطوة بخطوة',
    aiAssistantDesc: 'الدردشة مع الذكاء الاصطناعي للحصول على إجابات فورية حول بياناتك',
    smartRecommendationsDesc: 'اقتراحات مدعومة بالذكاء الاصطناعي لتحسين عملياتك',
    savedInsightsDesc: 'الوصول إلى الرؤى المحفوظة والمفضلة لديك',
    insightHistoryDesc: 'عرض الرؤى والتقارير التحليلية السابقة',
    realTimeMonitoringDesc: 'المراقبة والتنبيهات المباشرة المدعومة بالذكاء الاصطناعي',
    predictiveAnalyticsDesc: 'التنبؤ بالاتجاهات والنتائج المستقبلية',
    dynamicTelemetryDesc: 'يتحكم ديناميكيًا في معدل إرسال بيانات المستشعرات بناءً على الظروف البيئية في الوقت الفعلي',
    aiReportsDesc: 'إنشاء تقارير تحليلية شاملة للذكاء الاصطناعي',
    siteComparisonDesc: 'مقارنة الأداء عبر مواقع متعددة باستخدام الذكاء الاصطناعي',
    anomalyDetectionDesc: 'اكتشاف الأنماط غير العادية والمشاكل المحتملة',
    carbonAnalyticsDesc: 'تتبع وتحسين بصمتك الكربونية',
    backToHub: 'العودة إلى المركز',
    startConversation: 'بدء محادثة',
    askMeAnythingAbout: 'اسألني أي شيء عن مواقعك أو أجهزتك أو استهلاك الطاقة أو الرؤى',
    loadingReportData: 'جاري تحميل بيانات التقرير...',
    generatingComprehensiveReport: 'إنشاء تقرير تحليلي شامل',
    comprehensiveAnalysisReport: 'تقرير التحليل الشامل',
    downloadReport: 'تنزيل التقرير',
    analysisOfEnvironmentalConditions: 'تحليل الظروف البيئية ومخاطر نمو العفن عبر جميع المواقع المراقبة',
    siteCostSavings: 'توفير تكاليف الموقع',
    topSitesByCostSavings: 'أفضل {count} مواقع حسب توفير التكاليف',
    costSavingsBySite: 'توفير التكاليف حسب الموقع',
    costSavingsBreakdown: 'تفصيل توفير التكاليف لهذا الموقع',
    leadingLocationsContributing: 'المواقع الرائدة المساهمة في إجمالي التوفير',
    siteBySiteBreakdown: 'التفصيل موقعًا بموقع',
    rank: 'الترتيب',
    siteName: 'اسم الموقع',
    costSavings: 'توفير التكاليف',
    carbonSaved: 'الكربون المحفوظ',
    impactScore: 'نقاط التأثير',
    selectSiteAndDevices: 'اختر الموقع والأجهزة',
    chooseSiteAndSelectDevices: 'اختر موقعًا وحدد الأجهزة التي تريد مقارنتها',
    siteSelection: 'اختيار الموقع',
    siteBySiteRiskAnalysis: 'تحليل المخاطر موقعًا بموقع',
    totalDevices: 'إجمالي الأجهزة',
    avgDaysToMould: 'متوسط الأيام حتى العفن',
    avgTemp: 'متوسط الحرارة',
    avgHumidity: 'متوسط الرطوبة',
    riskPercent: 'نسبة المخاطر %',
    riskDistributionAcrossAllSites: 'توزيع المخاطر عبر جميع المواقع',
    riskComparisonBySite: 'مقارنة المخاطر حسب الموقع',
    daysToMouldByDevice: 'الأيام حتى العفن حسب الجهاز (أفضل 20)',
    atRisk: 'معرض للخطر',
    temperature: 'درجة الحرارة',
    humidity: 'الرطوبة',
    daysToMould: 'الأيام حتى العفن',
    riskLevel: 'مستوى المخاطر',
    lastReading: 'آخر قراءة',
    noReportsAvailable: 'لا توجد تقارير متاحة',
    noReportsEnabled: 'لم تقم بتمكين أي تقارير بعد. انتقل إلى الإعدادات لتمكين التقارير التي تريد رؤيتها.',
    selectSitesToCompare: 'اختر المواقع للمقارنة',
    site: 'الموقع',
    savingsCarbonImpactReport: 'تقرير التوفير وتأثير الكربون',
    analysisOfCostSavings: 'تحليل توفير التكاليف وكفاءة الطاقة والتأثير البيئي',
    totalCostSavings: 'إجمالي توفير التكاليف',
    environmentalImpact: 'التأثير البيئي',
    energyCarbonImpact: 'تأثير الطاقة والكربون',
    energyCarbonCorrelation: 'العلاقة بين الطاقة والكربون',
    energyCarbonData: 'بيانات الطاقة والكربون',
    energySavedCarbonReductionLocation: 'الطاقة المحفوظة وتقليل الكربون لهذا الموقع',
    relationshipEnergyCarbonReduction: 'العلاقة بين الطاقة المحفوظة وتقليل الكربون عبر المواقع',
    carbonKg: 'الكربون (كجم)',
    energyKwh: 'الطاقة (كيلوواط ساعة)',
    yourEnergyOptimizationEfforts: 'جهود تحسين الطاقة الخاصة بك وفرت',
    ofCO2: 'من ثاني أكسيد الكربون',
    equivalentToPlanting: 'ما يعادل زراعة',
    trees: 'شجرة',
    orRemoving: 'أو إزالة',
    carsFromRoadForYear: 'سيارة من الطريق لمدة عام',
    financialImpact: 'التأثير المالي',
    totalCostSavingsOf: 'إجمالي توفير التكاليف',
    demonstrateSignificantFinancialBenefits: 'يوضح الفوائد المالية الكبيرة للإدارة والتحسين الاستباقي للطاقة',
    energyEfficiency: 'كفاءة الطاقة',
    bySaving: 'من خلال توفير',
    youveReducedEnergyConsumption: 'لقد قللت استهلاك الطاقة بما يعادل تشغيل',
    averageHomesAnnually: 'منزل متوسط سنويًا',
    bespokeReportingServices: 'خدمات التقارير المخصصة',
    weCraftCustomDashboards: 'نحن نصنع لوحات تحليلية مخصصة مصممة خصيصًا لاحتياجات المراقبة والتقارير الخاصة بك',
    customDashboards: 'لوحات مخصصة',
    tailoredVisualizationInterfaces: 'واجهات تصور مخصصة مصممة حول متطلبات البيانات الفريدة الخاصة بك',
    advancedAnalytics: 'التحليلات المتقدمة',
    deepInsightsPredictive: 'رؤى عميقة مع نمذجة تنبؤية وقدرات تحليل الاتجاهات',
    realTimeInsights: 'رؤى في الوقت الفعلي',
    liveDataProcessing: 'معالجة البيانات المباشرة مع التنبيهات والمراقبة الفورية',
    systemIntegration: 'تكامل النظام',
    seamlessConnection: 'اتصال سلس مع البنية التحتية والأدوات الموجودة لديك',
    readyToTransformData: 'هل أنت مستعد لتحويل بياناتك إلى رؤى قابلة للتنفيذ؟',
    getStarted: 'ابدأ',
    contactDevelopmentTeam: 'اتصل بفريق التطوير لمناقشة متطلباتك',
    device: 'الجهاز',
    selectDeviceType: 'اختر نوع الجهاز',
    chooseDeviceTypeToAnalyse: 'اختر نوع الجهاز الذي تريد تحليله',
    deviceType: 'نوع الجهاز',
    devicesSelected: 'الأجهزة المحددة',
    searchSitesByNameOrType: 'البحث عن المواقع بالاسم أو النوع...',
    searchSites: 'البحث عن المواقع…',
    clearFilters: 'مسح عوامل التصفية',
    properties: 'الخصائص',
    devices: 'الأجهزة',
    enterDashboard: 'دخول لوحة التحكم',
    of: 'من',
    matching: 'مطابقة',
    noSitesFound: 'لم يتم العثور على مواقع',
    noSitesMatchCriteria: 'لا توجد مواقع تطابق معايير البحث والتصفية الحالية.',
    tryAdjustingSearchOrFilters: 'حاول تعديل مصطلحات البحث أو المرشحات.',
    noSitesAvailable: 'لا توجد مواقع متاحة',
    noSitesConfigured: 'لم يتم تكوين أي مواقع لمنظمتك بعد.',
    unknownDashboardType: 'نوع لوحة التحكم غير معروف',
    dashboardTypeNotSupported: 'نوع لوحة التحكم "{type}" غير مدعوم.',
    unknown: 'غير معروف',
    
    // Help Page Content
    'helpContent.login': `<p>للوصول إلى {companyName} Insights، ستحتاج إلى تسجيل الدخول باستخدام بيانات الاعتماد الخاصة بك.</p>

            <h4>خطوات تسجيل الدخول:</h4>
            <ol>
              <li>أدخل اسم المستخدم في حقل اسم المستخدم</li>
              <li>أدخل كلمة المرور في حقل كلمة المرور</li>
              <li>انقر على زر "تسجيل الدخول"</li>
            </ol>
            
            <div class="help-tip">
              <strong>نصيحة:</strong> إذا نسيت كلمة المرور، اتصل بمسؤول النظام للحصول على المساعدة.
            </div>`,
    'helpContent.navigation': `<p>التنقل الرئيسي موجود في الشريط الجانبي الأيسر. يمكنك الوصول إلى أقسام مختلفة من التطبيق:</p>

            <h4>الأقسام الرئيسية:</h4>
            <ul>
              <li><strong>نظرة عامة</strong> - لوحة تحكم مع مؤشرات الأداء الرئيسية والتنبيهات وخريطة الموقع</li>
              <li><strong>المواقع</strong> - عرض وإدارة جميع مواقعك</li>
              <li><strong>التنبيهات</strong> - مراقبة وإدارة تنبيهات النظام</li>
              <li><strong>التقارير</strong> - إنشاء وعرض التقارير</li>
              <li><strong>الإدارة</strong> - إدارة الأجهزة وإعدادات النظام (يتطلب وصول المسؤول)</li>
              <li><strong>المساعدة</strong> - وثائق المساعدة الشاملة ومساعد الدردشة</li>
            </ul>
            
            `,
    'helpContent.settings': `<p>يمكنك تخصيص تجربتك مع إعدادات مختلفة:</p>
            
            <h4>إعدادات المظهر:</h4>
            <ul>
              <li>التبديل بين الوضع الفاتح والداكن باستخدام محول المظهر في أعلى اليمين</li>
              <li>يتم حفظ تفضيلاتك تلقائيًا</li>
            </ul>
            
            <h4>إعدادات اللغة:</h4>
            <ul>
              <li>تغيير لغة الواجهة باستخدام محدد اللغة</li>
              <li>اللغات المتاحة معروضة في القائمة المنسدلة</li>
            </ul>
            
            `,
    'helpContent.kpiCards': `<p>تعرض لوحة التحكم نظرة عامة مؤشرات الأداء الرئيسية (KPIs) التي تمنحك رؤى سريعة حول أداء نظامك.</p>

            <h4>مؤشرات الأداء الرئيسية المتاحة:</h4>
            <ul>
              <li><strong>الكربون المحفوظ</strong> - إجمالي انبعاثات الكربون المحفوظة، مع مؤشرات الاتجاه</li>
              <li><strong>توفير التكاليف</strong> - التوفير المالي المحقق، مقارنة بالفترات السابقة</li>
              <li><strong>الاستخدام الإجمالي</strong> - مقاييس استهلاك الطاقة الإجمالية</li>
              <li><strong>جهد الشبكة مقابل الدائرة</strong> - مراقبة الجهد والحالة</li>
            </ul>
            
            <h4>تأثيرات التمرير:</h4>
            <p>مرر فوق أي بطاقة مؤشر أداء رئيسي لرؤية ملاحظات بصرية محسنة. ستبرز البطاقات بحدود ملونة وظلال.</p>
            
            `,
    'helpContent.recentAlerts': `<p>يعرض قسم التنبيهات الأخيرة أحدث التنبيهات من نظامك، مما يساعدك على البقاء على اطلاع بأي مشاكل أو أحداث مهمة.</p>

            <h4>معلومات التنبيه:</h4>
            <ul>
              <li><strong>مستويات الخطورة:</strong>
                <ul>
                  <li><span class="badge-critical">حرج</span> - يتطلب اهتمامًا فوريًا</li>
                  <li><span class="badge-warning">تحذير</span> - مهم ولكن ليس عاجلاً</li>
                  <li><span class="badge-info">معلومات</span> - رسائل إعلامية</li>
                </ul>
              </li>
              <li><strong>رسالة التنبيه</strong> - وصف لما أثار التنبيه</li>
              <li><strong>الطابع الزمني</strong> - وقت إنشاء التنبيه</li>
            </ul>
            
            <h4>فهم قائمة التنبيهات:</h4>
            <p>يعرض كل تنبيه مع مؤشر خطورة ملون على الجانب الأيسر، وشارة خطورة، ورسالة التنبيه، والطابع الزمني. يتطابق التصميم مع صفحة التنبيهات للاتساق.</p>
            
            <div class="help-tip">
              <strong>نصيحة:</strong> انقر على تنبيه في قسم التنبيهات الأخيرة لعرض المزيد من التفاصيل أو الانتقال إلى صفحة التنبيهات الكاملة.
            </div>`,
    'helpContent.siteMap': `<p>توفر خريطة الموقع تمثيلاً مرئيًا لجميع مواقعك والبوابات على خريطة تفاعلية.</p>

            <h4>ميزات الخريطة:</h4>
            <ul>
              <li><strong>علامات البوابة</strong> - العلامات الخضراء تشير إلى البوابات المتصلة، العلامات الحمراء تشير إلى البوابات غير المتصلة</li>
              <li><strong>مواقع لوحة التوزيع</strong> - أيقونات البرق تظهر مواقع مراقبة لوحة التوزيع</li>
              <li><strong>تمييز المعالم</strong> - المباني المهمة مميزة (Guildhall, Old Bailey, Barbican, Mansion House)</li>
              <li><strong>المباني ثلاثية الأبعاد</strong> - تبديل العرض ثلاثي الأبعاد لرؤية المباني بثلاثة أبعاد</li>
            </ul>
            
            <h4>عناصر التحكم في الخريطة:</h4>
            <ul>
              <li><strong>تبديل ثلاثي الأبعاد</strong> - التبديل بين عروض الخريطة ثنائية وثلاثية الأبعاد</li>
              <li><strong>محدد النمط</strong> - تغيير نمط الخريطة (الشوارع، الأقمار الصناعية، في الهواء الطلق، الملاحة، الحد الأدنى)</li>
              <li><strong>ملء الشاشة</strong> - توسيع الخريطة إلى ملء الشاشة لعرض أفضل</li>
            </ul>

            <h4>التفاعل مع العلامات:</h4>
            <ul>
              <li>انقر على أي علامة لرؤية نافذة منبثقة بمعلومات مفصلة</li>
              <li>علامات البوابة تظهر الحالة واسم الموقع والإحداثيات</li>
              <li>علامات لوحة التوزيع تظهر الموقع وحالة المراقبة</li>
            </ul>
            
            <div class="help-tip">
              <strong>نصيحة:</strong> استخدم زر ملء الشاشة لعرض أفضل عند تحليل مواقع متعددة على الخريطة.
            </div>`,
    'helpContent.sitesOverview': `<p>تسمح لك صفحة المواقع بعرض وإدارة جميع مواقعك. يمكنك البحث والتصفية والوصول إلى لوحات التحكم التفصيلية لكل موقع.</p>

            <h4>عرض المواقع:</h4>
            <ul>
              <li><strong>عرض القائمة</strong> - رؤية المواقع بتنسيق قائمة مع المعلومات الرئيسية</li>
              <li><strong>عرض الشبكة</strong> - عرض المواقع كبطاقات في تخطيط شبكي</li>
              <li><strong>البحث</strong> - استخدم شريط البحث للعثور على مواقع محددة بالاسم</li>
              <li><strong>التصفية</strong> - تصفية المواقع حسب نوع لوحة التحكم (غرفة النبات المؤقتة، شبكة التدفئة، لوحة التوزيع)</li>
            </ul>
            
            `,
    'helpContent.siteDashboards': `<p>كل موقع له لوحة تحكم مخصصة توفر معلومات تفصيلية وقدرات مراقبة. يعتمد نوع لوحة التحكم على تكوين الموقع.</p>
            
            <h4>أنواع لوحات التحكم:</h4>
            
            <h5>1. لوحة تحكم لوحة التوزيع</h5>
            <p>توفر لوحة تحكم لوحة التوزيع عرضًا شاملاً لإدارة لوحة التوزيع مع هيكل شجري هرمي.</p>

            <h6>اللوحة اليسرى - الشجرة الهرمية:</h6>
            <ul>
              <li>يعرض هيكل الموقع مع الوحدات والخصائص ولوحات التوزيع</li>
              <li>وظيفة البحث للعثور بسرعة على لوحات توزيع أو وحدات محددة</li>
              <li>توسيع/طي العقد للتنقل في التسلسل الهرمي</li>
              <li>مؤشرات الحالة تظهر حالة الاتصال/عدم الاتصال لكل جهاز</li>
              <li>مؤشرات التنبيه تظهر ما إذا كانت هناك أي تنبيهات نشطة</li>
            </ul>

            <h6>اللوحة اليمنى - مؤشرات الأداء الرئيسية والرسوم البيانية:</h6>
            <ul>
              <li><strong>بطاقات مؤشرات الأداء الرئيسية</strong> - الكربون المحفوظ وتوفير التكاليف والاستخدام الإجمالي والتنبيهات</li>
              <li><strong>رسم بياني لاتجاه استخدام الطاقة</strong> - رسم بياني تفاعلي يوضح استهلاك الطاقة خلال آخر 24 ساعة</li>
              <li>مرر فوق بطاقات مؤشرات الأداء الرئيسية لرؤية ملاحظات بصرية محسنة</li>
              <li>انقر على مؤشر أداء رئيسي للتنبيه لعرض التنبيهات التفصيلية للموقع</li>
            </ul>

            <h6>التفاعل مع لوحات التوزيع:</h6>
            <ul>
              <li>انقر على أي لوحة توزيع في الشجرة لفتح نافذة تفصيلية</li>
              <li>تعرض النافذة معلومات الجهاز الشاملة والقراءات والبيانات التاريخية</li>
              <li>عرض قراءات الجهد والتيار والطاقة مع الرسوم البيانية</li>
              <li>تصدير البيانات أو عرضها في وضع ملء الشاشة</li>
            </ul>

            <h5>2. لوحة تحكم غرفة النبات المؤقتة</h5>
            <p>تم تصميم لوحة تحكم غرفة النبات المؤقتة لمراقبة تركيبات غرف النبات المؤقتة.</p>

            <h5>3. لوحة تحكم شبكة التدفئة</h5>
            <p>توفر لوحة تحكم شبكة التدفئة مراقبة لأنظمة شبكة التدفئة.</p>

            <div class="help-tip">
              <strong>نصيحة:</strong> التخطيط ثابت مع لوحات متساوية الحجم، لذا كل من شجرة التسلسل الهرمي ومؤشرات الأداء الرئيسية/الرسوم البيانية مرئية دائمًا دون التمرير.
            </div>`,
    'helpContent.siteNotes': `<p>تسمح لك ملاحظات الموقع بإضافة التعليقات التوضيحية والعلامات والملاحظات على صور الموقع للتوثيق والتعاون.</p>

            <h4>الميزات:</h4>
            <ul>
              <li><strong>عرض الصور</strong> - عرض صور الموقع بدقة عالية</li>
              <li><strong>العلامات</strong> - إضافة علامات إلى مواقع محددة على الصورة</li>
              <li><strong>محرر النص المنسق</strong> - إضافة ملاحظات ووثائق منسقة</li>
              <li><strong>المفكرة</strong> - قسم الملاحظات السريعة للمعلومات الخاصة بالموقع</li>
              <li><strong>معلومات الجهاز</strong> - عرض تفاصيل الجهاز بالنقر على العلامات</li>
            </ul>
            
            <h4>إضافة العلامات:</h4>
            <ol>
              <li>انقر على زر "إضافة علامة" أو انقر مباشرة على الصورة</li>
              <li>ضع العلامة حيث الحاجة</li>
              <li>أضف وصفًا أو رابطًا إلى جهاز</li>
              <li>احفظ العلامة</li>
            </ol>

            <h4>استخدام المفكرة:</h4>
            <ul>
              <li>انقر على أيقونة المفكرة لفتح محرر الملاحظات</li>
              <li>أضف ملاحظات سريعة أو تذكيرات أو معلومات خاصة بالموقع</li>
              <li>يتم حفظ الملاحظات تلقائيًا</li>
              <li>الوصول إلى الملاحظات في أي وقت من صفحة ملاحظات الموقع</li>
            </ul>
            
            <div class="help-tip">
              <strong>نصيحة:</strong> يمكن ربط العلامات بالأجهزة للوصول السريع إلى معلومات الجهاز والقراءات.
            </div>`,
    'helpContent.alertsOverview': `<p>توفر صفحة التنبيهات إدارة شاملة للتنبيهات مع ثلاثة علامات تبويب رئيسية: التنبيهات النشطة والتكوينات ومراقبة عدم الاتصال.</p>

            <h4>إحصائيات التنبيه:</h4>
            <p>في أعلى علامة تبويب التنبيهات النشطة، سترى ملخصًا يوضح:</p>
            <ul>
              <li>عدد التنبيهات الحرجة</li>
              <li>عدد تنبيهات التحذير</li>
              <li>عدد تنبيهات المعلومات</li>
              <li>إجمالي عدد التنبيهات</li>
            </ul>`,
    'helpContent.viewingAlerts': `<p>تعرض قائمة التنبيهات جميع التنبيهات مع معلومات تفصيلية وخيارات الإجراءات.</p>

            <h4>معلومات التنبيه المعروضة:</h4>
            <ul>
              <li><strong>مؤشر الخطورة</strong> - شريط ملون على اليسار (أحمر للحرج، أصفر للتحذير، أزرق للمعلومات)</li>
              <li><strong>شارة الخطورة</strong> - شارة بصرية تظهر مستوى التنبيه</li>
              <li><strong>رسالة التنبيه</strong> - وصف لما أثار التنبيه</li>
              <li><strong>معرف الجهاز</strong> - الجهاز الذي أنشأ التنبيه</li>
              <li><strong>الطابع الزمني</strong> - وقت رفع التنبيه</li>
              <li><strong>الحالة</strong> - الحالة الحالية (نشط، معترف به، محلول)</li>
            </ul>
            
            <h4>البحث والتصفية:</h4>
            <ul>
              <li><strong>شريط البحث</strong> - البحث عن التنبيهات حسب الرسالة أو معرف الجهاز</li>
              <li><strong>مرشح الخطورة</strong> - التصفية حسب مستوى الخطورة (الكل، حرج، تحذير، معلومات)</li>
              <li><strong>مرشح الحالة</strong> - التصفية حسب الحالة (الكل، نشط، معترف به، محلول)</li>
            </ul>
            
            `,
    'helpContent.alertActions': `<p>يمكنك تنفيذ إجراءات مختلفة على التنبيهات اعتمادًا على حالتها.</p>

            <h4>الإجراءات المتاحة:</h4>
            
            <h5>الاعتراف بالتنبيه:</h5>
            <ul>
              <li>متاح للتنبيهات النشطة</li>
              <li>يضع علامة على التنبيه كمعترف به</li>
              <li>يوضح أنك على علم بالمشكلة</li>
              <li>لا يحل التنبيه</li>
            </ul>
            
            <h5>حل التنبيه:</h5>
            <ul>
              <li>متاح للتنبيهات النشطة أو المعترف بها</li>
              <li>يضع علامة على التنبيه كمحلول</li>
              <li>يشير إلى أن المشكلة تم معالجتها</li>
              <li>يمكن عرض التنبيهات المحلولة ولكن يتم تصفيتها افتراضيًا</li>
            </ul>
            
            <h5>عرض التفاصيل:</h5>
            <ul>
              <li>متاح للتنبيهات المحلولة</li>
              <li>عرض معلومات تفصيلية حول التنبيه</li>
              <li>الوصول إلى البيانات التاريخية المتعلقة بالتنبيه</li>
            </ul>

            <div class="help-tip">
              <strong>نصيحة:</strong> استخدم إجراء الاعتراف لتتبع التنبيهات التي راجعتها، حتى لو لم تحلها بعد.
            </div>`,
    'helpContent.alertConfigurations': `<p>تسمح لك تكوينات التنبيه بإنشاء وإدارة قواعد تنبيه مخصصة تراقب نظامك للحالات المحددة.</p>

            <h4>إدارة التكوين:</h4>
            <p>يتم تنظيم التكوينات في لوحة كانبان مع ثلاثة أعمدة:</p>
            <ul>
              <li><strong>نشط</strong> - التكوينات التي تراقب حاليًا</li>
              <li><strong>متوقف</strong> - التكوينات المعطلة مؤقتًا</li>
              <li><strong>مسودات</strong> - قوالب التكوين (ميزة مستقبلية)</li>
            </ul>
            
            <h4>إنشاء تكوين جديد:</h4>
            <ol>
              <li>انقر على زر "إنشاء قاعدة تنبيه"</li>
              <li>اختر قالبًا من القائمة المنسدلة</li>
              <li>اختر النطاق (مستوى الموقع أو الوحدة أو الجهاز)</li>
              <li>اختر الموقع/الوحدة/الجهاز المحدد إن أمكن</li>
              <li>قم بتعيين قيم العتبة (الحد الأدنى والحد الأقصى)</li>
              <li>اختر مستوى التنبيه (معلومات، تحذير، أو حرج)</li>
              <li>قم بتمكين أو تعطيل التكوين</li>
              <li>انقر على "إنشاء" للحفظ</li>
            </ol>

            <h4>إدارة التكوينات:</h4>
            <ul>
              <li><strong>السحب والإفلات</strong> - نقل التكوينات بين أعمدة نشط ومتوقف</li>
              <li><strong>تحرير</strong> - انقر على زر التحرير لتعديل تكوين موجود</li>
              <li><strong>حذف</strong> - إزالة التكوين نهائيًا</li>
            </ul>

            <div class="help-tip">
              <strong>نصيحة:</strong> استخدم التكوينات المتوقفة لتعطيل المراقبة مؤقتًا دون حذف القاعدة بالكامل.
            </div>`,
    'helpContent.offlineMonitoring': `<p>تسمح لك علامة تبويب مراقبة عدم الاتصال بإعداد تنبيهات عندما تصبح الأجهزة غير متصلة.</p>

            <h4>إنشاء مراقب عدم اتصال:</h4>
            <ol>
              <li>انقر على زر "مراقب جديد"</li>
              <li>اختر الجهاز للمراقبة</li>
              <li>قم بتعيين عتبة عدم الاتصال (بالدقائق) - المدة التي يجب أن يكون فيها الجهاز غير متصل قبل إطلاق التنبيه</li>
              <li>اختر مستوى التنبيه (تحذير أو حرج)</li>
              <li>أضف وصفًا اختياريًا</li>
              <li>قم بتمكين أو تعطيل المراقب</li>
              <li>انقر على "إنشاء" للحفظ</li>
            </ol>

            <h4>معلومات المراقب:</h4>
            <p>يعرض كل مراقب:</p>
            <ul>
              <li>اسم الجهاز</li>
              <li>الوصف (إن وجد)</li>
              <li>طابع زمني لآخر رؤية</li>
              <li>عتبة عدم الاتصال</li>
              <li>مستوى التنبيه</li>
              <li>حالة التمكين/التعطيل</li>
            </ul>`,
    'helpContent.reportsOverview': `<p>توفر صفحة التقارير قدرات تحليلية وإعداد تقارير شاملة لبيانات نظامك.</p>

            <h4>التقارير المتاحة:</h4>
            <ul>
              <li><strong>تقرير المواقع</strong> - نظرة عامة على جميع المواقع مع المقاييس الرئيسية</li>
              <li><strong>استهلاك الطاقة</strong> - تحليل تفصيلي لاستخدام الطاقة</li>
              <li><strong>اتجاه التنبيهات</strong> - بيانات التنبيهات التاريخية والاتجاهات</li>
              <li><strong>اتجاهات درجة الحرارة</strong> - مراقبة درجة الحرارة بمرور الوقت</li>
              <li><strong>تكرار التنبيهات</strong> - تحليل أنماط التنبيهات</li>
            </ul>`,
    'helpContent.timeRanges': `<p>تسمح لك معظم التقارير بتصفية البيانات حسب النطاق الزمني لتحليل فترات محددة.</p>

            <h4>النطاقات الزمنية المتاحة:</h4>
            <ul>
              <li><strong>آخر 24 ساعة</strong> - البيانات من اليوم الماضي</li>
              <li><strong>آخر 7 أيام</strong> - عرض أسبوعي</li>
              <li><strong>آخر 30 يومًا</strong> - عرض شهري</li>
              <li><strong>آخر 90 يومًا</strong> - عرض ربع سنوي</li>
              <li><strong>نطاق مخصص</strong> - اختر تواريخ البدء والانتهاء المحددة</li>
            </ul>

            <div class="help-tip">
              <strong>نصيحة:</strong> استخدم نطاقات التاريخ المخصصة لتحليل أحداث أو فترات محددة.
            </div>`,
    'helpContent.chartsGraphs': `<p>تتضمن التقارير أنواعًا مختلفة من الرسوم البيانية والمخططات لتصور بياناتك بفعالية.</p>

            <h4>أنواع الرسوم البيانية:</h4>
            <ul>
              <li><strong>رسوم بيانية خطية</strong> - تظهر الاتجاهات بمرور الوقت</li>
              <li><strong>رسوم بيانية شريطية</strong> - مقارنة القيم عبر الفئات</li>
              <li><strong>رسوم بيانية منطقة</strong> - تظهر القيم التراكمية مع التدرجات</li>
              <li><strong>رسوم بيانية دائرية</strong> - عرض النسب والنسب المئوية</li>
            </ul>
            
            <h4>التفاعل مع الرسوم البيانية:</h4>
            <ul>
              <li>مرر فوق نقاط البيانات لرؤية القيم الدقيقة</li>
              <li>انقر على عناصر وسيلة الإيضاح لإظهار/إخفاء سلسلة البيانات</li>
              <li>استخدم عناصر التحكم في التكبير إن كانت متاحة</li>
              <li>تصدير الرسوم البيانية كصور أو PDF</li>
            </ul>
            
            `,
    'helpContent.exportingReports': `<p>يمكنك تصدير التقارير بتنسيقات مختلفة للمشاركة أو التحليل أو التوثيق.</p>

            <h4>تنسيقات التصدير:</h4>
            <ul>
              <li><strong>CSV</strong> - تصدير البيانات كقيم مفصولة بفواصل لتحليل جداول البيانات</li>
              <li><strong>PDF</strong> - تصدير الرسوم البيانية والتقارير كوثائق PDF</li>
              <li><strong>جدول الدوري</strong> - تصدير جداول المقارنة</li>
            </ul>
            
            <h4>ما يتم تصديره:</h4>
            <ul>
              <li>البيانات الحالية بناءً على المرشحات المحددة</li>
              <li>الرسوم البيانية والتصورات</li>
              <li>الإحصائيات الملخصة</li>
              <li>الطوابع الزمنية والبيانات الوصفية</li>
            </ul>

            <div class="help-tip">
              <strong>نصيحة:</strong> قم بتصدير التقارير بانتظام لأغراض حفظ السجلات والامتثال.
            </div>`,
    'helpContent.customReports': `<p>للاحتياجات المتقدمة لإعداد التقارير، يمكن إنشاء لوحات تحكم تحليلية مخصصة مصممة خصيصًا لمتطلباتك.</p>

            <h4>ميزات لوحة التحكم المخصصة:</h4>
            <ul>
              <li>واجهات تصور مخصصة</li>
              <li>تحليلات متقدمة مع النمذجة التنبؤية</li>
              <li>رؤى فورية مع تنبيهات فورية</li>
              <li>مقاييس ومؤشرات أداء رئيسية مخصصة</li>
            </ul>
            
            <p>اتصل بمسؤول النظام أو فريق الدعم لمناقشة متطلبات التقارير المخصصة.</p>`,
    'helpContent.adminOverview': `<p>توفر صفحة الإدارة أدوات إدارية لإدارة الأجهزة وتكوين إعدادات النظام. تتطلب هذه الصفحة وصول المسؤول.</p>

            <h4>ميزات الإدارة:</h4>
            <ul>
              <li><strong>إدارة الأجهزة</strong> - إنشاء وتحرير وحذف الأجهزة</li>
              <li><strong>البحث والتصفية في الأجهزة</strong> - العثور على الأجهزة حسب المعرف أو العنوان أو نوع الجهاز</li>
              <li><strong>تكوين الصمامات</strong> - تكوين الصمامات لأجهزة FUSEBOARD_UDC</li>
              <li><strong>إعدادات المؤسسة</strong> - تكوين معاملات حساب مؤشرات الأداء الرئيسية للمؤسسة بأكملها</li>
              <li><strong>إعدادات الموقع</strong> - تجاوز إعدادات المؤسسة لمواقع محددة</li>
            </ul>
            
            <div class="help-tip">
              <strong>ملاحظة:</strong> يلزم وصول المسؤول لعرض واستخدام هذه الصفحة. اتصل بمسؤول النظام إذا كنت بحاجة إلى صلاحيات المسؤول.
            </div>`,
    'helpContent.deviceManagement': `<p>إدارة جميع الأجهزة في نظامك من صفحة الإدارة.</p>

            <h4>عرض الأجهزة:</h4>
            <ul>
              <li><strong>جدول الأجهزة</strong> - رؤية جميع الأجهزة مع المعرف والعنوان ونوع الجهاز والموقع</li>
              <li><strong>البحث</strong> - استخدم شريط البحث للعثور على الأجهزة حسب المعرف أو العنوان</li>
              <li><strong>التصفية حسب النوع</strong> - تصفية الأجهزة حسب نوع الجهاز باستخدام القائمة المنسدلة</li>
            </ul>
            
            <h4>إنشاء جهاز:</h4>
            <ol>
              <li>انقر على زر "إضافة جهاز"</li>
              <li>أدخل معرف الجهاز (مطلوب، لا يمكن تغييره بعد الإنشاء)</li>
              <li>أدخل عنوانًا اختياريًا</li>
              <li>اختر نوع الجهاز من القائمة المنسدلة</li>
              <li>اختر الموقع الذي يوجد فيه الجهاز</li>
              <li>اختر الوحدات المرتبطة بالجهاز اختياريًا</li>
              <li>لأجهزة FUSEBOARD_UDC و CS01_NB، قم بتكوين صمامين على الأقل (انظر تكوين الصمامات)</li>
              <li>لأجهزة EWATTCH_SQUID، قم بتكوين تعيينات المشابك (انظر تكوين مشابك Squid)</li>
              <li>انقر على "إنشاء جهاز" للحفظ</li>
            </ol>

            <h4>تحرير جهاز:</h4>
            <ul>
              <li>انقر على أيقونة التحرير بجانب أي جهاز في الجدول</li>
              <li>قم بتعديل العنوان أو نوع الجهاز أو الموقع أو الوحدات</li>
              <li>ملاحظة: لا يمكن تغيير المعرف بعد الإنشاء</li>
              <li>لأجهزة FUSEBOARD_UDC و CS01_NB، قم بتحديث تكوينات الصمامات حسب الحاجة</li>
              <li>لأجهزة EWATTCH_SQUID، قم بتحديث تعيينات المشابك حسب الحاجة</li>
              <li>انقر على "تحديث الجهاز" لحفظ التغييرات</li>
            </ul>
            
            <h4>حذف جهاز:</h4>
            <ul>
              <li>انقر على أيقونة الحذف بجانب الجهاز</li>
              <li>أكد الحذف في مربع الحوار</li>
              <li><strong>تحذير:</strong> لا يمكن التراجع عن هذا الإجراء</li>
            </ul>
            
            <h4>أنواع الأجهزة:</h4>
            <p>يدعم النظام عدة أنواع من الأجهزة، ولكل منها متطلبات تكوين محددة:</p>
            
            <h5>FUSEBOARD_UDC (لوحة التوزيع UDC)</h5>
            <ul>
              <li><strong>الغرض:</strong> مراقبة دوائر لوحة التوزيع مع ما يصل إلى 4 مستشعرات تيار</li>
              <li><strong>التكوين:</strong> يتطلب تكوين الصمامات لصمامين على الأقل (انظر قسم تكوين الصمامات)</li>
              <li><strong>البيانات:</strong> يقيس التيار (A) لكل صمام، يحسب الطاقة (W) باستخدام الجهد وعامل القدرة المكونين</li>
              <li><strong>حالة الاستخدام:</strong> مراقبة لوحة التوزيع الرئيسية، تتبع الطاقة على مستوى الدائرة</li>
            </ul>
            
            <h5>CS01_NB (مستشعر التيار CS-01 NB-IoT)</h5>
            <ul>
              <li><strong>الغرض:</strong> مستشعر تيار NB-IoT يدعم ما يصل إلى 4 قنوات قياس تيار</li>
              <li><strong>التكوين:</strong> يتطلب تكوين الصمامات لقناتين على الأقل (انظر قسم تكوين الصمامات)</li>
              <li><strong>البيانات:</strong> يقيس التيار (A) لكل قناة، يحسب الطاقة (W) باستخدام الجهد وعامل القدرة المكونين</li>
              <li><strong>حالة الاستخدام:</strong> مراقبة التيار عن بُعد، تتبع الطاقة على مستوى الدائرة في المواقع صعبة الوصول</li>
              <li><strong>الميزات:</strong> يعمل بالبطارية، اتصال NB-IoT، استهلاك طاقة منخفض</li>
            </ul>
            
            <h5>EWATTCH_SQUID (جهاز مشبك Squid)</h5>
            <ul>
              <li><strong>الغرض:</strong> جهاز مراقبة تيار متعدد المشابك يدعم ما يصل إلى 12 مشبك</li>
              <li><strong>التكوين:</strong> يتطلب تعيين المشابك للوحدات (انظر تكوين مشابك Squid)</li>
              <li><strong>البيانات:</strong> يقيس التيار (A)، الطاقة (W)، الطاقة (kWh)، الجهد (V)، ومعاملات كهربائية أخرى</li>
              <li><strong>حالة الاستخدام:</strong> مراقبة كهربائية شاملة، تتبع دوائر متعددة، إدارة الطاقة</li>
              <li><strong>الميزات:</strong> يدعم أنواع قياس مختلفة بما في ذلك الطاقة النشطة، الطاقة التفاعلية، القوة الظاهرة، التردد</li>
            </ul>
            
            <h5>ELSYS_ELT (مستشعر بيئي Elsys)</h5>
            <ul>
              <li><strong>الغرض:</strong> مستشعر مراقبة بيئية</li>
              <li><strong>التكوين:</strong> إعداد جهاز أساسي (المعرف، العنوان، الموقع)</li>
              <li><strong>البيانات:</strong> درجة الحرارة، الرطوبة، ومعاملات بيئية أخرى</li>
              <li><strong>حالة الاستخدام:</strong> مراقبة بيئة المبنى، مراقبة نظام HVAC</li>
            </ul>
            
            <h5>ELVACO_4130 (عداد الحرارة Elvaco)</h5>
            <ul>
              <li><strong>الغرض:</strong> عداد حرارة لأنظمة التدفئة المركزية</li>
              <li><strong>التكوين:</strong> إعداد جهاز أساسي (المعرف، العنوان، الموقع)</li>
              <li><strong>البيانات:</strong> استهلاك الطاقة (kWh)، معدل التدفق (m³/h)، درجات حرارة الإرسال/العودة (°C)، الطاقة (W)</li>
              <li><strong>حالة الاستخدام:</strong> مراقبة التدفئة المركزية، إدارة شبكة الحرارة، فوترة الطاقة</li>
              <li><strong>الميزات:</strong> يدعم بروتوكول M-Bus، فترات تعريفة متعددة، اكتشاف أعلام الخطأ</li>
            </ul>
            
            <div class="help-tip">
              <strong>نصيحة:</strong> استخدم ميزات البحث والتصفية للعثور بسرعة على أجهزة محددة عند إدارة أعداد كبيرة من الأجهزة. لكل نوع جهاز متطلبات تكوين محددة - تأكد من إكمالها لمراقبة البيانات الدقيقة.
            </div>`,
    'helpContent.fuseConfiguration': `<p>تتطلب أجهزة FUSEBOARD_UDC و CS01_NB تكوين الصمامات لمراقبة وحساب استخدام الطاقة بشكل صحيح.</p>

            <h4>متطلبات تكوين الصمامات/القنوات:</h4>
            <ul>
              <li><strong>حد أدنى صمامين/قناتين</strong> - يجب تكوين الصمامات/القنوات 1 و 2 على الأقل</li>
              <li><strong>الصمامات/القنوات 3 و 4</strong> - اختياري، يمكن تركها فارغة</li>
              <li><strong>الحقول المطلوبة</strong> - اسم الجهاز والفئة والجهد وعامل القدرة</li>
              <li><strong>ينطبق على:</strong> أجهزة FUSEBOARD_UDC (صمامات) و CS01_NB (قنوات)</li>
            </ul>
            
            <h4>تكوين صمام/قناة:</h4>
            <ol>
              <li>أدخل <strong>اسم الجهاز</strong> (مثل "الإمداد الرئيسي"، "الدائرة أ"، "أجهزة المطبخ")</li>
              <li>اختر <strong>الفئة</strong>:
                <ul>
                  <li><strong>إمداد</strong> - نقطة دخول إمداد الطاقة الرئيسي</li>
                  <li><strong>دائرة</strong> - دائرة أو فرع فردي</li>
                  <li><strong>حمل</strong> - حمل أو جهاز محدد</li>
                  <li><strong>أخرى</strong> - فئة أخرى غير المذكورة أعلاه</li>
                </ul>
              </li>
              <li>أدخل <strong>الجهد (V)</strong> - عادة 240V للأنظمة البريطانية، 230V للأنظمة الأوروبية</li>
              <li>أدخل <strong>عامل القدرة</strong> - قيمة بين 0 و 1 (عادة 0.9 لمعظم الأحمال، 1.0 للأحمال المقاومة)</li>
            </ol>

            <h4>ملاحظات خاصة بالجهاز:</h4>
            <ul>
              <li><strong>FUSEBOARD_UDC:</strong> يمثل كل صمام موضع صمام فعلي على لوحة التوزيع. قم بتكوين الصمامات 1-4 بناءً على التثبيت الخاص بك.</li>
              <li><strong>CS01_NB:</strong> تمثل كل قناة إدخال مستشعر تيار. قم بتكوين القنوات 1-4 بناءً على المستشعرات المتصلة.</li>
            </ul>
            
            <h4>ملاحظات مهمة:</h4>
            <ul>
              <li>يتم قراءة قيم تكوين الصمام/القناة أثناء التثبيت وتبقى ثابتة</li>
              <li>تُستخدم هذه القيم لحسابات الطاقة الدقيقة: الطاقة (W) = الجهد (V) × التيار (A) × عامل القدرة</li>
              <li>يضمن التكوين الدقيق مقاييس مؤشرات الأداء الرئيسية الصحيحة، وحسابات توفير التكاليف، وتتبع البصمة الكربونية</li>
              <li>يمكنك تحديث تكوينات الصمام/القناة بعد إنشاء الجهاز</li>
              <li>يمكن حذف الصمامات/القنوات الاختيارية (3 و 4) بمسح جميع الحقول</li>
            </ul>
            
            <div class="help-tip">
              <strong>نصيحة:</strong> تأكد من دقة قيم الجهد وعامل القدرة لحسابات استخدام الطاقة الصحيحة ومقاييس توفير التكاليف. للتثبيتات البريطانية، الجهد القياسي هو 240V وعامل القدرة النموذجي هو 0.9.
            </div>`,
    'helpContent.organizationSettings': `<p>تتحكم إعدادات المؤسسة في حسابات مؤشرات الأداء الرئيسية لجميع المواقع ما لم يتم تجاوزها على مستوى الموقع.</p>

            <h4>تكوين مؤشرات الأداء الرئيسية للمؤسسة:</h4>
            
            <h5>معدل الوحدة (£ لكل كيلوواط ساعة):</h5>
            <ul>
              <li>التكلفة لكل كيلوواط ساعة من الكهرباء</li>
              <li>يُستخدم لحساب توفير التكاليف</li>
              <li>مثال: 0.30 يعني £0.30 لكل كيلوواط ساعة</li>
              <li>حقل مطلوب</li>
            </ul>
            
            <h5>دقائق الفترة:</h5>
            <ul>
              <li>عدد مرات وصول قراءات الجهاز (بالدقائق)</li>
              <li>يُستخدم للحسابات الدقيقة القائمة على الوقت</li>
              <li>مثال: 5 يعني وصول القراءات كل 5 دقائق</li>
              <li>حقل مطلوب، يجب أن يكون على الأقل 1</li>
            </ul>
            
            <h5>عامل الكربون (كجم CO₂e لكل كيلوواط ساعة):</h5>
            <ul>
              <li>عامل انبعاث الكربون لحساب توفير الكربون</li>
              <li>يُستخدم لحساب مقاييس الكربون المحفوظ</li>
              <li>مثال: 0.233 يعني 0.233 كجم CO₂e لكل كيلوواط ساعة</li>
              <li>حقل مطلوب</li>
            </ul>
            
            <h4>حفظ إعدادات المؤسسة:</h4>
            <ol>
              <li>انقر على زر "الإعدادات" في رأس صفحة الإدارة</li>
              <li>انتقل إلى قسم "تكوين مؤشرات الأداء الرئيسية للمؤسسة"</li>
              <li>أدخل أو حدّث القيم لمعدل الوحدة ودقائق الفترة وعامل الكربون</li>
              <li>انقر على "حفظ إعدادات المؤسسة"</li>
              <li>تنطبق الإعدادات على جميع المواقع ما لم يتم تجاوزها</li>
            </ol>

            <div class="help-tip">
              <strong>نصيحة:</strong> حدّث هذه الإعدادات عندما تتغير أسعار الكهرباء أو عوامل الكربون لضمان حسابات مؤشرات الأداء الرئيسية الدقيقة.
            </div>`,
    'helpContent.siteSettings': `<p>تسمح لك إعدادات الموقع بتجاوز إعدادات المؤسسة لمواقع محددة.</p>

            <h4>تكوين مؤشرات الأداء الرئيسية للموقع:</h4>
            <p>يمكنك تجاوز إعدادات المؤسسة للمواقع الفردية:</p>
            <ul>
              <li><strong>تجاوز معدل الوحدة</strong> - معدل الكهرباء الخاص بالموقع</li>
              <li><strong>تجاوز دقائق الفترة</strong> - فترة القراءة الخاصة بالموقع</li>
              <li><strong>تجاوز عامل الكربون</strong> - عامل انبعاث الكربون الخاص بالموقع</li>
            </ul>
            
            <h4>تكوين إعدادات الموقع:</h4>
            <ol>
              <li>انقر على زر "الإعدادات" في رأس صفحة الإدارة</li>
              <li>انتقل إلى قسم "تكوين مؤشرات الأداء الرئيسية للموقع"</li>
              <li>اختر موقعًا من القائمة المنسدلة</li>
              <li>أدخل قيم التجاوز (اتركها فارغة لاستخدام إعدادات المؤسسة الافتراضية)</li>
              <li>عرض القيم الفعالة الحالية في النص الصغير أسفل كل حقل</li>
              <li>انقر على "حفظ إعدادات الموقع"</li>
            </ol>
            
            <h4>فهم التجاوزات:</h4>
            <ul>
              <li><strong>تجاوز فارغ</strong> - يستخدم الموقع قيمة المؤسسة الافتراضية</li>
              <li><strong>تجاوز محدد</strong> - يستخدم الموقع قيمة التجاوز بدلاً من افتراضي المؤسسة</li>
              <li><strong>عرض القيمة الحالية</strong> - يوضح القيمة الفعالة المستخدمة (تجاوز أو افتراضي)</li>
            </ul>

            <div class="help-tip">
              <strong>نصيحة:</strong> استخدم تجاوزات الموقع عندما يكون للموقع أسعار كهرباء مختلفة أو يتطلب معاملات حساب مختلفة عن افتراضي المؤسسة.
            </div>`,
    'helpContent.chatbotOverview': `<p>مساعد الدردشة هو أداة مساعدة مدعومة بالذكاء الاصطناعي متاحة على صفحة المساعدة. يمكنه الإجابة على الأسئلة حول التطبيق وإرشادك إلى الوثائق ذات الصلة.</p>

            <h4>الوصول إلى مساعد الدردشة:</h4>
            <ul>
              <li>ابحث عن زر الدردشة في الزاوية اليمنى السفلى من صفحة المساعدة</li>
              <li>انقر على أيقونة الدردشة لفتح نافذة الدردشة</li>
              <li>يرحب المساعد بك برسالة ترحيب</li>
            </ul>
            
            <h4>طرح الأسئلة:</h4>
            <ul>
              <li>اكتب سؤالك في حقل الإدخال في الأسفل</li>
              <li>اضغط Enter أو انقر على زر الإرسال</li>
              <li>سيقدم المساعد إجابة بناءً على سؤالك</li>
              <li>يمكنك طرح أسئلة متابعة في نفس المحادثة</li>
            </ul>
            
            <h4>ما يمكنك أن تسأل عنه:</h4>
            <ul>
              <li><strong>التنقل</strong> - "كيف أنتقل إلى التنبيهات؟"</li>
              <li><strong>الميزات</strong> - "ما هي مؤشرات الأداء الرئيسية؟" أو "كيف أصدّر التقارير؟"</li>
              <li><strong>التنبيهات</strong> - "كيف أعترف بتنبيه؟"</li>
              <li><strong>الإعدادات</strong> - "كيف أغير المظهر؟"</li>
              <li><strong>المساعدة العامة</strong> - "كيف أصفّي المواقع؟"</li>
            </ul>

            <h4>ميزات الدردشة:</h4>
            <ul>
              <li><strong>سجل الرسائل</strong> - رؤية سجل محادثتك في نافذة الدردشة</li>
              <li><strong>الطوابع الزمنية</strong> - تظهر كل رسالة وقت إرسالها</li>
              <li><strong>مؤشر الكتابة</strong> - رؤية عندما يكتب المساعد ردًا</li>
              <li><strong>إغلاق/إعادة فتح</strong> - أغلق الدردشة وأعد فتحها في أي وقت</li>
            </ul>
            
            <div class="help-tip">
              <strong>نصيحة:</strong> يمكن لمساعد الدردشة مساعدتك في العثور على المعلومات بسرعة دون تصفح جميع أقسام المساعدة. جرب طرح أسئلة محددة للحصول على أفضل النتائج.
            </div>`,
    'helpContent.bestPractices': `<h4>استخدام لوحة التحكم:</h4>
            <ul>
              <li>تحقق من لوحة التحكم نظرة عامة بانتظام للحصول على رؤى سريعة</li>
              <li>قم بإعداد تكوينات التنبيهات بشكل استباقي للقبض على المشاكل مبكرًا</li>
              <li>استخدم خريطة الموقع للحصول على نظرة عامة مرئية لجميع المواقع</li>
              <li>راجع التقارير بشكل دوري لتحديد الاتجاهات</li>
            </ul>
            
            <h4>إدارة التنبيهات:</h4>
            <ul>
              <li>اعترف بالتنبيهات على الفور لتتبع حالة مراجعتك</li>
              <li>احل التنبيهات بعد معالجة المشكلة الأساسية</li>
              <li>استخدم مرشحات الخطورة للتركيز على المشاكل الحرجة أولاً</li>
              <li>قم بإعداد مراقبة عدم الاتصال للأجهزة الحرجة</li>
            </ul>
            
            <h4>تحليل البيانات:</h4>
            <ul>
              <li>قارن البيانات عبر نطاقات زمنية مختلفة لتحديد الأنماط</li>
              <li>صدّر التقارير للتحليل غير المتصل وحفظ السجلات</li>
              <li>استخدم ملاحظات الموقع لتوثيق النتائج المهمة</li>
              <li>راجع الاتجاهات التاريخية للتنبؤ بالاحتياجات المستقبلية</li>
            </ul>`,
    'helpContent.commonIssues': `<h4>عدم تحميل الخريطة:</h4>
            <ul>
              <li>تحقق من اتصالك بالإنترنت</li>
              <li>حاول تحديث الصفحة</li>
              <li>غيّر أنماط الخريطة - أحيانًا نمط مختلف يحمل أسرع</li>
              <li>امسح ذاكرة التخزين المؤقت للمتصفح إذا استمرت المشاكل</li>
            </ul>
            
            <h4>عدم عرض الرسوم البيانية:</h4>
            <ul>
              <li>تأكد من وجود بيانات للنطاق الزمني المحدد</li>
              <li>حاول اختيار نطاق زمني مختلف</li>
              <li>تحقق مما إذا كانت المرشحات مقيدة جدًا</li>
              <li>حدّث الصفحة لإعادة تحميل البيانات</li>
            </ul>
            
            <h4>عدم ظهور التنبيهات:</h4>
            <ul>
              <li>تحقق من إعدادات المرشح - قد تكون لديك مرشحات مطبقة</li>
              <li>تحقق من أن تكوينات التنبيهات مفعلة</li>
              <li>تحقق مما إذا كنت تبحث عن الحالة الصحيحة (نشط مقابل محلول)</li>
              <li>تأكد من أن الأجهزة متصلة وتقوم بالإبلاغ عن البيانات</li>
            </ul>
            
            <h4>مشاكل الأداء:</h4>
            <ul>
              <li>أغلق علامات التبويب غير الضرورية للمتصفح</li>
              <li>جرب متصفحًا مختلفًا إذا كان الحالي بطيئًا</li>
              <li>قلل النطاق الزمني للتقارير مع مجموعات البيانات الكبيرة</li>
              <li>اتصل بالدعم إذا استمرت المشاكل</li>
            </ul>`,
    'helpContent.keyboardShortcuts': `<p>بينما يتم تشغيل التطبيق بشكل أساسي بالماوس، إليك بعض اختصارات لوحة المفاتيح المفيدة:</p>
            
            <ul>
              <li><strong>Ctrl/Cmd + F</strong> - التركيز على حقول البحث (عند التوفر)</li>
              <li><strong>Esc</strong> - إغلاق النوافذ المنبثقة والنوافذ</li>
              <li><strong>Tab</strong> - التنقل بين حقول النموذج</li>
              <li><strong>Enter</strong> - إرسال النماذج أو تفعيل الأزرار</li>
            </ul>
            
            <div class="help-tip">
              <strong>نصيحة:</strong> يمكن تنفيذ معظم الإجراءات بنقرات الماوس. يتم توفير اختصارات لوحة المفاتيح كراحة.
            </div>`,
    'helpContent.gettingHelp': `<h4>الموارد داخل التطبيق:</h4>
            <ul>
              <li>صفحة المساعدة هذه - دليل شامل لجميع الميزات</li>
              <li>تلميحات الأدوات - مرر فوق الأزرار والأيقونات للحصول على معلومات سريعة</li>
              <li>المساعدة السياقية - بعض الصفحات تحتوي على نص مساعدة مضمن</li>
            </ul>
            
            <h4>قنوات الدعم:</h4>
            <ul>
              <li>اتصل بمسؤول النظام لمشاكل الحساب والوصول</li>
              <li>تواصل مع الدعم الفني للأخطاء أو المشاكل التقنية</li>
              <li>تحقق من إشعارات النظام للتحديثات والإعلانات المهمة</li>
            </ul>
            
            <div class="help-tip">
              <strong>نصيحة:</strong> عند الإبلاغ عن المشاكل، قم بتضمين لقطات الشاشة والتفاصيل حول ما كنت تحاول فعله عندما حدثت المشكلة.
            </div>`,
    'helpContent.aiHubOverview': `<p>مركز الذكاء الاصطناعي هو موقعك المركزي لجميع الميزات والرؤى المدعومة بالذكاء الاصطناعي. يوفر مجموعة شاملة من الأدوات لتحليل بيانات المبنى الخاص بك، وإنشاء الرؤى، وتحسين العمليات.</p>

            <h4>الميزات الرئيسية:</h4>
            <ul>
              <li><strong>معالج رؤى الذكاء الاصطناعي</strong> - دليل خطوة بخطوة لإنشاء رؤى مخصصة للذكاء الاصطناعي</li>
              <li><strong>مساعد الذكاء الاصطناعي</strong> - الدردشة مع الذكاء الاصطناعي للحصول على إجابات فورية حول بياناتك</li>
              <li><strong>التوصيات الذكية</strong> - اقتراحات مدعومة بالذكاء الاصطناعي لتحسين عملياتك</li>
              <li><strong>الرؤى المحفوظة</strong> - الوصول إلى الرؤى المفضلة والمحفوظة</li>
              <li><strong>تاريخ الرؤى</strong> - عرض الرؤى السابقة وتقارير التحليل</li>
              <li><strong>المراقبة في الوقت الفعلي</strong> - مراقبة وإنذارات مدعومة بالذكاء الاصطناعي مباشرة</li>
              <li><strong>التحليلات التنبؤية</strong> - توقع الاتجاهات والنتائج المستقبلية</li>
              <li><strong>التحسين التلقائي</strong> - توصيات آلية لتوفير الطاقة والتكاليف</li>
              <li><strong>تقارير الذكاء الاصطناعي</strong> - إنشاء تقارير تحليل شاملة للذكاء الاصطناعي</li>
              <li><strong>مقارنة المواقع</strong> - مقارنة الأداء عبر مواقع متعددة مع الذكاء الاصطناعي</li>
              <li><strong>اكتشاف الشذوذ</strong> - اكتشاف الأنماط غير العادية والمشاكل المحتملة</li>
              <li><strong>تحليلات الكربون</strong> - تتبع وتحسين البصمة الكربونية</li>
            </ul>
            
            <h4>تصفية الميزات:</h4>
            <p>استخدم أزرار أيقونات التصفية الدائرية في الأعلى لتصفية الميزات حسب الفئة:</p>
            <ul>
              <li><strong>جميع الميزات</strong> - عرض جميع الميزات المتاحة</li>
              <li><strong>الجيل</strong> - الميزات التي تولد الرؤى والتقارير</li>
              <li><strong>التفاعلية</strong> - الميزات للتفاعل المباشر مع الذكاء الاصطناعي</li>
              <li><strong>الذكاء</strong> - ميزات التوصيات المدعومة بالذكاء الاصطناعي</li>
              <li><strong>التحليلات</strong> - ميزات تحليل البيانات والمقارنة</li>
              <li><strong>المراقبة</strong> - ميزات المراقبة والكشف في الوقت الفعلي</li>
              <li><strong>التحسين</strong> - الميزات التي تركز على تحسين الكفاءة</li>
              <li><strong>الإدارة</strong> - الميزات لإدارة الرؤى المحفوظة والتاريخ</li>
              <li><strong>الاستدامة</strong> - الميزات المتعلقة بالكربون والتأثير البيئي</li>
            </ul>
            
            <div class="help-tip">
              <strong>نصيحة:</strong> انقر على أي بطاقة ميزة للوصول إلى أداة الذكاء الاصطناعي المحددة. استخدم أيقونات التصفية للعثور بسرعة على الميزات حسب الفئة.
            </div>`,
    'helpContent.aiInsightsWizard': `<p>يرشدك معالج رؤى الذكاء الاصطناعي خلال عملية خطوة بخطوة لإنشاء رؤى مخصصة للذكاء الاصطناعي مصممة خصيصًا لاحتياجاتك.</p>

            <h4>خطوات المعالج:</h4>
            <ol>
              <li><strong>اختر الموقع</strong> - اختر الموقع الذي تريد تحليله برؤى الذكاء الاصطناعي</li>
              <li><strong>اختر الوحدة (اختياري)</strong> - اختر وحدة واحدة أو أكثر، أو اختر جميع الوحدات للتحليل. يمكنك تخطي هذه الخطوة إذا كنت تريد تحليل جميع الوحدات.</li>
              <li><strong>اختر الجهاز (اختياري)</strong> - اختر جهازًا محددًا أو حلل جميع الأجهزة. يمكنك تخطي هذه الخطوة لتحليل جميع الأجهزة.</li>
              <li><strong>قدرات الذكاء الاصطناعي</strong> - اختر قدرة واحدة أو أكثر من قدرات الذكاء الاصطناعي لإنشاء رؤى مخصصة:
                <ul>
                  <li>الصيانة التنبؤية - توقع احتياجات المعدات قبل الأعطال</li>
                  <li>تحسين الطاقة - تقليل الاستهلاك والتكاليف التشغيلية</li>
                  <li>اكتشاف الشذوذ - التعرف على الأنماط والتنبيهات في الوقت الفعلي</li>
                  <li>تحليل التكاليف - حساب التوفير وعائد الاستثمار تلقائيًا</li>
                  <li>التنبؤ - توقع الأداء والاتجاهات المستقبلية</li>
                  <li>تأثير الكربون - تتبع وتحسين الاستدامة</li>
                </ul>
              </li>
              <li><strong>إنشاء الرؤى</strong> - راجع اختياراتك وأنشئ الرؤى</li>
            </ol>
            
            <h4>استخدام المعالج:</h4>
            <ul>
              <li>انقر "الخطوة التالية" للمتابعة خلال المعالج</li>
              <li>انقر "رجوع" للعودة إلى الخطوات السابقة</li>
              <li>انقر "تخطي" لتخطي الخطوات الاختيارية (الوحدات والأجهزة)</li>
              <li>اختر عناصر متعددة حيث ينطبق (الوحدات، الأجهزة، القدرات)</li>
              <li>راجع اختياراتك في الخطوة الأخيرة قبل الإنشاء</li>
            </ul>

            <div class="help-tip">
              <strong>نصيحة:</strong> يمكنك حفظ الرؤى المُنشأة للوصول إليها لاحقًا من ميزة الرؤى المحفوظة.
            </div>`,
    'helpContent.aiAssistant': `<p>يسمح لك مساعد الذكاء الاصطناعي بالدردشة مع الذكاء الاصطناعي للحصول على إجابات فورية حول بياناتك، أو مواقعك، أو أجهزتك، أو استهلاك الطاقة، أو الرؤى.</p>

            <h4>استخدام مساعد الذكاء الاصطناعي:</h4>
            <ul>
              <li>اكتب سؤالك في حقل الإدخال في الأسفل</li>
              <li>اضغط Enter أو انقر على زر الإرسال لإرسال سؤالك</li>
              <li>سيحلل الذكاء الاصطناعي بياناتك ويقدم ردًا مفصلاً</li>
              <li>يمكنك طرح أسئلة متابعة في نفس المحادثة</li>
            </ul>
            
            <h4>ما يمكنك أن تسأل عنه:</h4>
            <ul>
              <li>أسئلة حول مواقعك وأجهزتك</li>
              <li>تحليل استهلاك الطاقة</li>
              <li>فرص التحسين</li>
              <li>طلب تقارير مفصلة</li>
              <li>أسئلة عامة حول بياناتك</li>
            </ul>
            
            <div class="help-tip">
              <strong>نصيحة:</strong> كن محددًا في أسئلتك للحصول على أفضل النتائج. لدى الذكاء الاصطناعي إمكانية الوصول إلى بيانات المبنى الخاص بك ويمكنه تقديم إجابات مفصلة ومرتبطة بالسياق.
            </div>`,
  },
  es: {
    // Navigation
    overview: 'Resumen',
    sites: 'Sitios',
    compliance: 'Cumplimiento',
    navGroupMain: 'Principal',
    navGroupAnalytics: 'Analíticas',
    navGroupSystem: 'Sistema',

    // Dashboard
    totalUsage: 'Uso Total',
    alerts: 'Alertas',
    powerUsageTrend: 'Tendencia de Uso de Energía',
    active: 'Activo',
    
    // Common
    search: 'Buscar',
    backToSites: 'Volver a Sitios',
    expandAll: 'Expandir Todo',
    collapseAll: 'Contraer Todo',
    online: 'En Línea',
    offline: 'Desconectado',
    refreshLiveData: 'Actualizar Datos en Vivo',
    enterFullscreen: 'Entrar en Pantalla Completa',
    exitFullscreen: 'Salir de Pantalla Completa',
    live: 'En Vivo',
    graphic: 'Gráfico',
    historic: 'Histórico',
    fuseboardDevice: 'Dispositivo de Tablero de Fusibles',
    generatingAiInsights: 'Generando Insights de IA',
    analysingReadings: 'Analizando las últimas lecturas de este dispositivo...',
    mouldRiskZoneMap: 'Mapa de Zona de Riesgo de Moho',
    property: 'propiedad',
    withMouldRisk: 'con riesgo de moho',
    high: 'Alto',
    medium: 'Medio',
    low: 'Bajo',
    changeLanguage: 'Cambiar Idioma',
    searchPlaceholder: 'Buscar…',
    logout: 'Cerrar Sesión',
    
    // Loading Messages
    loadingFuseboardHierarchy: 'Cargando jerarquía de cuadros eléctricos...',
    loadingEnvironmentalDashboard: 'Cargando panel ambiental...',
    loadingHistoricalData: 'Cargando datos históricos...',
    loadingDashboardOverview: 'Cargando resumen del panel...',
    loadingReports: 'Cargando informes...',
    loadingConfigurations: 'Cargando configuraciones...',
    loadingOfflineDevices: 'Cargando dispositivos sin conexión...',
    noOfflineDevices: 'Todo en Orden',
    allDevicesReporting: 'Todos los dispositivos están funcionando normalmente',

    // Buttons & Actions
    exportCsv: 'Exportar CSV',
    exportPdf: 'Exportar PDF',
    exportLeagueTable: 'Exportar Tabla de Liga',
    switchTo2D: 'Cambiar a Vista 2D',
    switchTo3D: 'Cambiar a Vista 3D (Muestra Edificios 3D)',
    changeMapStyle: 'Cambiar Estilo de Mapa',
    collapseAllTreeNodes: 'Contraer todos los nodos del árbol',
    expandAllTreeNodes: 'Expandir todos los nodos del árbol',
    
    // Search Placeholders
    searchAlerts: 'Buscar alertas...',
    searchConfigurations: 'Buscar configuraciones...',
    searchByTime: 'Buscar por tiempo (ej: 14:30, 2024-01-15, 15 ene)...',
    
    // Export & Reports
    fuseboardChartsExport: 'Exportar Gráficos de Cuadros Eléctricos',
    exportedFromSmartInstallers: 'Exportado desde {companyName} Insights',
    generatedOn: 'Generado el:',
    exportLiveDataToCsv: 'Exportar Datos en Vivo a CSV',
    exportChartsToPdf: 'Exportar Gráficos a PDF',
    exportHistoricDataToCsv: 'Exportar Datos Históricos a CSV',
    
    // Form Labels
    to: 'a',
    from: 'de',
    
    // Time
    vsLastMonth: 'vs mes pasado',
    vsLastWeek: 'vs semana pasada',
    
    // Admin Page
    admin: 'Administrador',
    help: 'Ayuda',
    settings: 'Configuración',
    addDevice: 'Agregar Dispositivo',
    editDevice: 'Editar Dispositivo',
    createDevice: 'Crear Dispositivo',
    updateDevice: 'Actualizar Dispositivo',
    deleteDevice: 'Eliminar Dispositivo',
    searchDevices: 'Buscar dispositivos...',
    allDeviceTypes: 'Todos los Tipos de Dispositivos',
    noDevicesFound: 'No se encontraron dispositivos',
    tryAdjustingSearch: 'Intente ajustar su búsqueda o filtros',
    identifier: 'Identificador',
    title: 'Título',
    actions: 'Acciones',
    units: 'Unidades',
    unitsOptional: 'Unidades (Opcional)',
    noUnitsAvailable: 'No hay unidades disponibles para este sitio',
    unassignedDevices: 'Dispositivos No Asignados',
    siteLevel: 'Nivel de Sitio',
    notAssignedToUnit: 'No asignado a ninguna unidad',
    unitsSelected: 'unidad(es) seleccionada(s)',
    cancel: 'Cancelar',
    save: 'Guardar',
    delete: 'Eliminar',
    areYouSure: '¿Está seguro?',
    cannotBeUndone: 'Esta acción no se puede deshacer.',
    
    // Fuse Configuration
    fuseConfigurationRequired: 'Configuración de Fusibles (Requerido para FUSEBOARD_UDC)',
    configureAtLeast2Fuses: 'Configure al menos 2 fusibles con voltaje, factor de potencia, nombre del dispositivo y categoría. Los fusibles 3 y 4 son opcionales.',
    fuse: 'Fusible',
    deviceName: 'Nombre del Dispositivo',
    category: 'Categoría',
    voltage: 'Voltaje (V)',
    powerFactor: 'Factor de Potencia',
    supply: 'Suministro',
    circuit: 'Circuito',
    load: 'Carga',
    other: 'Otro',
    atLeast2FusesRequired: 'Se deben configurar al menos 2 fusibles para dispositivos FUSEBOARD_UDC',
    
    // Organization & Site Settings
    organizationKpiConfiguration: 'Configuración de KPI de la Organización',
    siteKpiConfiguration: 'Configuración de KPI del Sitio',
    unitRate: 'Tarifa Unitaria (£ por kWh)',
    intervalMinutes: 'Minutos de Intervalo',
    carbonFactor: 'Factor de Carbono (kg CO₂e por kWh)',
    howOftenReadingsArrive: 'Con qué frecuencia llegan las lecturas del dispositivo (en minutos)',
    carbonEmissionFactor: 'Factor de emisión de carbono para el cálculo de ahorro de carbono',
    appliesToAllSites: 'Estas configuraciones se aplican a todos los sitios a menos que se anulen a nivel de sitio.',
    overrideOrganizationSettings: 'Anular la configuración de la organización para un sitio específico. Déjelo vacío para usar los valores predeterminados de la organización.',
    selectASite: '-- Seleccionar un sitio --',
    unitRateOverride: 'Anulación de Tarifa Unitaria (£ por kWh)',
    intervalMinutesOverride: 'Anulación de Minutos de Intervalo',
    carbonFactorOverride: 'Anulación de Factor de Carbono (kg CO₂e por kWh)',
    leaveEmptyForDefault: 'Déjelo vacío para usar el valor predeterminado de la organización',
    current: 'Actual',
    usingOrgDefault: 'Usando el valor predeterminado de la organización',
    saveOrganizationSettings: 'Guardar Configuración de la Organización',
    saveSiteSettings: 'Guardar Configuración del Sitio',
    
    // Alerts
    recentAlerts: 'Alertas Recientes',
    recentAlertsHub: 'Centro de Alertas Recientes',
    noAlertsFound: 'No se encontraron alertas',
    activeAlerts: 'Alertas',
    configurations: 'Configuraciones',
    offlineMonitoring: 'Monitoreo Sin Conexión',
    acknowledged: 'Reconocido',
    resolved: 'Resuelto',
    critical: 'Crítico',
    warning: 'Advertencia',
    info: 'Información',
    all: 'Todos',
    severity: 'Severidad',
    status: 'Estado',
    acknowledge: 'Reconocer',
    resolve: 'Resolver',
    view: 'Ver',
    viewDetails: 'Ver Detalles',
    createAlertRule: 'Crear Regla de Alerta',
    newMonitor: 'Nuevo Monitor',
    
    // Common UI
    refresh: 'Actualizar',
    close: 'Cerrar',
    open: 'Abrir',
    yes: 'Sí',
    no: 'No',
    confirm: 'Confirmar',
    required: 'Requerido',
    optional: 'Opcional',
    loading: 'Cargando...',
    error: 'Error',
    success: 'Éxito',
    failed: 'Fallido',
    saved: 'Guardado',
    updated: 'Actualizado',
    deleted: 'Eliminado',
    created: 'Creado',
    
    // Navigation
    aiHub: 'Centro de IA',
    
    // Pagination
    itemsPerPage: 'Elementos por página',
    previous: 'Anterior',
    next: 'Siguiente',
    page: 'Página',
    showing: 'Mostrando',
    item: 'elemento',
    items: 'elementos',
    nextStep: 'Siguiente Paso',
    previousAlert: 'Alerta anterior',
    nextAlert: 'Alerta siguiente',
    
    // Map
    gridVsCircuitVoltage: 'Voltaje de Red vs Circuito',
    
    // Reports
    reports: 'Informes',
    timeRange: 'Rango de Tiempo',
    last24Hours: 'Últimas 24 Horas',
    last7Days: 'Últimos 7 Días',
    last30Days: 'Últimos 30 Días',
    last90Days: 'Últimos 90 Días',
    customRange: 'Rango Personalizado',
    
    // Chat Assistant
    helpAssistant: 'Asistente de Ayuda',
    askMeAnything: 'Pregúntame cualquier cosa sobre {companyName} Insights',
    askAQuestion: 'Haz una pregunta...',
    send: 'Enviar',
    closeChat: 'Cerrar chat',
    
    // Additional Alert & UI Strings
    viewAll: 'Ver Todo',
    viewAllAlerts: 'Ver Todas las Alertas',
    refreshAlerts: 'Actualizar alertas',
    alertOverview: 'Resumen de Alertas',
    unread: 'No Leído',
    allSeverity: 'Toda la Severidad',
    allStatus: 'Todos los Estados',
    markAsUnseen: 'Marcar como No Visto',
    allClear: '¡Todo Claro!',
    noAlertsMatchFilters: 'No hay alertas que coincidan con sus filtros actuales',
    loadingAlerts: 'Cargando alertas...',
    exportAlertsToCsv: 'Exportar Alertas a CSV',
    manage: 'Gestionar',
    paused: 'Pausado',
    drafts: 'Borradores',
    editMonitor: 'Editar Monitor',
    deleteMonitor: 'Eliminar Monitor',
    noAlertsToExport: 'No hay alertas para exportar',
    total: 'Total',
    activeFuses: 'Fusibles Activos',
    overloadAlert: 'Alerta de sobrecarga',
    noAlerts: 'Sin alertas',
    smartInstallers: 'Instaladores Inteligentes',
    edit: 'Editar',
    energySaved: 'Energía Ahorrada',
    totalEnergy: 'Energía Total',
    totalEnergySaved: 'Energía Total Ahorrada',
    voltageTrend: 'Tendencia de Voltaje',
    environmentalTrend: 'Tendencia Ambiental',
    cumulativeTotal: 'Total acumulado',
    
    // Reports
    leagueTable: 'Tabla de Liga',
    aiInsights: 'Insights de IA',
    savingsCarbon: 'Ahorros y Carbono',
    customisedReporting: 'Informes Personalizados',
    recommendedActions: 'Acciones Recomendadas',
    recommendedAction: 'Acción Recomendada',
    noInsightsAvailable: 'No Hay Insights Disponibles',
    noInsightsDescription: 'Seleccione un dispositivo para ver insights generados por IA',
    sensorHealth: 'Salud del Sensor',

    // Site Manager
    siteNotes: 'Gestor del Sitio',
    dashboard: 'Panel de Control',
    
    // Help Page - UI
    searchHelpTopics: 'Buscar temas de ayuda...',
    searchHelp: 'Buscar ayuda...',
    tableOfContents: 'Tabla de Contenidos',
    noResultsFound: 'No se encontraron resultados',
    tryAdjustingSearchTerms: 'Intenta ajustar tus términos de búsqueda o navega por la tabla de contenidos.',
    helpCenter: 'Centro de Ayuda',
    helpHeroSubtitle: 'Todo lo que necesitas para aprovechar al máximo {companyName} Monitoring Platform',

    // Quick Start Guide
    quickStartGuide: 'Guía de Inicio Rápido',
    quickStartSubtitle: '¿Nuevo en {companyName} Monitoring Platform? Comienza aquí para ponerte en marcha rápidamente.',
    quickStartLogin: 'Iniciar Sesión',
    quickStartLoginDesc: 'Accede a la plataforma con autenticación Auth0',
    quickStartDashboard: 'Vista General del Panel',
    quickStartDashboardDesc: 'Comprende los KPIs, alertas y monitoreo de sitios',
    quickStartFeatures: 'Funciones Principales',
    quickStartFeaturesDesc: 'Explora Sitios, Alertas e Informes',
    stillNeedHelp: '¿Aún necesitas ayuda?',
    stillNeedHelpDesc: 'Nuestro equipo está aquí para asistirte. Usa el asistente de chat para obtener soporte instantáneo.',
    openChatAssistant: 'Abrir Asistente de Chat',

    aiInsightsWizardDesc: 'Generar insights personalizados de IA con guía paso a paso',
    aiAssistantDesc: 'Chatea con IA para obtener respuestas instantáneas sobre tus datos',
    smartRecommendationsDesc: 'Sugerencias impulsadas por IA para optimizar tus operaciones',
    savedInsightsDesc: 'Accede a tus insights guardados y favoritos',
    insightHistoryDesc: 'Ver insights y informes de análisis pasados',
    realTimeMonitoringDesc: 'Monitoreo y alertas en tiempo real impulsados por IA',
    predictiveAnalyticsDesc: 'Predecir tendencias y resultados futuros',
    dynamicTelemetryDesc: 'Controla dinámicamente la frecuencia de los informes de sensores según las condiciones ambientales en tiempo real',
    aiReportsDesc: 'Generar informes de análisis de IA completos',
    siteComparisonDesc: 'Comparar rendimiento en múltiples sitios con IA',
    anomalyDetectionDesc: 'Detectar patrones inusuales y problemas potenciales',
    carbonAnalyticsDesc: 'Rastrear y optimizar tu huella de carbono',
    backToHub: 'Volver al Centro',
    startConversation: 'Iniciar conversación',
    askMeAnythingAbout: 'Pregúntame cualquier cosa sobre tus sitios, dispositivos, consumo de energía o insights',
    loadingReportData: 'Cargando datos del informe...',
    generatingComprehensiveReport: 'Generando informe de análisis completo',
    comprehensiveAnalysisReport: 'Informe de Análisis Completo',
    downloadReport: 'Descargar Informe',
    analysisOfEnvironmentalConditions: 'Análisis de condiciones ambientales y riesgo de crecimiento de moho en todos los sitios monitoreados',
    siteCostSavings: 'Ahorro de Costos del Sitio',
    topSitesByCostSavings: 'Top {count} Sitios por Ahorro de Costos',
    costSavingsBySite: 'Ahorro de Costos por Sitio',
    costSavingsBreakdown: 'Desglose de ahorro de costos para esta ubicación',
    leadingLocationsContributing: 'Ubicaciones líderes que contribuyen al ahorro total',
    siteBySiteBreakdown: 'Desglose Sitio por Sitio',
    rank: 'Rango',
    siteName: 'Nombre del Sitio',
    costSavings: 'Ahorro de Costos',
    carbonSaved: 'Carbono Ahorrado',
    impactScore: 'Puntuación de Impacto',
    selectSiteAndDevices: 'Seleccionar Sitio y Dispositivos',
    chooseSiteAndSelectDevices: 'Elige un sitio y selecciona los dispositivos que quieres comparar',
    siteSelection: 'Selección de Sitio',
    siteBySiteRiskAnalysis: 'Análisis de Riesgo Sitio por Sitio',
    totalDevices: 'Total de Dispositivos',
    avgDaysToMould: 'Promedio de Días hasta Moho',
    avgTemp: 'Temp. Promedio',
    avgHumidity: 'Humedad Promedio',
    riskPercent: 'Riesgo %',
    riskDistributionAcrossAllSites: 'Distribución de Riesgo en Todos los Sitios',
    riskComparisonBySite: 'Comparación de Riesgo por Sitio',
    daysToMouldByDevice: 'Días hasta Moho por Dispositivo (Top 20)',
    atRisk: 'En Riesgo',
    temperature: 'Temperatura',
    humidity: 'Humedad',
    riskLevel: 'Nivel de Riesgo',
    lastReading: 'Última Lectura',
    noReportsAvailable: 'No Hay Informes Disponibles',
    noReportsEnabled: 'Aún no has habilitado ningún informe. Ve a Configuración para habilitar los informes que quieres ver.',
    selectSitesToCompare: 'Seleccionar Sitios para Comparar',
    site: 'Sitio',
    savingsCarbonImpactReport: 'Informe de Ahorros e Impacto de Carbono',
    analysisOfCostSavings: 'Análisis de ahorros de costos, eficiencia energética e impacto ambiental',
    totalCostSavings: 'Ahorro Total de Costos',
    environmentalImpact: 'Impacto Ambiental',
    energyCarbonImpact: 'Impacto de Energía y Carbono',
    energyCarbonCorrelation: 'Correlación de Energía y Carbono',
    energyCarbonData: 'Datos de Energía y Carbono',
    energySavedCarbonReductionLocation: 'Energía ahorrada y reducción de carbono para esta ubicación',
    relationshipEnergyCarbonReduction: 'Relación entre energía ahorrada y reducción de carbono en todos los sitios',
    carbonKg: 'Carbono (kg)',
    energyKwh: 'Energía (kWh)',
    yourEnergyOptimizationEfforts: 'Tus esfuerzos de optimización de energía han ahorrado',
    ofCO2: 'de CO₂',
    equivalentToPlanting: 'equivalente a plantar',
    trees: 'árboles',
    orRemoving: 'o retirar',
    carsFromRoadForYear: 'autos de la carretera por un año',
    financialImpact: 'Impacto Financiero',
    totalCostSavingsOf: 'Ahorros totales de costos de',
    demonstrateSignificantFinancialBenefits: 'demuestran los beneficios financieros significativos de la gestión y optimización proactiva de energía',
    energyEfficiency: 'Eficiencia Energética',
    bySaving: 'Al ahorrar',
    youveReducedEnergyConsumption: 'has reducido el consumo de energía equivalente a alimentar',
    averageHomesAnnually: 'hogares promedio anualmente',
    bespokeReportingServices: 'Servicios de Informes Personalizados',
    weCraftCustomDashboards: 'Creamos paneles analíticos personalizados adaptados a tus necesidades específicas de monitoreo e informes',
    customDashboards: 'Paneles Personalizados',
    tailoredVisualizationInterfaces: 'Interfaces de visualización personalizadas diseñadas en torno a tus requisitos únicos de datos',
    advancedAnalytics: 'Análisis Avanzado',
    deepInsightsPredictive: 'Insights profundos con capacidades de modelado predictivo y análisis de tendencias',
    realTimeInsights: 'Insights en Tiempo Real',
    liveDataProcessing: 'Procesamiento de datos en vivo con alertas y monitoreo instantáneos',
    systemIntegration: 'Integración del Sistema',
    seamlessConnection: 'Conexión perfecta con tu infraestructura y herramientas existentes',
    readyToTransformData: '¿Listo para transformar tus datos en insights accionables?',
    getStarted: 'Comenzar',
    contactDevelopmentTeam: 'Contacta a nuestro equipo de desarrollo para discutir tus requisitos',
    device: 'Dispositivo',
    selectDeviceType: 'Seleccionar Tipo de Dispositivo',
    chooseDeviceTypeToAnalyse: 'Elige el tipo de dispositivo que quieres analizar',
    deviceType: 'Tipo de Dispositivo',
    devicesSelected: 'Dispositivos Seleccionados',
    
    // Help Page - Section Titles
    gettingStarted: 'Comenzar',
    overviewDashboard: 'Panel de Resumen',
    sitesManagement: 'Gestión de Sitios',
    alertsManagement: 'Gestión de Alertas',
    reportsAnalytics: 'Informes y Análisis',
    adminSettings: 'Administración y Configuración',
    chatAssistant: 'Asistente de Chat',
    tipsTroubleshooting: 'Consejos y Solución de Problemas',
    
    // Help Page - Subsection Titles
    loggingIn: 'Iniciar Sesión',
    navigationOverview: 'Resumen de Navegación',
    settingsPreferences: 'Configuración y Preferencias',
    understandingKpiCards: 'Entender las Tarjetas KPI',
    recentAlertsSection: 'Sección de Alertas Recientes',
    siteMap: 'Mapa del Sitio',
    sitesPageOverview: 'Resumen de la Página de Sitios',
    siteDashboards: 'Paneles de Sitio',
    siteNotesAnnotations: 'Gestor del Sitio y Anotaciones',
    alertsPageOverview: 'Resumen de la Página de Alertas',
    viewingFilteringAlerts: 'Ver y Filtrar Alertas',
    alertActions: 'Acciones de Alerta',
    alertConfigurations: 'Configuraciones de Alerta',
    offlineDeviceMonitoring: 'Monitoreo de Dispositivos Desconectados',
    reportsPageOverview: 'Resumen de la Página de Informes',
    selectingTimeRanges: 'Seleccionar Rangos de Tiempo',
    understandingChartsGraphs: 'Entender Gráficos y Diagramas',
    exportingReports: 'Exportar Informes',
    customReportingServices: 'Servicios de Informes Personalizados',
    adminPageOverview: 'Resumen de la Página de Administración',
    deviceManagement: 'Gestión de Dispositivos',
    fuseConfiguration: 'Configuración de Fusibles (FUSEBOARD_UDC)',
    organizationSettings: 'Configuración de la Organización',
    siteSettings: 'Configuración del Sitio',
    usingChatAssistant: 'Usar el Asistente de Chat',
    bestPractices: 'Mejores Prácticas',
    commonIssuesSolutions: 'Problemas Comunes y Soluciones',
    keyboardShortcuts: 'Atajos de Teclado',
    gettingAdditionalHelp: 'Obtener Ayuda Adicional',
    
    // AI Hub - Section Titles
    aiHubOverview: 'Resumen del Centro de IA',
    aiInsightsWizard: 'Asistente de Insights de IA',
    aiAssistant: 'Asistente de IA',
    smartRecommendations: 'Recomendaciones Inteligentes',
    savedInsights: 'Insights Guardados',
    insightHistory: 'Historial de Insights',
    realTimeMonitoring: 'Monitoreo en Tiempo Real',
    predictiveAnalytics: 'Análisis Predictivo',
    dynamicTelemetry: 'Telemetría Dinámica',
    aiReports: 'Informes de IA',
    siteComparison: 'Comparación de Sitios',
    anomalyDetection: 'Detección de Anomalías',
    carbonAnalytics: 'Análisis de Carbono',
    
    // Sites Page
    allTypes: 'Todos los Tipos',
    temporaryPlantRoom: 'Sala de Plantas Temporal',
    heatNetwork: 'Red de Calefacción',
    fuseboardManagement: 'Gestión de Cuadros Eléctricos',
    environmentalMonitoring: 'Monitoreo Ambiental',
    daysToMould: 'Días hasta Moho',
    mouldRisk: 'Riesgo de Moho',
    mouldRiskScore: 'Puntuación de Riesgo de Moho',
    mouldRiskCalculation: 'Cálculo de Riesgo de Moho',
    mouldRiskActive: 'Riesgo de Moho Activo',
    mouldRiskAssessment: 'Evaluación de Riesgo de Moho',
    environmentalSensor: 'Sensor Ambiental',
    environmentalSensors: 'Sensores Ambientales',
    averageDaysToMould: 'Promedio de Días hasta Moho',
    averageDaysToCritical: 'Promedio de Días hasta Crítico',
    averageTemperature: 'Temperatura Promedio',
    averageHumidity: 'Humedad Promedio',
    insufficientData: '—',
    devicesAtRisk: 'Dispositivos en Riesgo',
    mouldRiskStatus: 'Estado de Riesgo de Moho',
    propertiesAtRisk: 'Dispositivos en Riesgo',
    siteAverageTrend: 'Tendencia Promedio del Sitio',
    siteAverage: 'Promedio del Sitio',
    risk: 'Riesgo',
    criticalRisk: 'Riesgo Crítico',
    highRisk: 'Alto Riesgo',
    mediumRisk: 'Riesgo Medio',
    lowRisk: 'Bajo Riesgo',
    noRisk: 'Sin Riesgo',
    mouldRiskReport: 'Informe de Riesgo de Moho',
    mouldRiskAssessmentReport: 'Informe de Evaluación de Riesgo de Moho',
    deviceMouldRiskAssessment: 'Evaluación de Riesgo de Moho del Dispositivo',
    days: 'días',
    noEnvironmentalSensorsFound: 'No se encontraron sensores ambientales',
    attentionRequired: 'Atención Requerida',
    allSystemsNormal: 'Todos los Sistemas Normales',
    noSiteDataAvailable: 'No hay datos del sitio disponibles',
    noSavingsDataAvailable: 'No hay datos de ahorro disponibles',
    noComparisonDataAvailable: 'No hay datos de comparación disponibles',
    noSiteSavingsDataAvailable: 'No hay datos de ahorro del sitio disponibles',
    systemStatus: 'Estado del Sistema',
    errorLoadingInsights: 'Error al Cargar Insights',
    highPriority: 'Alta Prioridad',
    mediumPriority: 'Prioridad Media',
    lowPriority: 'Baja Prioridad',
    systemHealth: 'Salud del Sistema',
    popular: 'Popular',
    new: 'Nuevo',
    beta: 'Acceso anticipado',
    selectSite: 'Seleccionar Sitio',
    selectUnit: 'Seleccionar Unidad',
    selectDevice: 'Seleccionar Dispositivo',
    aiCapabilities: 'Capacidades de IA',
    generate: 'Generar',
    predictiveMaintenance: 'Mantenimiento Predictivo',
    energyOptimisation: 'Optimización de Energía',
    costAnalysis: 'Análisis de Costos',
    forecasting: 'Pronóstico',
    carbonImpact: 'Impacto de Carbono',
    searchSitesByNameOrType: 'Buscar sitios por nombre o tipo...',
    searchSites: 'Buscar sitios…',
    clearFilters: 'Limpiar filtros',
    properties: 'Propiedades',
    devices: 'Dispositivos',
    enterDashboard: 'Entrar al Panel',
    of: 'de',
    matching: 'coincidiendo',
    noSitesFound: 'No se Encontraron Sitios',
    noSitesMatchCriteria: 'No hay sitios que coincidan con sus criterios de búsqueda y filtrado actuales.',
    tryAdjustingSearchOrFilters: 'Intenta ajustar tus términos de búsqueda o filtros.',
    noSitesAvailable: 'No Hay Sitios Disponibles',
    noSitesConfigured: 'Aún no se han configurado sitios para su organización.',
    unknownDashboardType: 'Tipo de Panel Desconocido',
    dashboardTypeNotSupported: 'El tipo de panel "{type}" no es compatible.',
    unknown: 'Desconocido',
    
    // Help Page Content
    'helpContent.login': `<p>Para acceder a {companyName} Insights, necesitarás iniciar sesión con tus credenciales.</p>

            <h4>Pasos para Iniciar Sesión:</h4>
            <ol>
              <li>Ingresa tu nombre de usuario en el campo de nombre de usuario</li>
              <li>Ingresa tu contraseña en el campo de contraseña</li>
              <li>Haz clic en el botón "Iniciar Sesión"</li>
            </ol>
            
            <div class="help-tip">
              <strong>Consejo:</strong> Si olvidas tu contraseña, contacta a tu administrador del sistema para obtener ayuda.
            </div>`,
    'helpContent.navigation': `<p>La navegación principal se encuentra en la barra lateral izquierda. Puedes acceder a diferentes secciones de la aplicación:</p>

            <h4>Secciones Principales:</h4>
            <ul>
              <li><strong>Resumen</strong> - Panel con KPIs, alertas y mapa del sitio</li>
              <li><strong>Sitios</strong> - Ver y gestionar todos tus sitios</li>
              <li><strong>Alertas</strong> - Monitorear y gestionar alertas del sistema</li>
              <li><strong>Informes</strong> - Generar y ver informes</li>
              <li><strong>Administración</strong> - Gestión de dispositivos y configuración del sistema (se requiere acceso de administrador)</li>
              <li><strong>Ayuda</strong> - Documentación de ayuda completa y asistente de chat</li>
            </ul>
            
            `,
    'helpContent.settings': `<p>Puedes personalizar tu experiencia con varias configuraciones:</p>
            
            <h4>Configuración de Tema:</h4>
            <ul>
              <li>Alterna entre modo claro y oscuro usando el conmutador de tema en la esquina superior derecha</li>
              <li>Tu preferencia se guarda automáticamente</li>
            </ul>
            
            <h4>Configuración de Idioma:</h4>
            <ul>
              <li>Cambia el idioma de la interfaz usando el selector de idioma</li>
              <li>Los idiomas disponibles se muestran en el menú desplegable</li>
            </ul>
            
            `,
    'helpContent.kpiCards': `<p>El panel de Resumen muestra indicadores clave de rendimiento (KPIs) que te brindan información rápida sobre el rendimiento de tu sistema.</p>

            <h4>KPIs Disponibles:</h4>
            <ul>
              <li><strong>Carbono Ahorrado</strong> - Emisiones totales de carbono ahorradas, con indicadores de tendencia</li>
              <li><strong>Ahorro de Costos</strong> - Ahorros financieros logrados, comparados con períodos anteriores</li>
              <li><strong>Uso Total</strong> - Métricas de consumo de energía general</li>
              <li><strong>Voltaje de Red vs Circuito</strong> - Monitoreo y estado del voltaje</li>
            </ul>
            
            <h4>Efectos al Pasar el Mouse:</h4>
            <p>Pasa el mouse sobre cualquier tarjeta KPI para ver retroalimentación visual mejorada. Las tarjetas se resaltarán con bordes y sombras de colores.</p>
            
            `,
    'helpContent.recentAlerts': `<p>La sección de Alertas Recientes te muestra las últimas alertas de tu sistema, ayudándote a mantenerte informado sobre cualquier problema o evento importante.</p>

            <h4>Información de Alerta:</h4>
            <ul>
              <li><strong>Niveles de Severidad:</strong>
                <ul>
                  <li><span class="badge-critical">CRÍTICO</span> - Requiere atención inmediata</li>
                  <li><span class="badge-warning">ADVERTENCIA</span> - Importante pero no urgente</li>
                  <li><span class="badge-info">INFO</span> - Mensajes informativos</li>
                </ul>
              </li>
              <li><strong>Mensaje de Alerta</strong> - Descripción de lo que activó la alerta</li>
              <li><strong>Marca de Tiempo</strong> - Cuándo se generó la alerta</li>
            </ul>
            
            <h4>Entendiendo la Lista de Alertas:</h4>
            <p>Cada alerta se muestra con un indicador de severidad de color en el lado izquierdo, una insignia de severidad, el mensaje de alerta y la marca de tiempo. El estilo coincide con la página de Alertas para consistencia.</p>
            
            <div class="help-tip">
              <strong>Consejo:</strong> Haz clic en una alerta en la sección de Alertas Recientes para ver más detalles o navegar a la página completa de Alertas.
            </div>`,
    'helpContent.siteMap': `<p>El Mapa del Sitio proporciona una representación visual de todos tus sitios y puertas de enlace en un mapa interactivo.</p>

            <h4>Características del Mapa:</h4>
            <ul>
              <li><strong>Marcadores de Puerta de Enlace</strong> - Los marcadores verdes indican puertas de enlace en línea, los marcadores rojos indican puertas de enlace fuera de línea</li>
              <li><strong>Ubicaciones de Cuadro de Fusibles</strong> - Los iconos de rayo muestran ubicaciones de monitoreo de cuadros de fusibles</li>
              <li><strong>Destacados de Puntos de Referencia</strong> - Los edificios importantes están destacados (Guildhall, Old Bailey, Barbican, Mansion House)</li>
              <li><strong>Edificios 3D</strong> - Alterna la vista 3D para ver edificios en tres dimensiones</li>
            </ul>
            
            <h4>Controles del Mapa:</h4>
            <ul>
              <li><strong>Alternar 3D</strong> - Cambiar entre vistas de mapa 2D y 3D</li>
              <li><strong>Selector de Estilo</strong> - Cambiar el estilo del mapa (Calles, Satélite, Al Aire Libre, Navegación, Mínimo)</li>
              <li><strong>Pantalla Completa</strong> - Expandir el mapa a pantalla completa para una mejor visualización</li>
            </ul>

            <h4>Interactuando con los Marcadores:</h4>
            <ul>
              <li>Haz clic en cualquier marcador para ver una ventana emergente con información detallada</li>
              <li>Los marcadores de puerta de enlace muestran estado, nombre del sitio y coordenadas</li>
              <li>Los marcadores de cuadro de fusibles muestran ubicación y estado de monitoreo</li>
            </ul>
            
            <div class="help-tip">
              <strong>Consejo:</strong> Usa el botón de pantalla completa para una mejor vista al analizar múltiples sitios en el mapa.
            </div>`,
    'helpContent.sitesOverview': `<p>La página de Sitios te permite ver y gestionar todos tus sitios. Puedes buscar, filtrar y acceder a paneles detallados para cada sitio.</p>

            <h4>Ver Sitios:</h4>
            <ul>
              <li><strong>Vista de Lista</strong> - Ver sitios en formato de lista con información clave</li>
              <li><strong>Vista de Cuadrícula</strong> - Ver sitios como tarjetas en un diseño de cuadrícula</li>
              <li><strong>Búsqueda</strong> - Usa la barra de búsqueda para encontrar sitios específicos por nombre</li>
              <li><strong>Filtro</strong> - Filtrar sitios por tipo de panel (Planta Temporal, Red de Calefacción, Cuadro de Fusibles)</li>
            </ul>
            
            `,
    'helpContent.siteDashboards': `<p>Cada sitio tiene un panel dedicado que proporciona información detallada y capacidades de monitoreo. El tipo de panel depende de la configuración del sitio.</p>
            
            <h4>Tipos de Panel:</h4>
            
            <h5>1. Panel de Cuadro de Fusibles</h5>
            <p>El panel de Cuadro de Fusibles proporciona una vista completa de la gestión de cuadros de fusibles con una estructura de árbol jerárquica.</p>

            <h6>Panel Izquierdo - Árbol Jerárquico:</h6>
            <ul>
              <li>Muestra la estructura del sitio con unidades, propiedades y cuadros de fusibles</li>
              <li>Función de búsqueda para encontrar rápidamente cuadros de fusibles o unidades específicas</li>
              <li>Expandir/contraer nodos para navegar por la jerarquía</li>
              <li>Los indicadores de estado muestran el estado en línea/fuera de línea para cada dispositivo</li>
              <li>Los indicadores de alerta muestran si hay alertas activas</li>
            </ul>

            <h6>Panel Derecho - KPIs y Gráficos:</h6>
            <ul>
              <li><strong>Tarjetas KPI</strong> - Carbono ahorrado, ahorro de costos, uso total y alertas</li>
              <li><strong>Gráfico de Tendencia de Uso de Energía</strong> - Gráfico interactivo que muestra el consumo de energía durante las últimas 24 horas</li>
              <li>Pasa el mouse sobre las tarjetas KPI para ver retroalimentación visual mejorada</li>
              <li>Haz clic en el KPI de alerta para ver alertas detalladas del sitio</li>
            </ul>

            <h6>Interactuando con Cuadros de Fusibles:</h6>
            <ul>
              <li>Haz clic en cualquier cuadro de fusibles en el árbol para abrir un modal detallado</li>
              <li>El modal muestra información completa del dispositivo, lecturas y datos históricos</li>
              <li>Ver lecturas de voltaje, corriente y potencia con gráficos</li>
              <li>Exportar datos o ver en modo de pantalla completa</li>
            </ul>

            <h5>2. Panel de Planta Temporal</h5>
            <p>El panel de Planta Temporal está diseñado para monitorear instalaciones de salas de plantas temporales.</p>

            <h5>3. Panel de Red de Calefacción</h5>
            <p>El panel de Red de Calefacción proporciona monitoreo para sistemas de red de calefacción.</p>

            <div class="help-tip">
              <strong>Consejo:</strong> El diseño está fijo con paneles de igual tamaño, por lo que tanto el árbol jerárquico como los KPIs/gráficos siempre son visibles sin desplazarse.
            </div>`,
    'helpContent.siteNotes': `<p>Las Notas del Sitio te permiten agregar anotaciones, marcadores y notas a las imágenes del sitio para documentación y colaboración.</p>

            <h4>Características:</h4>
            <ul>
              <li><strong>Visualización de Imágenes</strong> - Ver imágenes del sitio en alta resolución</li>
              <li><strong>Marcadores</strong> - Agregar marcadores a ubicaciones específicas en la imagen</li>
              <li><strong>Editor de Texto Enriquecido</strong> - Agregar notas y documentación formateadas</li>
              <li><strong>Bloc de Notas</strong> - Sección de notas rápidas para información específica del sitio</li>
              <li><strong>Información del Dispositivo</strong> - Ver detalles del dispositivo haciendo clic en los marcadores</li>
            </ul>
            
            <h4>Agregar Marcadores:</h4>
            <ol>
              <li>Haz clic en el botón "Agregar Marcador" o haz clic directamente en la imagen</li>
              <li>Posiciona el marcador donde sea necesario</li>
              <li>Agrega una descripción o enlace a un dispositivo</li>
              <li>Guarda el marcador</li>
            </ol>

            <h4>Usando el Bloc de Notas:</h4>
            <ul>
              <li>Haz clic en el icono del bloc de notas para abrir el editor de notas</li>
              <li>Agrega notas rápidas, recordatorios o información específica del sitio</li>
              <li>Las notas se guardan automáticamente</li>
              <li>Accede a las notas en cualquier momento desde la página de notas del sitio</li>
            </ul>
            
            <div class="help-tip">
              <strong>Consejo:</strong> Los marcadores pueden vincularse a dispositivos para acceder rápidamente a información y lecturas del dispositivo.
            </div>`,
    'helpContent.alertsOverview': `<p>La página de Alertas proporciona gestión integral de alertas con tres pestañas principales: Alertas Activas, Configuraciones y Monitoreo Fuera de Línea.</p>

            <h4>Estadísticas de Alerta:</h4>
            <p>En la parte superior de la pestaña de Alertas Activas, verás un resumen que muestra:</p>
            <ul>
              <li>Número de alertas críticas</li>
              <li>Número de alertas de advertencia</li>
              <li>Número de alertas de información</li>
              <li>Recuento total de alertas</li>
            </ul>`,
    'helpContent.viewingAlerts': `<p>La lista de alertas muestra todas las alertas con información detallada y opciones de acción.</p>

            <h4>Información de Alerta Mostrada:</h4>
            <ul>
              <li><strong>Indicador de Severidad</strong> - Barra de color a la izquierda (rojo para crítico, amarillo para advertencia, azul para información)</li>
              <li><strong>Insignia de Severidad</strong> - Insignia visual que muestra el nivel de alerta</li>
              <li><strong>Mensaje de Alerta</strong> - Descripción de lo que activó la alerta</li>
              <li><strong>Identificador del Dispositivo</strong> - Qué dispositivo generó la alerta</li>
              <li><strong>Marca de Tiempo</strong> - Cuándo se generó la alerta</li>
              <li><strong>Estado</strong> - Estado actual (Activo, Reconocido, Resuelto)</li>
            </ul>
            
            <h4>Buscar y Filtrar:</h4>
            <ul>
              <li><strong>Barra de Búsqueda</strong> - Buscar alertas por mensaje o identificador de dispositivo</li>
              <li><strong>Filtro de Severidad</strong> - Filtrar por nivel de severidad (Todos, Crítico, Advertencia, Información)</li>
              <li><strong>Filtro de Estado</strong> - Filtrar por estado (Todos, Activo, Reconocido, Resuelto)</li>
            </ul>
            
            `,
    'helpContent.alertActions': `<p>Puedes realizar varias acciones en las alertas dependiendo de su estado.</p>

            <h4>Acciones Disponibles:</h4>
            
            <h5>Reconocer Alerta:</h5>
            <ul>
              <li>Disponible para alertas activas</li>
              <li>Marca la alerta como reconocida</li>
              <li>Muestra que estás al tanto del problema</li>
              <li>No resuelve la alerta</li>
            </ul>
            
            <h5>Resolver Alerta:</h5>
            <ul>
              <li>Disponible para alertas activas o reconocidas</li>
              <li>Marca la alerta como resuelta</li>
              <li>Indica que el problema ha sido abordado</li>
              <li>Las alertas resueltas aún se pueden ver pero se filtran por defecto</li>
            </ul>
            
            <h5>Ver Detalles:</h5>
            <ul>
              <li>Disponible para alertas resueltas</li>
              <li>Ver información detallada sobre la alerta</li>
              <li>Acceder a datos históricos relacionados con la alerta</li>
            </ul>

            <div class="help-tip">
              <strong>Consejo:</strong> Usa la acción de reconocer para rastrear qué alertas has revisado, incluso si aún no las has resuelto.
            </div>`,
    'helpContent.alertConfigurations': `<p>Las Configuraciones de Alerta te permiten crear y gestionar reglas de alerta personalizadas que monitorean tu sistema para condiciones específicas.</p>

            <h4>Gestión de Configuración:</h4>
            <p>Las configuraciones se organizan en un tablero Kanban con tres columnas:</p>
            <ul>
              <li><strong>Activo</strong> - Configuraciones que actualmente están monitoreando</li>
              <li><strong>Pausado</strong> - Configuraciones que están temporalmente deshabilitadas</li>
              <li><strong>Borradores</strong> - Plantillas de configuración (característica futura)</li>
            </ul>
            
            <h4>Crear una Nueva Configuración:</h4>
            <ol>
              <li>Haz clic en el botón "Crear Regla de Alerta"</li>
              <li>Selecciona una plantilla del menú desplegable</li>
              <li>Elige el alcance (nivel de Sitio, Unidad o Dispositivo)</li>
              <li>Selecciona el sitio/unidad/dispositivo específico si es aplicable</li>
              <li>Establece valores de umbral (mínimo y máximo)</li>
              <li>Elige el nivel de alerta (Información, Advertencia o Crítico)</li>
              <li>Habilita o deshabilita la configuración</li>
              <li>Haz clic en "Crear" para guardar</li>
            </ol>

            <h4>Gestionando Configuraciones:</h4>
            <ul>
              <li><strong>Arrastrar y Soltar</strong> - Mover configuraciones entre columnas Activo y Pausado</li>
              <li><strong>Editar</strong> - Haz clic en el botón de editar para modificar una configuración existente</li>
              <li><strong>Eliminar</strong> - Eliminar una configuración permanentemente</li>
            </ul>

            <div class="help-tip">
              <strong>Consejo:</strong> Usa configuraciones pausadas para deshabilitar temporalmente el monitoreo sin eliminar la regla por completo.
            </div>`,
    'helpContent.offlineMonitoring': `<p>La pestaña de Monitoreo Fuera de Línea te permite configurar alertas para cuando los dispositivos se desconecten.</p>

            <h4>Crear un Monitor Fuera de Línea:</h4>
            <ol>
              <li>Haz clic en el botón "Nuevo Monitor"</li>
              <li>Selecciona el dispositivo a monitorear</li>
              <li>Establece el umbral fuera de línea (en minutos) - cuánto tiempo debe estar un dispositivo fuera de línea antes de activar una alerta</li>
              <li>Elige el nivel de alerta (Advertencia o Crítico)</li>
              <li>Agrega una descripción opcional</li>
              <li>Habilita o deshabilita el monitor</li>
              <li>Haz clic en "Crear" para guardar</li>
            </ol>

            <h4>Información del Monitor:</h4>
            <p>Cada monitor muestra:</p>
            <ul>
              <li>Nombre del dispositivo</li>
              <li>Descripción (si se proporciona)</li>
              <li>Marca de tiempo de última vez visto</li>
              <li>Umbral fuera de línea</li>
              <li>Nivel de alerta</li>
              <li>Estado Habilitado/Deshabilitado</li>
            </ul>`,
    'helpContent.reportsOverview': `<p>La página de Informes proporciona capacidades analíticas e informes completos para los datos de tu sistema.</p>

            <h4>Informes Disponibles:</h4>
            <ul>
              <li><strong>Informe de Sitios</strong> - Resumen de todos los sitios con métricas clave</li>
              <li><strong>Consumo de Energía</strong> - Análisis detallado del uso de energía</li>
              <li><strong>Tendencia de Alertas</strong> - Datos históricos de alertas y tendencias</li>
              <li><strong>Tendencias de Temperatura</strong> - Monitoreo de temperatura a lo largo del tiempo</li>
              <li><strong>Frecuencia de Alertas</strong> - Análisis de patrones de alertas</li>
            </ul>`,
    'helpContent.timeRanges': `<p>La mayoría de los informes te permiten filtrar datos por rango de tiempo para analizar períodos específicos.</p>

            <h4>Rangos de Tiempo Disponibles:</h4>
            <ul>
              <li><strong>Últimas 24 Horas</strong> - Datos del día pasado</li>
              <li><strong>Últimos 7 Días</strong> - Vista semanal</li>
              <li><strong>Últimos 30 Días</strong> - Vista mensual</li>
              <li><strong>Últimos 90 Días</strong> - Vista trimestral</li>
              <li><strong>Rango Personalizado</strong> - Selecciona fechas de inicio y fin específicas</li>
            </ul>

            <div class="help-tip">
              <strong>Consejo:</strong> Usa rangos de fechas personalizados para analizar eventos o períodos específicos de interés.
            </div>`,
    'helpContent.chartsGraphs': `<p>Los informes incluyen varios tipos de gráficos y diagramas para visualizar tus datos de manera efectiva.</p>

            <h4>Tipos de Gráficos:</h4>
            <ul>
              <li><strong>Gráficos de Línea</strong> - Muestran tendencias a lo largo del tiempo</li>
              <li><strong>Gráficos de Barra</strong> - Comparan valores entre categorías</li>
              <li><strong>Gráficos de Área</strong> - Muestran valores acumulativos con degradados</li>
              <li><strong>Gráficos Circulares</strong> - Muestran proporciones y porcentajes</li>
            </ul>
            
            <h4>Interactuando con los Gráficos:</h4>
            <ul>
              <li>Pasa el mouse sobre los puntos de datos para ver valores exactos</li>
              <li>Haz clic en elementos de la leyenda para mostrar/ocultar series de datos</li>
              <li>Usa los controles de zoom si están disponibles</li>
              <li>Exporta gráficos como imágenes o PDF</li>
            </ul>
            
            `,
    'helpContent.exportingReports': `<p>Puedes exportar informes en varios formatos para compartir, analizar o documentar.</p>

            <h4>Formatos de Exportación:</h4>
            <ul>
              <li><strong>CSV</strong> - Exportar datos como valores separados por comas para análisis de hojas de cálculo</li>
              <li><strong>PDF</strong> - Exportar gráficos e informes como documentos PDF</li>
              <li><strong>Tabla de Clasificación</strong> - Exportar tablas de comparación</li>
            </ul>
            
            <h4>Qué se Exporta:</h4>
            <ul>
              <li>Datos actuales basados en los filtros seleccionados</li>
              <li>Gráficos y visualizaciones</li>
              <li>Estadísticas resumidas</li>
              <li>Marcas de tiempo y metadatos</li>
            </ul>

            <div class="help-tip">
              <strong>Consejo:</strong> Exporta informes regularmente para mantener registros y cumplimiento.
            </div>`,
    'helpContent.customReports': `<p>Para necesidades avanzadas de informes, se pueden crear paneles analíticos personalizados adaptados a tus requisitos específicos.</p>

            <h4>Características del Panel Personalizado:</h4>
            <ul>
              <li>Interfaces de visualización personalizadas</li>
              <li>Análisis avanzados con modelado predictivo</li>
              <li>Información en tiempo real con alertas instantáneas</li>
              <li>Métricas y KPIs personalizados</li>
            </ul>
            
            <p>Contacta a tu administrador del sistema o equipo de soporte para discutir requisitos de informes personalizados.</p>`,
    'helpContent.adminOverview': `<p>La página de Administración proporciona herramientas administrativas para gestionar dispositivos y configurar ajustes del sistema. Esta página requiere acceso de administrador.</p>

            <h4>Características de Administración:</h4>
            <ul>
              <li><strong>Gestión de Dispositivos</strong> - Crear, editar y eliminar dispositivos</li>
              <li><strong>Búsqueda y Filtrado de Dispositivos</strong> - Encontrar dispositivos por identificador, título o tipo de dispositivo</li>
              <li><strong>Configuración de Fusibles</strong> - Configurar fusibles para dispositivos FUSEBOARD_UDC</li>
              <li><strong>Configuración de Organización</strong> - Configurar parámetros de cálculo de KPI para toda la organización</li>
              <li><strong>Configuración de Sitio</strong> - Anular configuraciones de organización para sitios específicos</li>
            </ul>
            
            <div class="help-tip">
              <strong>Nota:</strong> Se requiere acceso de administrador para ver y usar esta página. Contacta a tu administrador del sistema si necesitas privilegios de administrador.
            </div>`,
    'helpContent.deviceManagement': `<p>Gestiona todos los dispositivos en tu sistema desde la página de Administración.</p>

            <h4>Ver Dispositivos:</h4>
            <ul>
              <li><strong>Tabla de Dispositivos</strong> - Ver todos los dispositivos con identificador, título, tipo de dispositivo y sitio</li>
              <li><strong>Búsqueda</strong> - Usa la barra de búsqueda para encontrar dispositivos por identificador o título</li>
              <li><strong>Filtro por Tipo</strong> - Filtrar dispositivos por tipo de dispositivo usando el menú desplegable</li>
            </ul>
            
            <h4>Crear un Dispositivo:</h4>
            <ol>
              <li>Haz clic en el botón "Agregar Dispositivo"</li>
              <li>Ingresa el identificador del dispositivo (requerido, no se puede cambiar después de la creación)</li>
              <li>Ingresa un título opcional</li>
              <li>Selecciona el tipo de dispositivo del menú desplegable</li>
              <li>Selecciona el sitio donde se encuentra el dispositivo</li>
              <li>Opcionalmente selecciona unidades asociadas con el dispositivo</li>
              <li>Para dispositivos FUSEBOARD_UDC y CS01_NB, configura al menos 2 fusibles (ver Configuración de Fusibles)</li>
              <li>Para dispositivos EWATTCH_SQUID, configura las asignaciones de pinzas (ver Configuración de Pinzas Squid)</li>
              <li>Haz clic en "Crear Dispositivo" para guardar</li>
            </ol>

            <h4>Editar un Dispositivo:</h4>
            <ul>
              <li>Haz clic en el icono de editar junto a cualquier dispositivo en la tabla</li>
              <li>Modifica el título, tipo de dispositivo, sitio o unidades</li>
              <li>Nota: El identificador no se puede cambiar después de la creación</li>
              <li>Para dispositivos FUSEBOARD_UDC y CS01_NB, actualiza las configuraciones de fusibles según sea necesario</li>
              <li>Para dispositivos EWATTCH_SQUID, actualiza las asignaciones de pinzas según sea necesario</li>
              <li>Haz clic en "Actualizar Dispositivo" para guardar los cambios</li>
            </ul>
            
            <h4>Eliminar un Dispositivo:</h4>
            <ul>
              <li>Haz clic en el icono de eliminar junto al dispositivo</li>
              <li>Confirma la eliminación en el cuadro de diálogo</li>
              <li><strong>Advertencia:</strong> Esta acción no se puede deshacer</li>
            </ul>
            
            <h4>Tipos de Dispositivos:</h4>
            <p>El sistema admite varios tipos de dispositivos, cada uno con requisitos de configuración específicos:</p>
            
            <h5>FUSEBOARD_UDC (Fuseboard UDC)</h5>
            <ul>
              <li><strong>Propósito:</strong> Monitorea circuitos de fuseboard con hasta 4 sensores de corriente</li>
              <li><strong>Configuración:</strong> Requiere configuración de fusibles para al menos 2 fusibles (ver sección Configuración de Fusibles)</li>
              <li><strong>Datos:</strong> Mide corriente (A) para cada fusible, calcula potencia (W) usando voltaje y factor de potencia configurados</li>
              <li><strong>Caso de Uso:</strong> Monitoreo de fuseboard principal, seguimiento de energía a nivel de circuito</li>
            </ul>
            
            <h5>CS01_NB (Sensor de Corriente CS-01 NB-IoT)</h5>
            <ul>
              <li><strong>Propósito:</strong> Sensor de corriente NB-IoT que admite hasta 4 canales de medición de corriente</li>
              <li><strong>Configuración:</strong> Requiere configuración de fusibles para al menos 2 canales (ver sección Configuración de Fusibles)</li>
              <li><strong>Datos:</strong> Mide corriente (A) para cada canal, calcula potencia (W) usando voltaje y factor de potencia configurados</li>
              <li><strong>Caso de Uso:</strong> Monitoreo remoto de corriente, seguimiento de energía a nivel de circuito en ubicaciones de difícil acceso</li>
              <li><strong>Características:</strong> Alimentado por batería, conectividad NB-IoT, bajo consumo de energía</li>
            </ul>
            
            <h5>EWATTCH_SQUID (Dispositivo de Pinzas Squid)</h5>
            <ul>
              <li><strong>Propósito:</strong> Dispositivo de monitoreo de corriente multi-pinzas que admite hasta 12 pinzas</li>
              <li><strong>Configuración:</strong> Requiere asignación de pinzas a unidades (ver Configuración de Pinzas Squid)</li>
              <li><strong>Datos:</strong> Mide corriente (A), potencia (W), energía (kWh), voltaje (V) y otros parámetros eléctricos</li>
              <li><strong>Caso de Uso:</strong> Monitoreo eléctrico integral, seguimiento de múltiples circuitos, gestión de energía</li>
              <li><strong>Características:</strong> Admite varios tipos de medición incluyendo energía activa, energía reactiva, potencia aparente, frecuencia</li>
            </ul>
            
            <h5>ELSYS_ELT (Sensor Ambiental Elsys)</h5>
            <ul>
              <li><strong>Propósito:</strong> Sensor de monitoreo ambiental</li>
              <li><strong>Configuración:</strong> Configuración básica del dispositivo (identificador, título, sitio)</li>
              <li><strong>Datos:</strong> Temperatura, humedad y otros parámetros ambientales</li>
              <li><strong>Caso de Uso:</strong> Monitoreo ambiental de edificios, monitoreo de sistemas HVAC</li>
            </ul>
            
            <h5>ELVACO_4130 (Medidor de Calor Elvaco)</h5>
            <ul>
              <li><strong>Propósito:</strong> Medidor de calor para sistemas de calefacción distrital</li>
              <li><strong>Configuración:</strong> Configuración básica del dispositivo (identificador, título, sitio)</li>
              <li><strong>Datos:</strong> Consumo de energía (kWh), caudal (m³/h), temperaturas de ida/retorno (°C), potencia (W)</li>
              <li><strong>Caso de Uso:</strong> Monitoreo de calefacción distrital, gestión de redes de calor, facturación de energía</li>
              <li><strong>Características:</strong> Admite protocolo M-Bus, múltiples períodos de tarifa, detección de banderas de error</li>
            </ul>
            
            <div class="help-tip">
              <strong>Consejo:</strong> Usa las funciones de búsqueda y filtro para encontrar rápidamente dispositivos específicos al gestionar grandes cantidades de dispositivos. Cada tipo de dispositivo tiene requisitos de configuración específicos: asegúrate de completarlos para un monitoreo preciso de datos.
            </div>`,
    'helpContent.fuseConfiguration': `<p>Los dispositivos FUSEBOARD_UDC y CS01_NB requieren configuración de fusibles para monitorear y calcular correctamente el uso de energía.</p>

            <h4>Requisitos de Configuración de Fusibles/Canales:</h4>
            <ul>
              <li><strong>Mínimo 2 Fusibles/Canales</strong> - Al menos los fusibles/canales 1 y 2 deben estar configurados</li>
              <li><strong>Fusibles/Canales 3 y 4</strong> - Opcional, se pueden dejar vacíos</li>
              <li><strong>Campos Requeridos</strong> - Nombre del dispositivo, categoría, voltaje y factor de potencia</li>
              <li><strong>Se Aplica A:</strong> Dispositivos FUSEBOARD_UDC (fusibles) y CS01_NB (canales)</li>
            </ul>
            
            <h4>Configurar un Fusible/Canal:</h4>
            <ol>
              <li>Ingresa el <strong>Nombre del Dispositivo</strong> (por ejemplo, "Suministro Principal", "Circuito A", "Electrodomésticos de Cocina")</li>
              <li>Selecciona la <strong>Categoría</strong>:
                <ul>
                  <li><strong>Suministro</strong> - Punto de entrada del suministro de energía principal</li>
                  <li><strong>Circuito</strong> - Circuito o rama individual</li>
                  <li><strong>Carga</strong> - Carga o electrodoméstico específico</li>
                  <li><strong>Otro</strong> - Otra categoría no listada arriba</li>
                </ul>
              </li>
              <li>Ingresa el <strong>Voltaje (V)</strong> - Típicamente 240V para sistemas del Reino Unido, 230V para sistemas de la UE</li>
              <li>Ingresa el <strong>Factor de Potencia</strong> - Un valor entre 0 y 1 (típicamente 0.9 para la mayoría de las cargas, 1.0 para cargas resistivas)</li>
            </ol>

            <h4>Notas Específicas del Dispositivo:</h4>
            <ul>
              <li><strong>FUSEBOARD_UDC:</strong> Cada fusible representa una posición física de fusible en el fuseboard. Configura los fusibles 1-4 según tu instalación.</li>
              <li><strong>CS01_NB:</strong> Cada canal representa una entrada de sensor de corriente. Configura los canales 1-4 según qué sensores estén conectados.</li>
            </ul>
            
            <h4>Notas Importantes:</h4>
            <ul>
              <li>Los valores de configuración de fusible/canal se leen durante la instalación y permanecen constantes</li>
              <li>Estos valores se usan para cálculos precisos de energía: Potencia (W) = Voltaje (V) × Corriente (A) × Factor de Potencia</li>
              <li>La configuración precisa asegura métricas de KPI correctas, cálculos de ahorro de costos y seguimiento de huella de carbono</li>
              <li>Puedes actualizar las configuraciones de fusible/canal después de crear el dispositivo</li>
              <li>Los fusibles/canales opcionales (3 y 4) se pueden eliminar borrando todos los campos</li>
            </ul>
            
            <div class="help-tip">
              <strong>Consejo:</strong> Asegúrate de que los valores de voltaje y factor de potencia sean precisos para cálculos correctos de uso de energía y métricas de ahorro de costos. Para instalaciones del Reino Unido, el voltaje estándar es 240V y el factor de potencia típico es 0.9.
            </div>`,
    'helpContent.organizationSettings': `<p>La configuración de la organización controla los cálculos de KPI para todos los sitios a menos que se anulen a nivel de sitio.</p>

            <h4>Configuración de KPI de Organización:</h4>
            
            <h5>Tasa Unitaria (£ por kWh):</h5>
            <ul>
              <li>El costo por kilovatio-hora de electricidad</li>
              <li>Se usa para calcular el ahorro de costos</li>
              <li>Ejemplo: 0.30 significa £0.30 por kWh</li>
              <li>Campo requerido</li>
            </ul>
            
            <h5>Minutos de Intervalo:</h5>
            <ul>
              <li>Con qué frecuencia llegan las lecturas del dispositivo (en minutos)</li>
              <li>Se usa para cálculos precisos basados en el tiempo</li>
              <li>Ejemplo: 5 significa que las lecturas llegan cada 5 minutos</li>
              <li>Campo requerido, debe ser al menos 1</li>
            </ul>
            
            <h5>Factor de Carbono (kg CO₂e por kWh):</h5>
            <ul>
              <li>Factor de emisión de carbono para el cálculo de ahorro de carbono</li>
              <li>Se usa para calcular las métricas de carbono ahorrado</li>
              <li>Ejemplo: 0.233 significa 0.233 kg CO₂e por kWh</li>
              <li>Campo requerido</li>
            </ul>
            
            <h4>Guardar Configuración de Organización:</h4>
            <ol>
              <li>Haz clic en el botón "Configuración" en el encabezado de la página de Administración</li>
              <li>Navega a la sección "Configuración de KPI de la Organización"</li>
              <li>Ingresa o actualiza los valores para tasa unitaria, minutos de intervalo y factor de carbono</li>
              <li>Haz clic en "Guardar Configuración de Organización"</li>
              <li>La configuración se aplica a todos los sitios a menos que se anule</li>
            </ol>

            <div class="help-tip">
              <strong>Consejo:</strong> Actualiza esta configuración cuando cambien las tasas de electricidad o los factores de carbono para asegurar cálculos precisos de KPI.
            </div>`,
    'helpContent.siteSettings': `<p>La configuración del sitio te permite anular la configuración de la organización para sitios específicos.</p>

            <h4>Configuración de KPI de Sitio:</h4>
            <p>Puedes anular la configuración de la organización para sitios individuales:</p>
            <ul>
              <li><strong>Anulación de Tasa Unitaria</strong> - Tasa de electricidad específica del sitio</li>
              <li><strong>Anulación de Minutos de Intervalo</strong> - Intervalo de lectura específico del sitio</li>
              <li><strong>Anulación de Factor de Carbono</strong> - Factor de emisión de carbono específico del sitio</li>
            </ul>
            
            <h4>Configurar Configuración de Sitio:</h4>
            <ol>
              <li>Haz clic en el botón "Configuración" en el encabezado de la página de Administración</li>
              <li>Navega a la sección "Configuración de KPI de Sitio"</li>
              <li>Selecciona un sitio del menú desplegable</li>
              <li>Ingresa valores de anulación (déjalos vacíos para usar los valores predeterminados de la organización)</li>
              <li>Ver los valores efectivos actuales en el texto pequeño debajo de cada campo</li>
              <li>Haz clic en "Guardar Configuración de Sitio"</li>
            </ol>
            
            <h4>Entendiendo las Anulaciones:</h4>
            <ul>
              <li><strong>Anulación Vacía</strong> - El sitio usa el valor predeterminado de la organización</li>
              <li><strong>Anulación Establecida</strong> - El sitio usa el valor de anulación en lugar del predeterminado de la organización</li>
              <li><strong>Visualización de Valor Actual</strong> - Muestra el valor efectivo que se está usando (anulación o predeterminado)</li>
            </ul>

            <div class="help-tip">
              <strong>Consejo:</strong> Usa anulaciones de sitio cuando un sitio tenga diferentes tasas de electricidad o requiera diferentes parámetros de cálculo que el predeterminado de la organización.
            </div>`,
    'helpContent.chatbotOverview': `<p>El Asistente de Chat es una herramienta de ayuda impulsada por IA disponible en la página de Ayuda. Puede responder preguntas sobre la aplicación y guiarte a documentación relevante.</p>

            <h4>Acceder al Asistente de Chat:</h4>
            <ul>
              <li>Busca el botón de chat en la esquina inferior derecha de la página de Ayuda</li>
              <li>Haz clic en el icono de chat para abrir la ventana de chat</li>
              <li>El asistente te saluda con un mensaje de bienvenida</li>
            </ul>
            
            <h4>Hacer Preguntas:</h4>
            <ul>
              <li>Escribe tu pregunta en el campo de entrada en la parte inferior</li>
              <li>Presiona Enter o haz clic en el botón de enviar</li>
              <li>El asistente proporcionará una respuesta basada en tu pregunta</li>
              <li>Puedes hacer preguntas de seguimiento en la misma conversación</li>
            </ul>
            
            <h4>Qué Puedes Preguntar:</h4>
            <ul>
              <li><strong>Navegación</strong> - "¿Cómo navego a las alertas?"</li>
              <li><strong>Características</strong> - "¿Qué son los KPIs?" o "¿Cómo exporto informes?"</li>
              <li><strong>Alertas</strong> - "¿Cómo reconozco una alerta?"</li>
              <li><strong>Configuración</strong> - "¿Cómo cambio el tema?"</li>
              <li><strong>Ayuda General</strong> - "¿Cómo filtro sitios?"</li>
            </ul>

            <h4>Características del Chat:</h4>
            <ul>
              <li><strong>Historial de Mensajes</strong> - Ver el historial de tu conversación en la ventana de chat</li>
              <li><strong>Marcas de Tiempo</strong> - Cada mensaje muestra cuándo se envió</li>
              <li><strong>Indicador de Escritura</strong> - Ver cuando el asistente está escribiendo una respuesta</li>
              <li><strong>Cerrar/Reabrir</strong> - Cierra el chat y vuelve a abrirlo en cualquier momento</li>
            </ul>
            
            <div class="help-tip">
              <strong>Consejo:</strong> El asistente de chat puede ayudarte a encontrar información rápidamente sin navegar por todas las secciones de ayuda. Intenta hacer preguntas específicas para obtener mejores resultados.
            </div>`,
    'helpContent.bestPractices': `<h4>Uso del Panel:</h4>
            <ul>
              <li>Revisa el panel de Resumen regularmente para obtener información rápida</li>
              <li>Configura configuraciones de alerta de manera proactiva para detectar problemas temprano</li>
              <li>Usa el mapa del sitio para obtener una vista general visual de todas las ubicaciones</li>
              <li>Revisa los informes periódicamente para identificar tendencias</li>
            </ul>
            
            <h4>Gestión de Alertas:</h4>
            <ul>
              <li>Reconoce las alertas rápidamente para rastrear tu estado de revisión</li>
              <li>Resuelve las alertas después de abordar el problema subyacente</li>
              <li>Usa filtros de severidad para enfocarte en problemas críticos primero</li>
              <li>Configura monitoreo fuera de línea para dispositivos críticos</li>
            </ul>
            
            <h4>Análisis de Datos:</h4>
            <ul>
              <li>Compara datos en diferentes rangos de tiempo para identificar patrones</li>
              <li>Exporta informes para análisis fuera de línea y mantenimiento de registros</li>
              <li>Usa notas del sitio para documentar hallazgos importantes</li>
              <li>Revisa tendencias históricas para predecir necesidades futuras</li>
            </ul>`,
    'helpContent.commonIssues': `<h4>Mapa No Carga:</h4>
            <ul>
              <li>Verifica tu conexión a internet</li>
              <li>Intenta actualizar la página</li>
              <li>Cambia los estilos del mapa - a veces un estilo diferente carga más rápido</li>
              <li>Limpia la caché de tu navegador si los problemas persisten</li>
            </ul>
            
            <h4>Gráficos No Se Muestran:</h4>
            <ul>
              <li>Asegúrate de tener datos para el rango de tiempo seleccionado</li>
              <li>Intenta seleccionar un rango de tiempo diferente</li>
              <li>Verifica si los filtros son demasiado restrictivos</li>
              <li>Actualiza la página para recargar los datos</li>
            </ul>
            
            <h4>Alertas No Aparecen:</h4>
            <ul>
              <li>Verifica la configuración del filtro - podrías tener filtros aplicados</li>
              <li>Verifica que las configuraciones de alerta estén habilitadas</li>
              <li>Verifica si estás viendo el estado correcto (Activo vs Resuelto)</li>
              <li>Asegúrate de que los dispositivos estén en línea y reportando datos</li>
            </ul>
            
            <h4>Problemas de Rendimiento:</h4>
            <ul>
              <li>Cierra las pestañas innecesarias del navegador</li>
              <li>Prueba un navegador diferente si el actual es lento</li>
              <li>Reduce el rango de tiempo para informes con grandes conjuntos de datos</li>
              <li>Contacta al soporte si los problemas persisten</li>
            </ul>`,
    'helpContent.keyboardShortcuts': `<p>Si bien la aplicación se maneja principalmente con el mouse, aquí hay algunos atajos de teclado útiles:</p>
            
            <ul>
              <li><strong>Ctrl/Cmd + F</strong> - Enfocar en campos de búsqueda (cuando estén disponibles)</li>
              <li><strong>Esc</strong> - Cerrar modales y ventanas emergentes</li>
              <li><strong>Tab</strong> - Navegar entre campos de formulario</li>
              <li><strong>Enter</strong> - Enviar formularios o activar botones</li>
            </ul>
            
            <div class="help-tip">
              <strong>Consejo:</strong> La mayoría de las acciones se pueden realizar con clics del mouse. Los atajos de teclado se proporcionan como una conveniencia.
            </div>`,
    'helpContent.gettingHelp': `<h4>Recursos en la Aplicación:</h4>
            <ul>
              <li>Esta página de ayuda - guía completa de todas las características</li>
              <li>Información sobre herramientas - pasa el mouse sobre botones e iconos para información rápida</li>
              <li>Ayuda contextual - algunas páginas tienen texto de ayuda integrado</li>
            </ul>
            
            <h4>Canales de Soporte:</h4>
            <ul>
              <li>Contacta a tu administrador del sistema para problemas de cuenta y acceso</li>
              <li>Comunícate con el soporte técnico para errores o problemas técnicos</li>
              <li>Verifica las notificaciones del sistema para actualizaciones y anuncios importantes</li>
            </ul>
            
            <div class="help-tip">
              <strong>Consejo:</strong> Al reportar problemas, incluye capturas de pantalla y detalles sobre lo que estabas tratando de hacer cuando ocurrió el problema.
            </div>`,
  },
  zh: {
    // Navigation
    overview: '概览',
    sites: '站点',
    compliance: '合规',
    navGroupMain: '主要',
    navGroupAnalytics: '分析',
    navGroupSystem: '系统',

    // Dashboard
    totalUsage: '总使用量',
    alerts: '警报',
    powerUsageTrend: '用电趋势',
    active: '活跃',
    
    // Common
    search: '搜索',
    backToSites: '返回站点',
    expandAll: '展开全部',
    collapseAll: '收起全部',
    online: '在线',
    offline: '离线',
    refreshLiveData: '刷新实时数据',
    enterFullscreen: '进入全屏',
    exitFullscreen: '退出全屏',
    live: '实时',
    graphic: '图表',
    historic: '历史',
    fuseboardDevice: '配电板设备',
    generatingAiInsights: '生成AI洞察',
    analysingReadings: '正在分析此设备的最新读数...',
    mouldRiskZoneMap: '霉菌风险区域图',
    property: '属性',
    withMouldRisk: '有霉菌风险',
    high: '高',
    medium: '中',
    low: '低',
    changeLanguage: '更改语言',
    searchPlaceholder: '搜索…',
    logout: '退出登录',
    
    // Loading Messages
    loadingFuseboardHierarchy: '加载配电盘层次结构...',
    loadingEnvironmentalDashboard: '加载环境仪表板...',
    loadingHistoricalData: '加载历史数据...',
    loadingDashboardOverview: '加载仪表板概览...',
    loadingReports: '加载报告...',
    loadingConfigurations: '加载配置...',
    loadingOfflineDevices: '加载离线设备...',
    noOfflineDevices: '一切正常',
    allDevicesReporting: '所有设备运行正常',

    // Buttons & Actions
    exportCsv: '导出CSV',
    exportPdf: '导出PDF',
    exportLeagueTable: '导出排行榜',
    switchTo2D: '切换到2D视图',
    switchTo3D: '切换到3D视图（显示3D建筑）',
    changeMapStyle: '更改地图样式',
    collapseAllTreeNodes: '收起所有树节点',
    expandAllTreeNodes: '展开所有树节点',
    
    // Search Placeholders
    searchAlerts: '搜索警报...',
    searchConfigurations: '搜索配置...',
    searchByTime: '按时间搜索（例如：14:30，2024-01-15，1月15日）...',
    
    // Export & Reports
    fuseboardChartsExport: '配电盘图表导出',
    exportedFromSmartInstallers: '从智能安装商洞察导出',
    generatedOn: '生成于：',
    exportLiveDataToCsv: '导出实时数据到CSV',
    exportChartsToPdf: '导出图表到PDF',
    exportHistoricDataToCsv: '导出历史数据到CSV',
    
    // Form Labels
    to: '到',
    from: '从',
    
    // Time
    vsLastMonth: '与上月相比',
    vsLastWeek: '与上周相比',
    
    // Admin Page
    admin: '管理',
    help: '帮助',
    settings: '设置',
    addDevice: '添加设备',
    editDevice: '编辑设备',
    createDevice: '创建设备',
    updateDevice: '更新设备',
    deleteDevice: '删除设备',
    searchDevices: '搜索设备...',
    allDeviceTypes: '所有设备类型',
    noDevicesFound: '未找到设备',
    tryAdjustingSearch: '尝试调整搜索或筛选条件',
    identifier: '标识符',
    title: '标题',
    actions: '操作',
    units: '单位',
    unitsOptional: '单位（可选）',
    noUnitsAvailable: '此站点没有可用单位',
    unassignedDevices: '未分配设备',
    siteLevel: '站点级别',
    notAssignedToUnit: '未分配到任何单位',
    unitsSelected: '个单位已选择',
    cancel: '取消',
    save: '保存',
    delete: '删除',
    areYouSure: '您确定吗？',
    cannotBeUndone: '此操作无法撤销。',
    
    // Fuse Configuration
    fuseConfigurationRequired: '保险丝配置（FUSEBOARD_UDC 必需）',
    configureAtLeast2Fuses: '配置至少 2 个保险丝，包括电压、功率因数、设备名称和类别。保险丝 3 和 4 是可选的。',
    fuse: '保险丝',
    deviceName: '设备名称',
    category: '类别',
    voltage: '电压 (V)',
    powerFactor: '功率因数',
    supply: '供电',
    circuit: '电路',
    load: '负载',
    other: '其他',
    atLeast2FusesRequired: 'FUSEBOARD_UDC 设备必须配置至少 2 个保险丝',
    
    // Organization & Site Settings
    organizationKpiConfiguration: '组织 KPI 配置',
    siteKpiConfiguration: '站点 KPI 配置',
    unitRate: '单位费率（£/kWh）',
    intervalMinutes: '间隔分钟数',
    carbonFactor: '碳因子（kg CO₂e/kWh）',
    howOftenReadingsArrive: '设备读数到达频率（分钟）',
    carbonEmissionFactor: '用于计算碳节约的碳排放因子',
    appliesToAllSites: '这些设置适用于所有站点，除非在站点级别被覆盖。',
    overrideOrganizationSettings: '覆盖特定站点的组织设置。留空以使用组织默认值。',
    selectASite: '-- 选择一个站点 --',
    unitRateOverride: '单位费率覆盖（£/kWh）',
    intervalMinutesOverride: '间隔分钟数覆盖',
    carbonFactorOverride: '碳因子覆盖（kg CO₂e/kWh）',
    leaveEmptyForDefault: '留空以使用组织默认值',
    current: '当前',
    usingOrgDefault: '使用组织默认值',
    saveOrganizationSettings: '保存组织设置',
    saveSiteSettings: '保存站点设置',
    
    // Alerts
    recentAlerts: '最近警报',
    recentAlertsHub: '最近警报中心',
    noAlertsFound: '未找到警报',
    activeAlerts: '警报',
    configurations: '配置',
    offlineMonitoring: '离线监控',
    acknowledged: '已确认',
    resolved: '已解决',
    critical: '严重',
    warning: '警告',
    info: '信息',
    all: '全部',
    severity: '严重程度',
    status: '状态',
    acknowledge: '确认',
    resolve: '解决',
    view: '查看',
    viewDetails: '查看详情',
    createAlertRule: '创建警报规则',
    newMonitor: '新监控器',
    
    // Common UI
    refresh: '刷新',
    close: '关闭',
    open: '打开',
    yes: '是',
    no: '否',
    confirm: '确认',
    required: '必需',
    optional: '可选',
    loading: '加载中...',
    error: '错误',
    success: '成功',
    failed: '失败',
    saved: '已保存',
    updated: '已更新',
    deleted: '已删除',
    created: '已创建',
    
    // Navigation
    aiHub: 'AI 中心',
    
    // Pagination
    itemsPerPage: '每页项目数',
    previous: '上一页',
    next: '下一页',
    page: '页',
    showing: '显示',
    item: '项',
    items: '项',
    nextStep: '下一步',
    previousAlert: '上一个警报',
    nextAlert: '下一个警报',
    
    // Map
    gridVsCircuitVoltage: '电网与电路电压',
    
    // Reports
    reports: '报告',
    timeRange: '时间范围',
    last24Hours: '最近 24 小时',
    last7Days: '最近 7 天',
    last30Days: '最近 30 天',
    last90Days: '最近 90 天',
    customRange: '自定义范围',
    
    // Chat Assistant
    helpAssistant: '帮助助手',
    askMeAnything: '问我任何关于 {companyName} Insights 的问题',
    askAQuestion: '提问...',
    send: '发送',
    closeChat: '关闭聊天',
    
    // Additional Alert & UI Strings
    viewAll: '查看全部',
    viewAllAlerts: '查看所有警报',
    refreshAlerts: '刷新警报',
    alertOverview: '警报概览',
    unread: '未读',
    allSeverity: '所有严重程度',
    allStatus: '所有状态',
    markAsUnseen: '标记为未查看',
    allClear: '全部清除！',
    noAlertsMatchFilters: '没有警报匹配您当前的筛选条件',
    loadingAlerts: '加载警报中...',
    exportAlertsToCsv: '导出警报到 CSV',
    manage: '管理',
    paused: '已暂停',
    drafts: '草稿',
    editMonitor: '编辑监控器',
    deleteMonitor: '删除监控器',
    noAlertsToExport: '没有要导出的警报',
    total: '总计',
    activeFuses: '活动保险丝',
    overloadAlert: '过载警报',
    noAlerts: '无警报',
    smartInstallers: '智能安装商',
    edit: '编辑',
    energySaved: '节省能源',
    totalEnergy: '总能源',
    totalEnergySaved: '总节省能源',
    voltageTrend: '电压趋势',
    environmentalTrend: '环境趋势',
    cumulativeTotal: '累计总计',
    
    // Reports
    leagueTable: '排行榜',
    aiInsights: 'AI洞察',
    savingsCarbon: '节省与碳',
    customisedReporting: '定制报告',
    recommendedActions: '建议操作',
    recommendedAction: '建议操作',
    noInsightsAvailable: '暂无洞察',
    noInsightsDescription: '选择设备以查看AI生成的洞察',
    sensorHealth: '传感器健康',

    // Site Manager
    siteNotes: '站点管理',
    dashboard: '仪表板',
    
    // Help Page - UI
    searchHelpTopics: '搜索帮助主题...',
    searchHelp: '搜索帮助...',
    tableOfContents: '目录',
    noResultsFound: '未找到结果',
    tryAdjustingSearchTerms: '尝试调整搜索词或浏览目录。',
    helpCenter: '帮助中心',
    helpHeroSubtitle: '充分利用 {companyName} Monitoring Platform 所需的一切',

    // Quick Start Guide
    quickStartGuide: '快速入门指南',
    quickStartSubtitle: '初次使用 {companyName} Monitoring Platform？从这里开始快速上手。',
    quickStartLogin: '登录',
    quickStartLoginDesc: '使用 Auth0 身份验证访问平台',
    quickStartDashboard: '仪表板概览',
    quickStartDashboardDesc: '了解 KPI、警报和站点监控',
    quickStartFeatures: '主要功能',
    quickStartFeaturesDesc: '探索站点、警报和报告',
    stillNeedHelp: '仍然需要帮助？',
    stillNeedHelpDesc: '我们的团队随时为您提供帮助。使用聊天助手获取即时支持。',
    openChatAssistant: '打开聊天助手',

    // Help Page - Section Titles
    gettingStarted: '入门',
    overviewDashboard: '概览仪表板',
    sitesManagement: '站点管理',
    alertsManagement: '警报管理',
    reportsAnalytics: '报告与分析',
    adminSettings: '管理与设置',
    chatAssistant: '聊天助手',
    tipsTroubleshooting: '提示与故障排除',
    
    // Help Page - Subsection Titles
    loggingIn: '登录',
    navigationOverview: '导航概览',
    settingsPreferences: '设置与偏好',
    understandingKpiCards: '了解KPI卡片',
    recentAlertsSection: '最近警报部分',
    siteMap: '站点地图',
    sitesPageOverview: '站点页面概览',
    siteDashboards: '站点仪表板',
    siteNotesAnnotations: '站点管理与注释',
    alertsPageOverview: '警报页面概览',
    viewingFilteringAlerts: '查看和筛选警报',
    alertActions: '警报操作',
    alertConfigurations: '警报配置',
    offlineDeviceMonitoring: '离线设备监控',
    reportsPageOverview: '报告页面概览',
    selectingTimeRanges: '选择时间范围',
    understandingChartsGraphs: '了解图表和图形',
    exportingReports: '导出报告',
    customReportingServices: '自定义报告服务',
    adminPageOverview: '管理页面概览',
    deviceManagement: '设备管理',
    fuseConfiguration: '保险丝配置 (FUSEBOARD_UDC)',
    organizationSettings: '组织设置',
    siteSettings: '站点设置',
    usingChatAssistant: '使用聊天助手',
    bestPractices: '最佳实践',
    commonIssuesSolutions: '常见问题与解决方案',
    keyboardShortcuts: '键盘快捷键',
    gettingAdditionalHelp: '获取额外帮助',
    
    // AI Hub - Section Titles
    aiHubOverview: 'AI Hub概览',
    aiInsightsWizard: 'AI洞察向导',
    aiAssistant: 'AI助手',
    smartRecommendations: '智能推荐',
    savedInsights: '已保存的洞察',
    insightHistory: '洞察历史',
    realTimeMonitoring: '实时监控',
    predictiveAnalytics: '预测分析',
    dynamicTelemetry: '动态遥测',
    aiReports: 'AI报告',
    siteComparison: '站点比较',
    anomalyDetection: '异常检测',
    carbonAnalytics: '碳分析',
    aiInsightsWizardDesc: '通过分步指导生成自定义AI洞察',
    aiAssistantDesc: '与AI聊天，即时获取有关您数据的答案',
    smartRecommendationsDesc: 'AI驱动的建议，优化您的运营',
    savedInsightsDesc: '访问您已保存和收藏的洞察',
    insightHistoryDesc: '查看过去的洞察和分析报告',
    realTimeMonitoringDesc: 'AI驱动的实时监控和警报',
    predictiveAnalyticsDesc: '预测趋势和未来结果',
    dynamicTelemetryDesc: '根据实时环境条件动态控制传感器上报频率',
    aiReportsDesc: '生成全面的AI分析报告',
    siteComparisonDesc: '使用AI比较多个站点的性能',
    anomalyDetectionDesc: '检测异常模式和潜在问题',
    carbonAnalyticsDesc: '跟踪和优化您的碳足迹',
    backToHub: '返回中心',
    startConversation: '开始对话',
    askMeAnythingAbout: '问我任何关于您的站点、设备、能源消耗或洞察的问题',
    loadingReportData: '正在加载报告数据...',
    generatingComprehensiveReport: '正在生成综合分析报告',
    comprehensiveAnalysisReport: '综合分析报告',
    downloadReport: '下载报告',
    analysisOfEnvironmentalConditions: '分析所有监控站点的环境条件和霉菌生长风险',
    siteCostSavings: '站点成本节约',
    topSitesByCostSavings: '成本节约前 {count} 个站点',
    costSavingsBySite: '按站点成本节约',
    costSavingsBreakdown: '此位置的成本节约明细',
    leadingLocationsContributing: '贡献总节约的主要位置',
    siteBySiteBreakdown: '逐站点明细',
    rank: '排名',
    siteName: '站点名称',
    costSavings: '成本节约',
    carbonSaved: '已节约碳',
    impactScore: '影响分数',
    selectSiteAndDevices: '选择站点和设备',
    chooseSiteAndSelectDevices: '选择一个站点并选择要比较的设备',
    siteSelection: '站点选择',
    siteBySiteRiskAnalysis: '逐站点风险分析',
    totalDevices: '设备总数',
    avgDaysToMould: '平均到霉菌天数',
    avgTemp: '平均温度',
    avgHumidity: '平均湿度',
    riskPercent: '风险 %',
    riskDistributionAcrossAllSites: '所有站点的风险分布',
    riskComparisonBySite: '按站点风险比较',
    daysToMouldByDevice: '按设备到霉菌天数（前20）',
    atRisk: '处于风险',
    temperature: '温度',
    humidity: '湿度',
    riskLevel: '风险级别',
    lastReading: '最后读数',
    noReportsAvailable: '没有可用报告',
    noReportsEnabled: '您尚未启用任何报告。转到设置以启用您想要查看的报告。',
    selectSitesToCompare: '选择要比较的站点',
    site: '站点',
    savingsCarbonImpactReport: '节约与碳影响报告',
    analysisOfCostSavings: '成本节约、能源效率和环境影响分析',
    totalCostSavings: '总成本节约',
    environmentalImpact: '环境影响',
    energyCarbonImpact: '能源与碳影响',
    energyCarbonCorrelation: '能源与碳关联',
    energyCarbonData: '能源与碳数据',
    energySavedCarbonReductionLocation: '此位置的能源节约和碳减排',
    relationshipEnergyCarbonReduction: '所有站点能源节约与碳减排之间的关系',
    carbonKg: '碳 (kg)',
    energyKwh: '能源 (kWh)',
    yourEnergyOptimizationEfforts: '您的能源优化努力已节省',
    ofCO2: '的 CO₂',
    equivalentToPlanting: '相当于种植',
    trees: '棵树',
    orRemoving: '或移除',
    carsFromRoadForYear: '辆汽车一年',
    financialImpact: '财务影响',
    totalCostSavingsOf: '总成本节约',
    demonstrateSignificantFinancialBenefits: '展示了主动能源管理和优化的显著财务效益',
    energyEfficiency: '能源效率',
    bySaving: '通过节约',
    youveReducedEnergyConsumption: '您已减少能源消耗，相当于为',
    averageHomesAnnually: '个普通家庭每年供电',
    bespokeReportingServices: '定制报告服务',
    weCraftCustomDashboards: '我们制作定制分析仪表板，根据您的特定监控和报告需求量身定制',
    customDashboards: '定制仪表板',
    tailoredVisualizationInterfaces: '围绕您独特的数据需求设计的定制可视化界面',
    advancedAnalytics: '高级分析',
    deepInsightsPredictive: '具有预测建模和趋势分析功能的深度洞察',
    realTimeInsights: '实时洞察',
    liveDataProcessing: '实时数据处理，具有即时警报和监控',
    systemIntegration: '系统集成',
    seamlessConnection: '与您现有的基础设施和工具无缝连接',
    readyToTransformData: '准备好将您的数据转化为可操作的洞察？',
    getStarted: '开始',
    contactDevelopmentTeam: '联系我们的开发团队讨论您的需求',
    device: '设备',
    selectDeviceType: '选择设备类型',
    chooseDeviceTypeToAnalyse: '选择要分析的设备类型',
    deviceType: '设备类型',
    devicesSelected: '已选设备',
    
    // Sites Page
    allTypes: '所有类型',
    temporaryPlantRoom: '临时机房',
    heatNetwork: '热网',
    fuseboardManagement: '配电盘管理',
    environmentalMonitoring: '环境监测',
    daysToMould: '到霉菌的天数',
    mouldRisk: '霉菌风险',
    mouldRiskScore: '霉菌风险评分',
    mouldRiskCalculation: '霉菌风险计算',
    mouldRiskActive: '霉菌风险活跃',
    mouldRiskAssessment: '霉菌风险评估',
    environmentalSensor: '环境传感器',
    environmentalSensors: '环境传感器',
    averageDaysToMould: '平均到霉菌天数',
    averageDaysToCritical: '平均关键天数',
    averageTemperature: '平均温度',
    averageHumidity: '平均湿度',
    insufficientData: '—',
    devicesAtRisk: '有风险的设备',
    mouldRiskStatus: '霉菌风险状态',
    propertiesAtRisk: '有风险的设备',
    siteAverageTrend: '站点平均趋势',
    siteAverage: '站点平均',
    risk: '风险',
    criticalRisk: '严重风险',
    highRisk: '高风险',
    mediumRisk: '中等风险',
    lowRisk: '低风险',
    noRisk: '无风险',
    mouldRiskReport: '霉菌风险报告',
    mouldRiskAssessmentReport: '霉菌风险评估报告',
    deviceMouldRiskAssessment: '设备霉菌风险评估',
    days: '天',
    noEnvironmentalSensorsFound: '未找到环境传感器',
    attentionRequired: '需要注意',
    allSystemsNormal: '所有系统正常',
    noSiteDataAvailable: '无站点数据可用',
    noSavingsDataAvailable: '无节省数据可用',
    noComparisonDataAvailable: '无比较数据可用',
    noSiteSavingsDataAvailable: '无站点节省数据可用',
    systemStatus: '系统状态',
    errorLoadingInsights: '加载洞察时出错',
    highPriority: '高优先级',
    mediumPriority: '中优先级',
    lowPriority: '低优先级',
    systemHealth: '系统健康',
    popular: '热门',
    new: '新',
    beta: '抢先体验',
    selectSite: '选择站点',
    selectUnit: '选择单元',
    selectDevice: '选择设备',
    aiCapabilities: 'AI能力',
    generate: '生成',
    predictiveMaintenance: '预测性维护',
    energyOptimisation: '能源优化',
    costAnalysis: '成本分析',
    forecasting: '预测',
    carbonImpact: '碳影响',
    searchSitesByNameOrType: '按名称或类型搜索站点...',
    searchSites: '搜索站点…',
    clearFilters: '清除筛选',
    properties: '属性',
    devices: '设备',
    enterDashboard: '进入仪表板',
    of: '的',
    matching: '匹配',
    noSitesFound: '未找到站点',
    noSitesMatchCriteria: '没有站点匹配您当前的搜索和筛选条件。',
    tryAdjustingSearchOrFilters: '尝试调整搜索词或筛选器。',
    noSitesAvailable: '没有可用站点',
    noSitesConfigured: '尚未为您的组织配置任何站点。',
    unknownDashboardType: '未知仪表板类型',
    dashboardTypeNotSupported: '仪表板类型"{type}"不受支持。',
    unknown: '未知',
    
    // Help Page Content
    'helpContent.login': `<p>要访问 {companyName} Insights，您需要使用凭据登录。</p>

            <h4>登录步骤：</h4>
            <ol>
              <li>在用户名字段中输入您的用户名</li>
              <li>在密码字段中输入您的密码</li>
              <li>点击"登录"按钮</li>
            </ol>
            
            <div class="help-tip">
              <strong>提示：</strong>如果您忘记了密码，请联系系统管理员获取帮助。
            </div>`,
    'helpContent.navigation': `<p>主导航位于左侧边栏。您可以访问应用程序的不同部分：</p>

            <h4>主要部分：</h4>
            <ul>
              <li><strong>概览</strong> - 包含KPI、警报和站点地图的仪表板</li>
              <li><strong>站点</strong> - 查看和管理所有站点</li>
              <li><strong>警报</strong> - 监控和管理系统警报</li>
              <li><strong>报告</strong> - 生成和查看报告</li>
              <li><strong>管理</strong> - 设备管理和系统配置（需要管理员访问）</li>
              <li><strong>帮助</strong> - 完整的帮助文档和聊天助手</li>
            </ul>
            
            `,
    'helpContent.settings': `<p>您可以通过各种设置自定义您的体验：</p>
            
            <h4>主题设置：</h4>
            <ul>
              <li>使用右上角的主题切换器在浅色和深色模式之间切换</li>
              <li>您的偏好会自动保存</li>
            </ul>
            
            <h4>语言设置：</h4>
            <ul>
              <li>使用语言选择器更改界面语言</li>
              <li>可用语言显示在下拉菜单中</li>
            </ul>
            
            `,
    'helpContent.kpiCards': `<p>概览仪表板显示关键绩效指标（KPI），为您提供系统性能的快速洞察。</p>

            <h4>可用KPI：</h4>
            <ul>
              <li><strong>保存的碳</strong> - 总节省的碳排放量，带有趋势指标</li>
              <li><strong>成本节约</strong> - 实现的财务节约，与前期相比</li>
              <li><strong>总使用量</strong> - 总体能耗指标</li>
              <li><strong>电网与电路电压</strong> - 电压监控和状态</li>
            </ul>
            
            <h4>悬停效果：</h4>
            <p>将鼠标悬停在任何KPI卡片上以查看增强的视觉反馈。卡片将以彩色边框和阴影突出显示。</p>
            
            `,
    'helpContent.recentAlerts': `<p>最近警报部分显示您系统的最新警报，帮助您了解任何问题或重要事件。</p>

            <h4>警报信息：</h4>
            <ul>
              <li><strong>严重性级别：</strong>
                <ul>
                  <li><span class="badge-critical">严重</span> - 需要立即关注</li>
                  <li><span class="badge-warning">警告</span> - 重要但不紧急</li>
                  <li><span class="badge-info">信息</span> - 信息性消息</li>
                </ul>
              </li>
              <li><strong>警报消息</strong> - 触发警报的描述</li>
              <li><strong>时间戳</strong> - 警报生成的时间</li>
            </ul>
            
            <h4>理解警报列表：</h4>
            <p>每个警报显示时左侧有彩色严重性指示器、严重性徽章、警报消息和时间戳。样式与警报页面一致。</p>
            
            <div class="help-tip">
              <strong>提示：</strong>点击最近警报部分中的警报以查看更多详细信息或导航到完整的警报页面。
            </div>`,
    'helpContent.siteMap': `<p>站点地图在交互式地图上提供所有站点和网关的可视化表示。</p>

            <h4>地图功能：</h4>
            <ul>
              <li><strong>网关标记</strong> - 绿色标记表示在线网关，红色标记表示离线网关</li>
              <li><strong>保险丝板位置</strong> - 闪电图标显示保险丝板监控位置</li>
              <li><strong>地标突出显示</strong> - 重要建筑物被突出显示（Guildhall, Old Bailey, Barbican, Mansion House）</li>
              <li><strong>3D建筑物</strong> - 切换3D视图以三维方式查看建筑物</li>
            </ul>
            
            <h4>地图控件：</h4>
            <ul>
              <li><strong>3D切换</strong> - 在2D和3D地图视图之间切换</li>
              <li><strong>样式选择器</strong> - 更改地图样式（街道、卫星、户外、导航、最小）</li>
              <li><strong>全屏</strong> - 将地图扩展到全屏以获得更好的视图</li>
            </ul>

            <h4>与标记交互：</h4>
            <ul>
              <li>点击任何标记以查看带有详细信息的弹出窗口</li>
              <li>网关标记显示状态、站点名称和坐标</li>
              <li>保险丝板标记显示位置和监控状态</li>
            </ul>
            
            <div class="help-tip">
              <strong>提示：</strong>使用全屏按钮在分析地图上的多个站点时获得更好的视图。
            </div>`,
    'helpContent.sitesOverview': `<p>站点页面允许您查看和管理所有站点。您可以搜索、过滤并访问每个站点的详细仪表板。</p>

            <h4>查看站点：</h4>
            <ul>
              <li><strong>列表视图</strong> - 以列表格式查看站点，包含关键信息</li>
              <li><strong>网格视图</strong> - 以网格布局中的卡片形式查看站点</li>
              <li><strong>搜索</strong> - 使用搜索栏按名称查找特定站点</li>
              <li><strong>过滤</strong> - 按仪表板类型过滤站点（临时工厂、热网、保险丝板）</li>
            </ul>
            
            `,
    'helpContent.siteDashboards': `<p>每个站点都有一个专用仪表板，提供详细信息和监控功能。仪表板类型取决于站点配置。</p>
            
            <h4>仪表板类型：</h4>
            
            <h5>1. 保险丝板仪表板</h5>
            <p>保险丝板仪表板提供具有分层树结构的保险丝板管理的全面视图。</p>

            <h6>左侧面板 - 层次树：</h6>
            <ul>
              <li>显示站点结构，包含单元、属性和保险丝板</li>
              <li>搜索功能可快速查找特定保险丝板或单元</li>
              <li>展开/折叠节点以导航层次结构</li>
              <li>状态指示器显示每个设备的在线/离线状态</li>
              <li>警报指示器显示是否有任何活动警报</li>
            </ul>

            <h6>右侧面板 - KPI和图表：</h6>
            <ul>
              <li><strong>KPI卡片</strong> - 保存的碳、成本节约、总使用量和警报</li>
              <li><strong>用电趋势图</strong> - 显示过去24小时能耗的交互式图表</li>
              <li>将鼠标悬停在KPI卡片上以查看增强的视觉反馈</li>
              <li>点击警报KPI以查看站点的详细警报</li>
            </ul>

            <h6>与保险丝板交互：</h6>
            <ul>
              <li>点击树中的任何保险丝板以打开详细模态窗口</li>
              <li>模态窗口显示完整的设备信息、读数和历史数据</li>
              <li>查看电压、电流和功率读数以及图表</li>
              <li>导出数据或以全屏模式查看</li>
            </ul>

            <h5>2. 临时工厂仪表板</h5>
            <p>临时工厂仪表板设计用于监控临时工厂房安装。</p>

            <h5>3. 热网仪表板</h5>
            <p>热网仪表板为热网系统提供监控。</p>

            <div class="help-tip">
              <strong>提示：</strong>布局固定，面板大小相等，因此层次树和KPI/图表始终可见，无需滚动。
            </div>`,
    'helpContent.siteNotes': `<p>站点注释允许您向站点图像添加注释、标记和笔记，用于文档和协作。</p>

            <h4>功能：</h4>
            <ul>
              <li><strong>图像查看</strong> - 以高分辨率查看站点图像</li>
              <li><strong>标记</strong> - 在图像上的特定位置添加标记</li>
              <li><strong>富文本编辑器</strong> - 添加格式化的笔记和文档</li>
              <li><strong>记事本</strong> - 用于站点特定信息的快速笔记部分</li>
              <li><strong>设备信息</strong> - 通过点击标记查看设备详细信息</li>
            </ul>
            
            <h4>添加标记：</h4>
            <ol>
              <li>点击"添加标记"按钮或直接在图像上点击</li>
              <li>将标记放置在需要的位置</li>
              <li>添加描述或链接到设备</li>
              <li>保存标记</li>
            </ol>

            <h4>使用记事本：</h4>
            <ul>
              <li>点击记事本图标以打开笔记编辑器</li>
              <li>添加快速笔记、提醒或站点特定信息</li>
              <li>笔记会自动保存</li>
              <li>随时从站点注释页面访问笔记</li>
            </ul>
            
            <div class="help-tip">
              <strong>提示：</strong>标记可以链接到设备，以快速访问设备信息和读数。
            </div>`,
    'helpContent.alertsOverview': `<p>警报页面提供全面的警报管理，包含三个主要标签：活动警报、配置和离线监控。</p>

            <h4>警报统计：</h4>
            <p>在活动警报标签的顶部，您会看到一个摘要，显示：</p>
            <ul>
              <li>严重警报数量</li>
              <li>警告警报数量</li>
              <li>信息警报数量</li>
              <li>警报总数</li>
            </ul>`,
    'helpContent.viewingAlerts': `<p>警报列表显示所有警报，包含详细信息和操作选项。</p>

            <h4>显示的警报信息：</h4>
            <ul>
              <li><strong>严重性指示器</strong> - 左侧的彩色条（红色表示严重，黄色表示警告，蓝色表示信息）</li>
              <li><strong>严重性徽章</strong> - 显示警报级别的视觉徽章</li>
              <li><strong>警报消息</strong> - 触发警报的描述</li>
              <li><strong>设备标识符</strong> - 生成警报的设备</li>
              <li><strong>时间戳</strong> - 警报生成的时间</li>
              <li><strong>状态</strong> - 当前状态（活动、已确认、已解决）</li>
            </ul>
            
            <h4>搜索和过滤：</h4>
            <ul>
              <li><strong>搜索栏</strong> - 按消息或设备标识符搜索警报</li>
              <li><strong>严重性过滤器</strong> - 按严重性级别过滤（全部、严重、警告、信息）</li>
              <li><strong>状态过滤器</strong> - 按状态过滤（全部、活动、已确认、已解决）</li>
            </ul>
            
            `,
    'helpContent.alertActions': `<p>您可以根据警报状态对警报执行各种操作。</p>

            <h4>可用操作：</h4>
            
            <h5>确认警报：</h5>
            <ul>
              <li>适用于活动警报</li>
              <li>将警报标记为已确认</li>
              <li>表明您知道问题</li>
              <li>不解决警报</li>
            </ul>
            
            <h5>解决警报：</h5>
            <ul>
              <li>适用于活动或已确认的警报</li>
              <li>将警报标记为已解决</li>
              <li>表示问题已得到处理</li>
              <li>已解决的警报仍可查看，但默认情况下会被过滤</li>
            </ul>
            
            <h5>查看详细信息：</h5>
            <ul>
              <li>适用于已解决的警报</li>
              <li>查看有关警报的详细信息</li>
              <li>访问与警报相关的历史数据</li>
            </ul>

            <div class="help-tip">
              <strong>提示：</strong>使用确认操作来跟踪您已审查的警报，即使您尚未解决它们。
            </div>`,
    'helpContent.alertConfigurations': `<p>警报配置允许您创建和管理自定义警报规则，监控系统以查找特定条件。</p>

            <h4>配置管理：</h4>
            <p>配置在看板板中组织，包含三列：</p>
            <ul>
              <li><strong>活动</strong> - 当前正在监控的配置</li>
              <li><strong>暂停</strong> - 临时禁用的配置</li>
              <li><strong>草稿</strong> - 配置模板（未来功能）</li>
            </ul>
            
            <h4>创建新配置：</h4>
            <ol>
              <li>点击"创建警报规则"按钮</li>
              <li>从下拉菜单中选择模板</li>
              <li>选择范围（站点、单元或设备级别）</li>
              <li>如果适用，选择特定站点/单元/设备</li>
              <li>设置阈值（最小值和最大值）</li>
              <li>选择警报级别（信息、警告或严重）</li>
              <li>启用或禁用配置</li>
              <li>点击"创建"保存</li>
            </ol>

            <h4>管理配置：</h4>
            <ul>
              <li><strong>拖放</strong> - 在活动和暂停列之间移动配置</li>
              <li><strong>编辑</strong> - 点击编辑按钮以修改现有配置</li>
              <li><strong>删除</strong> - 永久删除配置</li>
            </ul>

            <div class="help-tip">
              <strong>提示：</strong>使用暂停的配置来临时禁用监控，而无需完全删除规则。
            </div>`,
    'helpContent.offlineMonitoring': `<p>离线监控标签允许您设置警报，当设备离线时触发。</p>

            <h4>创建离线监控器：</h4>
            <ol>
              <li>点击"新监控器"按钮</li>
              <li>选择要监控的设备</li>
              <li>设置离线阈值（以分钟为单位）- 设备必须离线多长时间才能触发警报</li>
              <li>选择警报级别（警告或严重）</li>
              <li>添加可选描述</li>
              <li>启用或禁用监控器</li>
              <li>点击"创建"保存</li>
            </ol>

            <h4>监控器信息：</h4>
            <p>每个监控器显示：</p>
            <ul>
              <li>设备名称</li>
              <li>描述（如果提供）</li>
              <li>最后看到的时间戳</li>
              <li>离线阈值</li>
              <li>警报级别</li>
              <li>启用/禁用状态</li>
            </ul>`,
    'helpContent.reportsOverview': `<p>报告页面为您的系统数据提供全面的分析和报告功能。</p>

            <h4>可用报告：</h4>
            <ul>
              <li><strong>站点报告</strong> - 所有站点的概览，包含关键指标</li>
              <li><strong>能耗</strong> - 详细的能耗分析</li>
              <li><strong>警报趋势</strong> - 历史警报数据和趋势</li>
              <li><strong>温度趋势</strong> - 随时间变化的温度监控</li>
              <li><strong>警报频率</strong> - 警报模式分析</li>
            </ul>`,
    'helpContent.timeRanges': `<p>大多数报告允许您按时间范围过滤数据，以分析特定时期。</p>

            <h4>可用时间范围：</h4>
            <ul>
              <li><strong>过去24小时</strong> - 过去一天的数据</li>
              <li><strong>过去7天</strong> - 周视图</li>
              <li><strong>过去30天</strong> - 月视图</li>
              <li><strong>过去90天</strong> - 季度视图</li>
              <li><strong>自定义范围</strong> - 选择特定的开始和结束日期</li>
            </ul>

            <div class="help-tip">
              <strong>提示：</strong>使用自定义日期范围来分析特定事件或感兴趣的时期。
            </div>`,
    'helpContent.chartsGraphs': `<p>报告包含各种类型的图表和图形，以有效可视化您的数据。</p>

            <h4>图表类型：</h4>
            <ul>
              <li><strong>折线图</strong> - 显示随时间变化的趋势</li>
              <li><strong>柱状图</strong> - 跨类别比较值</li>
              <li><strong>面积图</strong> - 显示带渐变的累积值</li>
              <li><strong>饼图</strong> - 显示比例和百分比</li>
            </ul>
            
            <h4>与图表交互：</h4>
            <ul>
              <li>将鼠标悬停在数据点上以查看确切值</li>
              <li>点击图例项以显示/隐藏数据系列</li>
              <li>如果可用，使用缩放控件</li>
              <li>将图表导出为图像或PDF</li>
            </ul>
            
            `,
    'helpContent.exportingReports': `<p>您可以以各种格式导出报告，用于共享、分析或文档记录。</p>

            <h4>导出格式：</h4>
            <ul>
              <li><strong>CSV</strong> - 将数据导出为逗号分隔值，用于电子表格分析</li>
              <li><strong>PDF</strong> - 将图表和报告导出为PDF文档</li>
              <li><strong>排行榜</strong> - 导出比较表</li>
            </ul>
            
            <h4>导出内容：</h4>
            <ul>
              <li>基于所选过滤器的当前数据</li>
              <li>图表和可视化</li>
              <li>汇总统计</li>
              <li>时间戳和元数据</li>
            </ul>

            <div class="help-tip">
              <strong>提示：</strong>定期导出报告以用于记录保存和合规目的。
            </div>`,
    'helpContent.customReports': `<p>对于高级报告需求，可以创建根据您的特定要求定制的自定义分析仪表板。</p>

            <h4>自定义仪表板功能：</h4>
            <ul>
              <li>定制的可视化界面</li>
              <li>具有预测建模的高级分析</li>
              <li>具有即时警报的实时洞察</li>
              <li>自定义指标和KPI</li>
            </ul>
            
            <p>联系您的系统管理员或支持团队以讨论自定义报告要求。</p>`,
    'helpContent.adminOverview': `<p>管理页面提供用于管理设备和配置系统设置的管理工具。此页面需要管理员访问权限。</p>

            <h4>管理功能：</h4>
            <ul>
              <li><strong>设备管理</strong> - 创建、编辑和删除设备</li>
              <li><strong>设备搜索和过滤</strong> - 按标识符、标题或设备类型查找设备</li>
              <li><strong>保险丝配置</strong> - 为FUSEBOARD_UDC设备配置保险丝</li>
              <li><strong>组织设置</strong> - 为整个组织配置KPI计算参数</li>
              <li><strong>站点设置</strong> - 为特定站点覆盖组织设置</li>
            </ul>
            
            <div class="help-tip">
              <strong>注意：</strong>需要管理员访问权限才能查看和使用此页面。如果您需要管理员权限，请联系系统管理员。
            </div>`,
    'helpContent.deviceManagement': `<p>从管理页面管理系统中的所有设备。</p>

            <h4>查看设备：</h4>
            <ul>
              <li><strong>设备表</strong> - 查看所有设备，包含标识符、标题、设备类型和站点</li>
              <li><strong>搜索</strong> - 使用搜索栏按标识符或标题查找设备</li>
              <li><strong>按类型过滤</strong> - 使用下拉菜单按设备类型过滤设备</li>
            </ul>
            
            <h4>创建设备：</h4>
            <ol>
              <li>点击"添加设备"按钮</li>
              <li>输入设备标识符（必需，创建后无法更改）</li>
              <li>输入可选标题</li>
              <li>从下拉菜单中选择设备类型</li>
              <li>选择设备所在的站点</li>
              <li>可选地选择与设备关联的单元</li>
              <li>对于FUSEBOARD_UDC和CS01_NB设备，至少配置2个保险丝（参见保险丝配置）</li>
              <li>对于EWATTCH_SQUID设备，配置钳位分配（参见Squid钳位配置）</li>
              <li>点击"创建设备"保存</li>
            </ol>

            <h4>编辑设备：</h4>
            <ul>
              <li>点击表中任何设备旁边的编辑图标</li>
              <li>修改标题、设备类型、站点或单元</li>
              <li>注意：创建后无法更改标识符</li>
              <li>对于FUSEBOARD_UDC和CS01_NB设备，根据需要更新保险丝配置</li>
              <li>对于EWATTCH_SQUID设备，根据需要更新钳位分配</li>
              <li>点击"更新设备"保存更改</li>
            </ul>
            
            <h4>删除设备：</h4>
            <ul>
              <li>点击设备旁边的删除图标</li>
              <li>在确认对话框中确认删除</li>
              <li><strong>警告：</strong>此操作无法撤销</li>
            </ul>
            
            <h4>设备类型：</h4>
            <p>系统支持多种设备类型，每种类型都有特定的配置要求：</p>
            
            <h5>FUSEBOARD_UDC（配电盘UDC）</h5>
            <ul>
              <li><strong>用途：</strong>监控配电盘电路，最多支持4个电流传感器</li>
              <li><strong>配置：</strong>需要为至少2个保险丝配置保险丝配置（参见保险丝配置部分）</li>
              <li><strong>数据：</strong>测量每个保险丝的电流（A），使用配置的电压和功率因数计算功率（W）</li>
              <li><strong>使用场景：</strong>主配电盘监控，电路级功率跟踪</li>
            </ul>
            
            <h5>CS01_NB（CS-01 NB-IoT电流传感器）</h5>
            <ul>
              <li><strong>用途：</strong>NB-IoT电流传感器，支持最多4个电流测量通道</li>
              <li><strong>配置：</strong>需要为至少2个通道配置保险丝配置（参见保险丝配置部分）</li>
              <li><strong>数据：</strong>测量每个通道的电流（A），使用配置的电压和功率因数计算功率（W）</li>
              <li><strong>使用场景：</strong>远程电流监控，难以到达位置的电路级功率跟踪</li>
              <li><strong>特性：</strong>电池供电，NB-IoT连接，低功耗</li>
            </ul>
            
            <h5>EWATTCH_SQUID（Squid钳位设备）</h5>
            <ul>
              <li><strong>用途：</strong>多钳位电流监控设备，支持最多12个钳位</li>
              <li><strong>配置：</strong>需要将钳位分配给单元（参见Squid钳位配置）</li>
              <li><strong>数据：</strong>测量电流（A）、功率（W）、能量（kWh）、电压（V）和其他电气参数</li>
              <li><strong>使用场景：</strong>综合电气监控，多电路跟踪，能源管理</li>
              <li><strong>特性：</strong>支持多种测量类型，包括有功能量、无功能量、视在功率、频率</li>
            </ul>
            
            <h5>ELSYS_ELT（Elsys环境传感器）</h5>
            <ul>
              <li><strong>用途：</strong>环境监控传感器</li>
              <li><strong>配置：</strong>基本设备设置（标识符、标题、站点）</li>
              <li><strong>数据：</strong>温度、湿度和其他环境参数</li>
              <li><strong>使用场景：</strong>建筑环境监控，HVAC系统监控</li>
            </ul>
            
            <h5>ELVACO_4130（Elvaco热量表）</h5>
            <ul>
              <li><strong>用途：</strong>区域供热系统的热量表</li>
              <li><strong>配置：</strong>基本设备设置（标识符、标题、站点）</li>
              <li><strong>数据：</strong>能耗（kWh）、流量（m³/h）、供/回水温度（°C）、功率（W）</li>
              <li><strong>使用场景：</strong>区域供热监控，热网管理，能源计费</li>
              <li><strong>特性：</strong>支持M-Bus协议，多费率时段，错误标志检测</li>
            </ul>
            
            <div class="help-tip">
              <strong>提示：</strong>在管理大量设备时，使用搜索和过滤功能快速查找特定设备。每种设备类型都有特定的配置要求 - 确保完成这些配置以实现准确的数据监控。
            </div>`,
    'helpContent.fuseConfiguration': `<p>FUSEBOARD_UDC和CS01_NB设备需要保险丝配置才能正确监控和计算用电量。</p>

            <h4>保险丝/通道配置要求：</h4>
            <ul>
              <li><strong>最少2个保险丝/通道</strong> - 至少必须配置保险丝/通道1和2</li>
              <li><strong>保险丝/通道3和4</strong> - 可选，可以留空</li>
              <li><strong>必需字段</strong> - 设备名称、类别、电压和功率因数</li>
              <li><strong>适用于：</strong>FUSEBOARD_UDC（保险丝）和CS01_NB（通道）设备</li>
            </ul>
            
            <h4>配置保险丝/通道：</h4>
            <ol>
              <li>输入<strong>设备名称</strong>（例如，"主电源"、"电路A"、"厨房电器"）</li>
              <li>选择<strong>类别</strong>：
                <ul>
                  <li><strong>电源</strong> - 主电源入口点</li>
                  <li><strong>电路</strong> - 单个电路或分支</li>
                  <li><strong>负载</strong> - 特定负载或电器</li>
                  <li><strong>其他</strong> - 上面未列出的其他类别</li>
                </ul>
              </li>
              <li>输入<strong>电压（V）</strong> - 对于英国系统通常为240V，对于欧盟系统为230V</li>
              <li>输入<strong>功率因数</strong> - 0到1之间的值（大多数负载通常为0.9，阻性负载为1.0）</li>
            </ol>

            <h4>设备特定说明：</h4>
            <ul>
              <li><strong>FUSEBOARD_UDC：</strong>每个保险丝代表配电盘上的物理保险丝位置。根据您的安装配置保险丝1-4。</li>
              <li><strong>CS01_NB：</strong>每个通道代表一个电流传感器输入。根据连接的传感器配置通道1-4。</li>
            </ul>
            
            <h4>重要说明：</h4>
            <ul>
              <li>保险丝/通道配置值在安装期间读取并保持恒定</li>
              <li>这些值用于精确的功率计算：功率（W）= 电压（V）× 电流（A）× 功率因数</li>
              <li>准确的配置确保正确的KPI指标、成本节约计算和碳足迹跟踪</li>
              <li>您可以在创建设备后更新保险丝/通道配置</li>
              <li>可以通过清除所有字段来删除可选的保险丝/通道（3和4）</li>
            </ul>
            
            <div class="help-tip">
              <strong>提示：</strong>确保电压和功率因数值准确，以便正确计算用电量和成本节约指标。对于英国安装，标准电压为240V，典型功率因数为0.9。
            </div>`,
    'helpContent.organizationSettings': `<p>组织设置控制所有站点的KPI计算，除非在站点级别被覆盖。</p>

            <h4>组织KPI配置：</h4>
            
            <h5>单位费率（每kWh的£）：</h5>
            <ul>
              <li>每千瓦时电力的成本</li>
              <li>用于计算成本节约</li>
              <li>示例：0.30表示每kWh £0.30</li>
              <li>必需字段</li>
            </ul>
            
            <h5>间隔分钟数：</h5>
            <ul>
              <li>设备读数到达的频率（以分钟为单位）</li>
              <li>用于基于时间的精确计算</li>
              <li>示例：5表示读数每5分钟到达一次</li>
              <li>必需字段，必须至少为1</li>
            </ul>
            
            <h5>碳因子（每kWh的kg CO₂e）：</h5>
            <ul>
              <li>用于碳节约计算的碳排放因子</li>
              <li>用于计算保存的碳指标</li>
              <li>示例：0.233表示每kWh 0.233 kg CO₂e</li>
              <li>必需字段</li>
            </ul>
            
            <h4>保存组织设置：</h4>
            <ol>
              <li>点击管理页面标题中的"设置"按钮</li>
              <li>导航到"组织KPI配置"部分</li>
              <li>输入或更新单位费率、间隔分钟数和碳因子的值</li>
              <li>点击"保存组织设置"</li>
              <li>设置适用于所有站点，除非被覆盖</li>
            </ol>

            <div class="help-tip">
              <strong>提示：</strong>当电价或碳因子发生变化时，更新这些设置以确保准确的KPI计算。
            </div>`,
    'helpContent.siteSettings': `<p>站点设置允许您为特定站点覆盖组织设置。</p>

            <h4>站点KPI配置：</h4>
            <p>您可以为单个站点覆盖组织设置：</p>
            <ul>
              <li><strong>单位费率覆盖</strong> - 站点特定的电价</li>
              <li><strong>间隔分钟数覆盖</strong> - 站点特定的读数间隔</li>
              <li><strong>碳因子覆盖</strong> - 站点特定的碳排放因子</li>
            </ul>
            
            <h4>配置站点设置：</h4>
            <ol>
              <li>点击管理页面标题中的"设置"按钮</li>
              <li>导航到"站点KPI配置"部分</li>
              <li>从下拉菜单中选择站点</li>
              <li>输入覆盖值（留空以使用组织默认值）</li>
              <li>在每个字段下方的小文本中查看当前有效值</li>
              <li>点击"保存站点设置"</li>
            </ol>
            
            <h4>理解覆盖：</h4>
            <ul>
              <li><strong>空覆盖</strong> - 站点使用组织默认值</li>
              <li><strong>设置覆盖</strong> - 站点使用覆盖值而不是组织默认值</li>
              <li><strong>当前值显示</strong> - 显示正在使用的有效值（覆盖或默认）</li>
            </ul>

            <div class="help-tip">
              <strong>提示：</strong>当站点具有不同的电价或需要与组织默认值不同的计算参数时，使用站点覆盖。
            </div>`,
    'helpContent.chatbotOverview': `<p>聊天助手是帮助页面上可用的AI驱动的帮助工具。它可以回答有关应用程序的问题并引导您查看相关文档。</p>

            <h4>访问聊天助手：</h4>
            <ul>
              <li>在帮助页面的右下角查找聊天按钮</li>
              <li>点击聊天图标以打开聊天窗口</li>
              <li>助手会用欢迎消息问候您</li>
            </ul>
            
            <h4>提问：</h4>
            <ul>
              <li>在底部的输入字段中输入您的问题</li>
              <li>按Enter或点击发送按钮</li>
              <li>助手将根据您的问题提供答案</li>
              <li>您可以在同一对话中提出后续问题</li>
            </ul>
            
            <h4>您可以询问的内容：</h4>
            <ul>
              <li><strong>导航</strong> - "如何导航到警报？"</li>
              <li><strong>功能</strong> - "什么是KPI？"或"如何导出报告？"</li>
              <li><strong>警报</strong> - "如何确认警报？"</li>
              <li><strong>设置</strong> - "如何更改主题？"</li>
              <li><strong>一般帮助</strong> - "如何过滤站点？"</li>
            </ul>

            <h4>聊天功能：</h4>
            <ul>
              <li><strong>消息历史</strong> - 在聊天窗口中查看您的对话历史</li>
              <li><strong>时间戳</strong> - 每条消息显示发送时间</li>
              <li><strong>输入指示器</strong> - 查看助手何时正在输入回复</li>
              <li><strong>关闭/重新打开</strong> - 随时关闭和重新打开聊天</li>
            </ul>
            
            <div class="help-tip">
              <strong>提示：</strong>聊天助手可以帮助您快速查找信息，而无需浏览所有帮助部分。尝试提出具体问题以获得最佳结果。
            </div>`,
    'helpContent.bestPractices': `<h4>仪表板使用：</h4>
            <ul>
              <li>定期检查概览仪表板以获取快速洞察</li>
              <li>主动设置警报配置以尽早发现问题</li>
              <li>使用站点地图获取所有位置的视觉概览</li>
              <li>定期查看报告以识别趋势</li>
            </ul>
            
            <h4>警报管理：</h4>
            <ul>
              <li>及时确认警报以跟踪您的审查状态</li>
              <li>在解决根本问题后解决警报</li>
              <li>使用严重性过滤器首先关注严重问题</li>
              <li>为关键设备设置离线监控</li>
            </ul>
            
            <h4>数据分析：</h4>
            <ul>
              <li>比较不同时间范围的数据以识别模式</li>
              <li>导出报告用于离线分析和记录保存</li>
              <li>使用站点注释记录重要发现</li>
              <li>查看历史趋势以预测未来需求</li>
            </ul>`,
    'helpContent.commonIssues': `<h4>地图未加载：</h4>
            <ul>
              <li>检查您的互联网连接</li>
              <li>尝试刷新页面</li>
              <li>切换地图样式 - 有时不同的样式加载更快</li>
              <li>如果问题持续，清除浏览器缓存</li>
            </ul>
            
            <h4>图表未显示：</h4>
            <ul>
              <li>确保您有所选时间范围的数据</li>
              <li>尝试选择不同的时间范围</li>
              <li>检查过滤器是否过于严格</li>
              <li>刷新页面以重新加载数据</li>
            </ul>
            
            <h4>警报未出现：</h4>
            <ul>
              <li>检查过滤器设置 - 您可能已应用过滤器</li>
              <li>验证警报配置是否已启用</li>
              <li>检查您是否正在查看正确的状态（活动与已解决）</li>
              <li>确保设备在线并报告数据</li>
            </ul>
            
            <h4>性能问题：</h4>
            <ul>
              <li>关闭不必要的浏览器标签</li>
              <li>如果当前浏览器很慢，尝试使用不同的浏览器</li>
              <li>对于大型数据集，减少报告的时间范围</li>
              <li>如果问题持续，请联系支持</li>
            </ul>`,
    'helpContent.keyboardShortcuts': `<p>虽然应用程序主要通过鼠标操作，但这里有一些有用的键盘快捷键：</p>
            
            <ul>
              <li><strong>Ctrl/Cmd + F</strong> - 聚焦到搜索字段（当可用时）</li>
              <li><strong>Esc</strong> - 关闭模态窗口和弹出窗口</li>
              <li><strong>Tab</strong> - 在表单字段之间导航</li>
              <li><strong>Enter</strong> - 提交表单或激活按钮</li>
            </ul>
            
            <div class="help-tip">
              <strong>提示：</strong>大多数操作都可以通过鼠标点击执行。键盘快捷键作为便利功能提供。
            </div>`,
    'helpContent.gettingHelp': `<h4>应用内资源：</h4>
            <ul>
              <li>此帮助页面 - 所有功能的完整指南</li>
              <li>工具提示 - 将鼠标悬停在按钮和图标上以获取快速信息</li>
              <li>上下文帮助 - 某些页面有内联帮助文本</li>
            </ul>
            
            <h4>支持渠道：</h4>
            <ul>
              <li>联系系统管理员以解决帐户和访问问题</li>
              <li>联系技术支持以解决错误或技术问题</li>
              <li>检查系统通知以获取重要更新和公告</li>
            </ul>
            
            <div class="help-tip">
              <strong>提示：</strong>报告问题时，请包含屏幕截图和关于问题发生时您正在尝试做什么的详细信息。
            </div>`,
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });
  const [isRTL, setIsRTL] = useState(language === 'ar');

  useEffect(() => {
    localStorage.setItem('language', language);
    setIsRTL(false); // Disable RTL UI flipping
    
    // Keep document direction as LTR for all languages
    document.documentElement.dir = 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const changeLanguage = (newLanguage) => {
    setLanguage(newLanguage);
  };

  const t = (key, replacements = {}) => {
    let translation = translations[language]?.[key] || translations.en[key] || key;

    // Support {placeholder} interpolation for dynamic values like branding
    Object.keys(replacements).forEach(placeholder => {
      translation = translation.replace(new RegExp(`\\{${placeholder}\\}`, 'g'), replacements[placeholder]);
    });

    return translation;
  };

  const value = {
    language,
    changeLanguage,
    t,
    isRTL,
    availableLanguages: [
      { code: 'en', name: 'English', flag: '🇬🇧' },
      { code: 'ar', name: 'العربية', flag: '🇦🇪' },
      { code: 'es', name: 'Español', flag: '🇪🇸' },
      { code: 'zh', name: '中文', flag: '🇨🇳' }
    ]
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
